<template>
    <v-sheet :color="''" class="custom-dashboard-space cdb-builder-v1">
      <div class="custom-dashboard-content">
        <!-- Error State -->
        <div v-if="loadError" class="custom-dashboard-empty">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
          <div class="mt-2">
            <span class="text-h6" style="opacity: .7;">An error occurred while loading the dashboard.</span>
          </div>
          <v-btn @click="retryLoad" outlined color="primary" class="mt-4">
            <v-icon small class="mr-1">mdi-refresh</v-icon> Retry
          </v-btn>
        </div>

        <!-- Tabs -->
        <div class="cdb-tab-bar cdb-tab-bar--v1">
        <div class="cdb-band-title">
          <v-icon>mdi-chart-bar</v-icon>
          <span>Explorer</span>
        </div>
        <div
          class="cdb-band-tabs"
          ref="tabBar"
          aria-label="Dashboard tabs"
          @wheel="onTabStripWheel"
          @focusin="onTabStripFocus">
        <v-btn-toggle
          background-color="transparent"
          :value="tab"
          dense
          id="custom-dashboard-tabs"
          color="primary"
          class="d-flex flex-nowrap"
          mandatory>
          <!-- Tabs render from renderedTabs (browser-local Show / hide dashboards) but keep
               their index into `pages`: :value pins each button to that index, so the toggle,
               `tab`, the panels and the ?tab= route helpers share one index space. -->
          <v-btn
            v-for="{ page, index } in renderedTabs"
            :key="page.id"
            :value="index"
            :data-page-index="index"
            :ripple="{ class: 'primary--text' }"
            :title="page.name"
            outlined
            @click="onTabClick(index)">
            <v-icon small class="mr-1">{{ page.icon || 'mdi-view-dashboard' }}</v-icon>
            <span class="cdb-tab-label">{{ page.name }}</span>
            <v-menu dense offset-x right v-if="isRealPageId(page.id) && showAuthoring">
              <template #activator="{ attrs, on }">
                <!-- A non-button activator so v-btn-toggle never registers the kebab as a
                     phantom toggle item (a nested v-btn was hijacking tab selection on click
                     instead of opening this menu). -->
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1 cdb-touch-target cdb-tab-kebab"
                  role="button"
                  tabindex="0"
                  @click.stop
                  @mousedown.stop
                  @keydown.enter.stop.prevent="on.click && on.click($event)">
                  <v-icon x-small>mdi-dots-vertical</v-icon>
                </span>
              </template>
              <v-list class="more-options-menu">
                <v-list-item @click="updatePage(page.id)" dense>
                  <v-list-item-icon class="mr-2 justify-center">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Update</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="deletePage(page.id)" dense>
                  <v-list-item-icon class="mr-2 justify-center">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </v-btn-toggle>

          <!-- add-dashboard "+" beside the tabs (showcase add-tab affordance) -->
          <v-btn
            v-if="canManageDashboards"
            icon
            class="cdb-add-tab"
            title="Add dashboard"
            aria-label="Add dashboard"
            @click="onBandAddDashboard">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>

        <div class="cdb-band-actions">
          <v-btn color="primary" outlined @click="openFilters" :loading="filterSessionLoading">
            <v-icon left>mdi-filter</v-icon> Filters
            <v-chip
              v-if="activeFilterCount > 0"
              x-small
              color="primary"
              class="ml-2 font-weight-bold"
              style="height: 20px; min-width: 20px; padding: 0 6px;">
              {{ activeFilterCount }}
            </v-chip>
          </v-btn>
          <!-- Dashboard settings cog: one menu for the mode-appropriate options. Edit mode
               (managers) adds Coloring Schemas + the Tab width presets; the browser-local
               Open-in-3D preference shows in BOTH modes. close-on-content-click stays false
               so a width pick or the toggle keeps the menu open; action items close it
               explicitly. -->
          <v-menu v-model="settingsMenuOpen" offset-y left :close-on-content-click="false" max-width="340">
            <template #activator="{ attrs, on }">
              <v-btn icon class="ml-2" v-bind="attrs" v-on="on" aria-label="Dashboard settings" title="Dashboard settings">
                <v-icon>mdi-cog-outline</v-icon>
              </v-btn>
            </template>
            <v-list dense role="menu">
              <template v-if="editMode && canManageDashboards">
                <v-list-item role="menuitem" @click="settingsMenuOpen = false; $refs.coloringSchemasManager.open()">
                  <v-list-item-icon class="mr-3"><v-icon small>mdi-palette-swatch-outline</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title>Coloring Schemas</v-list-item-title></v-list-item-content>
                </v-list-item>
                <v-divider class="my-1" />
                <v-subheader>
                  <v-icon small class="mr-2">mdi-arrow-expand-horizontal</v-icon> Tab width
                </v-subheader>
                <v-list-item
                  v-for="opt in contentWidthOptions"
                  :key="opt.id"
                  role="menuitemradio"
                  :aria-checked="opt.id === activeTabContentWidth ? 'true' : 'false'"
                  :disabled="contentWidthSaving"
                  @click="onContentWidthPick(opt.id)">
                  <v-list-item-content>
                    <v-list-item-title>{{ opt.label }}</v-list-item-title>
                    <v-list-item-subtitle>{{ opt.hint }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="opt.id === activeTabContentWidth">
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-divider class="my-1" />
              </template>
              <v-list-item role="menuitem">
                <v-list-item-content>
                  <v-list-item-title>Open 3D viewer on chart selection</v-list-item-title>
                  <v-list-item-subtitle>Applies to this browser only</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    v-model="autoOpenViewer3d"
                    hide-details
                    dense
                    inset
                    color="primary"
                    class="ma-0 pa-0"
                    @change="onAutoOpenViewerToggle" />
                </v-list-item-action>
              </v-list-item>
              <v-list-item role="menuitem" :disabled="!hideableTabs.length" @click="openTabVisibilityDialog">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-eye-settings-outline</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Show / hide dashboards</v-list-item-title>
                  <v-list-item-subtitle>Applies to this browser only</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- edit mode: Add Widget · Cancel · Done ; view mode: pencil edit -->
          <template v-if="editMode">
            <v-btn v-if="canManageDashboards" color="primary" @click="$refs.editSqlViewWidget.open()">
              <v-icon small class="mr-1">mdi-database-search</v-icon> Add Widget
            </v-btn>
            <div v-if="saveStatus !== 'idle'" class="cdb-save-status" :class="'cdb-save-status--' + saveStatus">
              <v-icon small class="cdb-save-status__glyph" :class="{ 'cdb-spin': saveStatus === 'saving' }">{{ saveStatusGlyph }}</v-icon>
              <span class="cdb-save-status__text">{{ saveStatusText }}</span>
              <template v-if="saveStatus === 'error'">
                <v-btn text x-small class="cdb-save-status__action" @click="onRetrySave">Retry</v-btn>
                <v-btn text x-small class="cdb-save-status__action" @click="onDiscardSave">Discard</v-btn>
              </template>
            </div>
            <v-btn outlined @click="onCancelClick">Cancel</v-btn>
            <v-btn color="primary" @click="onDoneClick">
              <v-icon small class="mr-1">mdi-check</v-icon> Done
            </v-btn>
          </template>
          <v-btn
            v-else-if="canManageDashboards"
            icon
            class="cdb-edit-icon"
            :loading="layoutMigrationLoading"
            :disabled="layoutMigrationLoading"
            @click="enterEditMode"
            title="Edit dashboard">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
        </div>

        <!-- Keep the dashboard tabs visible, then use their canvas for a clear first-step state. -->
        <section
          v-if="noData && !loadError"
          class="cdb-dashboard-empty-state cdb-dashboard-page-empty-state"
          role="status"
          aria-live="polite">
          <span class="cdb-dashboard-empty-state__icon" aria-hidden="true">
            <v-icon>mdi-chart-box-plus-outline</v-icon>
          </span>
          <h2>No dashboards yet</h2>
          <p>
            Create a dashboard to keep this facility&rsquo;s inspections, risks, and asset data in one focused view.
          </p>
          <v-btn
            v-if="canManageDashboards"
            color="primary"
            class="cdb-dashboard-empty-state__action"
            @click="onAddDashboardClick">
            <v-icon small left>mdi-plus</v-icon>
            Create dashboard
          </v-btn>
          <p v-else class="cdb-dashboard-page-empty-state__viewer-note">
            An editor can create the first dashboard for this facility.
          </p>
        </section>

        <!-- Multi-chart selection summary (points #7 + #9): one chip per active dimension,
             OR within a chip, "and" between chips, granular + clear-all removal. Renders for
             both flag states above the grid whenever a selection is active. -->
        <SelectionSummaryBar
          v-if="(hasSelections || selectionError) && !noData && !loadError"
          :selections="selectionChips"
          :error="selectionError"
          :combined-empty="combinedSelectionEmpty"
          :show-open-in3d="showOpenIn3d"
          @remove-value="onRemoveSelectionValue"
          @remove-chip="removeSelection"
          @clear-all="clearAllSelections()"
          @open-in3d="openSingleSelectionInViewer" />
        <div v-if="spatialSelectionNotice" class="pa-3 text-body-2" role="status">{{ spatialSelectionNotice }}</div>

        <v-row v-if="!noData && !loadError" no-gutters class="custom-dashboard-tab-items flex-nowrap" :style="{ flex: '1 1 auto', minHeight: '0' }">
          <v-col :cols="viewerOpened ? 8 : 12" class="h-100 overflow-y-auto">
            <!-- v-if (not v-show): every panel renders the ACTIVE tab's layout, so hidden
                 panels were full mounted duplicates — each one re-fetching every widget
                 (W x tab-count request storm, and the 429/503 boot failures). Only the
                 active panel exists now; tab switches remount, which they effectively
                 did anyway via the layout swap. -->
            <template v-for="(page, index) in pages">
            <div v-if="tab === index" :key="'panel-' + page.id" class="dark-background">
                <v-card flat>
                    <v-card-text v-if="isDefaultPage">

                    </v-card-text>
                    <v-card-text v-else-if="page.id == 'add'" class="d-flex justify-center align-center">

                    </v-card-text>
                    <v-card-text v-else-if="!isDefaultPage && !pageLoading"
                        class="custom-dashboard-grid-area black-background">
                        <div class="grid-wrapper custom-dashboard-grid-wrapper" :class="[contentWidthWrapperClass]">
                            <section
                                v-if="!layout.length"
                                class="cdb-dashboard-empty-state"
                                role="status"
                                aria-live="polite">
                                <span class="cdb-dashboard-empty-state__icon" aria-hidden="true">
                                    <v-icon>mdi-view-dashboard-outline</v-icon>
                                </span>
                                <h2>{{ canManageDashboards ? 'Build this dashboard' : 'No widgets yet' }}</h2>
                                <p v-if="canManageDashboards">
                                    Add a KPI, chart, or data grid to turn this page into an operational view.
                                </p>
                                <p v-else>
                                    This dashboard has no widgets yet. An editor can add the first one.
                                </p>
                                <v-btn
                                    v-if="canManageDashboards"
                                    color="primary"
                                    class="cdb-dashboard-empty-state__action"
                                    @click="onEmptyAddWidget">
                                    <v-icon small left>mdi-plus</v-icon>
                                    Add first widget
                                </v-btn>
                            </section>
                            <grid-layout v-else :key="gridKey" :layout.sync="layout" :col-num="gridColNum" :row-height="gridRowHeight"
                                :is-draggable="isDraggable" :is-resizable="isResizable" :is-mirrored="false" :vertical-compact="true"
                                :margin="gridMargin" :use-css-transforms="true">
                                <grid-item v-for="(item, index) in layout" :static="item.pinned" :x="item.x" :y="item.y"
                                    :w="item.w" :h="item.h" :min-w="layoutUiDensity" :min-h="layoutUiDensity"
                                    :i="index" :key="item.id" @resized="resizedHandler"
                                    @resize="resizeHandler" @moved="movedHandler"
                                    drag-allow-from=".widget-drag-handle">
                                    <div class="custom-dashboard-widget-wrapper">
                                        <dashboard-item ref="dashboardItems" :message="item" :widgetId="item.id"
                                            :library-schemas="activeTabColoringSchemas"
                                            :isolationClaim="activeIsolation"
                                            :isClaimCurrent="isIsolationClaimCurrent"
                                            :claimIsolation="beginIsolationClaim"
                                            :filterSessionId="sidForTable(tableByWidgetId[item.id])"
                                            :selectionFilters="selectionFiltersByWidgetId[item.id] || noSelectionFilters"
                                            :globalFilters="fallbackGlobalFilters"
                                            :about-global-filters="generalFilters"
                                            :about-filters-pending="filterSessionLoading"
                                            :dashboardFilterCount="activeFilterCount"
                                            :editMode="editMode"
                                            :frozen="isFrozenTable(tableByWidgetId[item.id])"
                                            :selection="chartSelections[item.id] || null"
                                            :sessionEmptyMatch="sessionEmptyMatchForTable(tableByWidgetId[item.id])"
                                            :crossFilterActive="hasSelections"
                                            @duplicate="duplicate" @del="del"
                                             @update="updateContentHandler"
                                             @openViewer="onWidgetOpenViewer"
                                             @openRowDetails="openRowDetails"
                                             @createChartFromGrid="onCreateChartFromGrid"
                                            @clearCrossFilter="clearAllSelections()"
                                            @sessionExpired="onWidgetSessionExpired"
                                            @selectionFilterRejected="onSelectionFilterRejected"
                                            @openDashboardFilters="openFilters"
                                            @legendFilter="onLegendFilter"
                                            @listDrill="onListDrill"
                                            @isolate-widget="onIsolateWidget" />
                                    </div>
                                </grid-item>
                            </grid-layout>
                            <!-- Presentational "add widget" tile that ends the grid in edit mode
                                 (showcase dashboard-builder-edit-mode.html:544-548). It lives
                                 OUTSIDE the grid-item v-for, so it is never a vue-grid-layout
                                 item: it can never enter `layout`, is never persisted/autosaved,
                                 and is never counted as a widget. Opens the same Add-Widget wizard
                                 as the band's edit-mode Add Widget button. -->
                            <button
                                v-if="editMode && canManageDashboards && layout.length"
                                type="button"
                                class="cdb-ghost-tile"
                                title="Add widget"
                                @click="$refs.editSqlViewWidget.open()">
                                <span class="cdb-ghost-tile__plus">
                                    <v-icon>mdi-plus</v-icon>
                                </span>
                                <span class="cdb-ghost-tile__label">Add widget</span>
                                <span class="cdb-ghost-tile__hint">Pick a register, a metric and a display type</span>
                            </button>
                        </div>
                    </v-card-text>

                    <v-card-text v-else class="custom-dashboard-loading d-flex align-center black-background">
                        <loading-popup
                            :show="true"
                            text="Preparing your dashboard">
                        </loading-popup>
                    </v-card-text>
                </v-card>
            </div>
            </template>
          </v-col>

          <!-- 3D Viewer Panel (slides in from right, like Inspection dashboard) -->
          <v-col cols="4" v-show="viewerOpened" class="h-100 overflow-hidden pa-2">
            <viewers-section
              ref="viewersSection"
              :hide360View="true"
              :industrialChrome="true"
              :viewerTab.sync="viewerTab"
              @closeViewersSection="closeViewer">
              <template #header-controls="{ activeTab }">
                <v-menu
                  v-if="activeTab === '3D View'"
                  offset-y
                  left
                  nudge-bottom="6"
                  min-width="310"
                  max-width="310"
                  :close-on-content-click="false">
                  <template #activator="{ on, attrs }">
                    <button
                      type="button"
                      class="cdb-viewer-settings-btn"
                      aria-label="Spatial focus settings"
                      title="Spatial focus settings"
                      v-bind="attrs"
                      v-on="on">
                      <v-icon size="16">mdi-tune-variant</v-icon>
                    </button>
                  </template>

                  <v-card
                    class="cdb-viewer-settings-card"
                    role="dialog"
                    aria-label="Spatial focus settings">
                    <div class="cdb-viewer-settings-card__eyebrow">Spatial focus</div>
                    <div class="cdb-viewer-settings-card__row">
                      <div class="cdb-viewer-settings-card__copy">
                        <div class="cdb-viewer-settings-card__title">
                          Use Master Tag as fallback POI
                        </div>
                        <div class="cdb-viewer-settings-card__description">
                          Only when CML/POI geometry is unavailable. Off keeps visible anchors gray.
                        </div>
                      </div>
                      <v-switch
                        class="cdb-viewer-settings-card__switch mt-0"
                        color="primary"
                        dense
                        inset
                        hide-details
                        :input-value="useMasterTagAsPoiFallback"
                        :loading="masterTagFallbackBusy"
                        :disabled="masterTagFallbackBusy"
                        :aria-label="useMasterTagAsPoiFallback
                          ? 'Disable Master Tag point-of-interest fallback'
                          : 'Enable Master Tag point-of-interest fallback'"
                        @change="setMasterTagPoiFallback" />
                    </div>
                  </v-card>
                </v-menu>
              </template>
              <!-- Coloured-isolation legend overlays the embedded 3D panel only.
                   activeTab comes from ViewersSection's own localViewerTab (the correct
                   3D-scoping signal), so it hides on any non-3D tab. -->
              <template #viewer-overlay="{ activeTab }">
                <ColoringLegend v-if="activeTab === '3D View'" @select-source="onLegendSourceChange"
                  @refine-value="onLegendRefineValue" @reset-refinement="onLegendResetRefinement" />
              </template>
            </viewers-section>
          </v-col>
        </v-row>
        <EditAIWidget ref="editAIWidget" @updated="editAIWidgetHandler"></EditAIWidget>
        <EditSqlViewWidget ref="editSqlViewWidget" :library-schemas="activeTabColoringSchemas" @updated="editAIWidgetHandler"></EditSqlViewWidget>
        <ColoringSchemasManager ref="coloringSchemasManager" :tab-id="page && page.id" @saved="onColoringSchemasSaved"></ColoringSchemasManager>

        <v-dialog v-model="dashboardModal.active" max-width="520" persistent>
            <v-card class="ds-dialog">
                <div class="ds-dialog__header">
                    <div class="ds-dialog__header-icon">
                        <v-icon color="primary" size="22">mdi-view-dashboard-outline</v-icon>
                    </div>
                    <div class="ds-dialog__header-text">
                        <span class="ds-dialog__title">{{ selectedPage.id ? 'Edit Dashboard' : 'Add Dashboard' }}</span>
                        <span class="ds-dialog__subtitle">Name it and choose an icon</span>
                    </div>
                    <v-btn icon small @click="closeDashboardModal" :disabled="dashboardModal.loading">
                        <v-icon size="20">mdi-close</v-icon>
                    </v-btn>
                </div>

                <div class="ds-dialog__body">
                    <label for="add-dashboard-name" class="ds-field-label">
                        Dashboard Name
                        <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                    </label>
                    <v-text-field
                        id="add-dashboard-name"
                        v-model="selectedPage.name"
                        outlined
                        dense
                        hide-details="auto"
                        :error-messages="duplicateNameError"
                        autofocus
                        placeholder="e.g. Integrity Overview"></v-text-field>

                    <label for="add-dashboard-icon" class="ds-field-label mt-5">
                        Icon
                        <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
                    </label>
                    <v-select
                        id="add-dashboard-icon"
                        v-model="selectedPage.icon"
                        :items="iconOptions"
                        outlined
                        dense
                        hide-details
                        item-text="text"
                        item-value="value">
                        <template #selection="{ item }">
                            <v-icon small class="mr-2">{{ item.value }}</v-icon>
                            <span>{{ item.text }}</span>
                        </template>
                        <template #item="{ item }">
                            <v-icon small class="mr-2">{{ item.value }}</v-icon>
                            <span>{{ item.text }}</span>
                        </template>
                    </v-select>
                </div>

                <div class="ds-dialog__footer">
                    <v-btn outlined color="error" @click="closeDashboardModal" :disabled="dashboardModal.loading">Cancel</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        depressed
                        @click="dashboardUpdateHandler"
                        :disabled="!selectedPage.name || !!duplicateNameError"
                        :loading="dashboardModal.loading">
                        {{ selectedPage.id ? 'Save' : 'Create' }}
                        <v-icon right size="18">mdi-arrow-right</v-icon>
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Show / hide dashboards: browser-local tab visibility (utils/hiddenDashboards.js).
             Each switch writes through immediately; pages are never modified. -->
        <v-dialog v-model="tabVisibilityDialogOpen" max-width="440">
            <v-card class="ds-dialog">
                <div class="ds-dialog__header">
                    <div class="ds-dialog__header-icon">
                        <v-icon color="primary" size="22">mdi-eye-settings-outline</v-icon>
                    </div>
                    <div class="ds-dialog__header-text">
                        <span class="ds-dialog__title">Show / hide dashboards</span>
                        <span class="ds-dialog__subtitle">Hidden tabs apply to this browser only</span>
                    </div>
                    <v-btn icon small aria-label="Close" @click="tabVisibilityDialogOpen = false">
                        <v-icon size="20">mdi-close</v-icon>
                    </v-btn>
                </div>

                <div class="ds-dialog__body cdb-tab-visibility-list">
                    <v-list dense class="pa-0">
                        <v-list-item v-for="{ page, visible, locked } in hideableTabs" :key="page.id" class="px-0">
                            <v-list-item-icon class="mr-3">
                                <v-icon small>{{ page.icon || 'mdi-view-dashboard' }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title :title="page.name">{{ page.name }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-switch
                                    :input-value="visible"
                                    :disabled="locked"
                                    :aria-label="(visible ? 'Hide ' : 'Show ') + page.name"
                                    :title="locked ? 'At least one dashboard must stay visible' : ''"
                                    hide-details
                                    dense
                                    inset
                                    color="primary"
                                    class="ma-0 pa-0"
                                    @change="onTabVisibilityChange(page, $event)" />
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </div>

                <div class="ds-dialog__footer">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed @click="tabVisibilityDialogOpen = false">Done</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Dashboard creation chooser -->
        <DashboardCreationChooser
          :value="creationChooser.active"
          :starter-state="starterState"
          :duplicate-state="duplicateState"
          :creating="creationChooser.creating"
          @input="onChooserInput"
          @select="onChooserSelect" />

        <row-details-drawer
          v-model="rowDetailsOpen"
          :row="rowDetailsRow"
          :config="rowDetailsConfig"
          :can-isolate="rowDetailsCanIsolate"
          @isolate="isolateDetailsRow" />

        <GlobalFiltersPanel
          ref="globalFilters"
          :viewerOpened="viewerOpened"
          @filtersChanges="handleFiltersChanges">
        </GlobalFiltersPanel>

      </div>
    </v-sheet>
</template>


<script>
/* eslint-disable  */
import VueGridLayout from 'vue-grid-layout';
import dashboardPagesService from '../service/custom-dashboard.service';
import apiService from '../service/dashboard-widgets.service';
import dashboardConfigurationService from '../service/dashboard-configuration.service';
import EditAIWidget from '../components/EditAIWidget.vue';
import EditSqlViewWidget from '../components/EditSqlViewWidget.vue';
import ColoringSchemasManager from '../components/ColoringSchemasManager.vue';
import DashboardWidget from '@/features/Chatbot/components/dashboard-widget.vue';
import DashboardItem from '../components/DashboardItem.vue';
import ViewersSection from '../components/ViewersSection.vue';
import ColoringLegend from '../components/ColoringLegend.vue';
import GlobalFiltersPanel from '../components/GlobalFiltersPanel.vue';
import SelectionSummaryBar from '../components/SelectionSummaryBar.vue';
import LoadingPopup from '@/components/common/loadingPopup.vue';
import RowDetailsDrawer from '@/features/Dashboard/components/RowDetailsDrawer.vue';
import { $updateArr, $has } from '@/utils';
import { log, warn, error } from '@/utils/debugLogger';
import { widgetSaveErrorMessages } from '../utils/widgetConfigContract';
import { API_LIMITS } from '@/constants/apiLimits';
import {
    LIST_DRILL_OUTCOME,
    buildListDrillValuesRequest,
    findTableEntry,
    listDrillScopeBlock,
    runListDrill,
} from '../utils/listDrill';
import { LIST_DRILL_REGISTRY } from '../service/listDrillHandlers';
import { debounce } from 'lodash';
import permissions from '../../Share/config/permissions';
import { createLayoutSaveQueue } from '../composables/useLayoutSaveQueue';
import { clearThemingColors, UNMAPPED_ISOLATE_COLOR } from '@/utils/forgeUtils';
import { makeIsolateColorResolver } from '@/features/Dashboard/utils/isolateColor';
import { formatHeaderName } from '@/features/Dashboard/utils/columnLabels';
import { toCategoryLabel } from '@/features/Dashboard/service/chart-data.service';
import {
    normalizeHighlightColorSource,
    HIGHLIGHT_COLOR_SOURCE,
} from '@/features/Dashboard/utils/poiConfig';
import {
    allMatchingRowWindow,
    buildSelectionFilters,
    buildSpatialQueryScope,
    buildTableSessionJobs,
    hasExactSpatialCapability,
    isFrozenTable,
    normalizeTableName,
    selectionFiltersForViewer,
    selectionFiltersForWidget,
} from '@/features/Dashboard/utils/dashboardSelectionScope';
import {
    buildSpatialResolveRequest,
    legacySpatialFocusOverride,
    resolveSpatialFocusSessionPolicy,
    spatialFocusFallbackEnabled,
} from '@/features/Dashboard/utils/spatialFocusPolicy';
import { useSpatialFocus } from '../composables/useSpatialFocus';
import { focusOutcomeMessage, hasDisplayTarget, spatialRefusalMessage } from '../utils/dashboardReader';
import registerService from '@/features/Registers/services/registerService';
import { resolveDefinition } from '@/features/Dashboard/utils/coloringSchemaEngine';
import { APPLIES_TO, COLORING_SOURCE, THEME } from '@/features/Dashboard/utils/coloringSchemas';
import { createEditExitCoordinator } from '../composables/useEditExitCoordinator';
import { registerDashboardExitGuard, clearDashboardExitGuard } from '../composables/dashboardExitGuard';
import DashboardCreationChooser from '../components/DashboardCreationChooser.vue';
import { preflightStarter, resolveStarterSources } from '../utils/starterManifest';
import { TAB_QUERY_KEY, resolveTabIndexFromQuery, fallbackTabIndex, tabIndexAfterDeletion } from '../utils/dashboardTabRoute';
import { scrollTabStripFromWheel, revealTabInStrip } from '../utils/dashboardTabScroll';
import { CONTENT_WIDTH_OPTIONS, normalizeContentWidth, contentWidthClass } from '../utils/contentWidth';
import { DASHBOARD_PREF, DASHBOARD_PREFS_STORAGE_KEY, getDashboardPreference, setDashboardPreference } from '../utils/dashboardPreferences';
import { readHiddenDashboardIds, writeHiddenDashboardIds, withDashboardVisibility, effectiveHiddenIds, visiblePageIndices, renderedTabEntries, preferVisibleTabIndex } from '../utils/hiddenDashboards';

// Transactional create-page-with-widgets endpoint (spec §6 ship criterion) does NOT
// exist yet — Starter/Duplicate stay disabled until it ships. Flip when the api lands.
const TRANSACTIONAL_CREATE_AVAILABLE = false;
const CREATE_ENDPOINT_PENDING_REASON = 'Available once the dashboard creation service update ships.';

// Sentinel page ids that are not real, persistable dashboards: 'default' (the empty
// placeholder page) and 'add' (the create-tab affordance). isRealPageId() below is the
// single guard the tab-scoped methods and template use to skip these pseudo pages.
const PSEUDO_PAGE_IDS = Object.freeze(['default', 'add']);

// Builder-v1 uses a grid that is exactly twice as dense as the legacy 18-column /
// 100px-row grid. DashboardPage.Color is currently unused by the UI, so a negative
// sentinel records which integer coordinate system a page's widgets use without a DB
// migration. Negative avoids colliding with historical non-negative colour values.
const LEGACY_LAYOUT_DENSITY = 1;
const DENSE_LAYOUT_DENSITY = 2;

// The colouring dimension the legend paints and can refine. A published legend declares its facets
// so the renderer can tell a refinable chart legend from a plain published one (the datagrid's), and
// so a second (toggle) facet can be added without changing the stored shape.
const LEGEND_FACET_COLORED = 'colored';
const DENSE_LAYOUT_PAGE_MARKER = -2;
const LEGACY_GRID_COLUMNS = 18;
const LEGACY_GRID_ROW_HEIGHT = 100;
const BUILDER_GRID_MARGIN = 16;

// Cross-TABLE selection cap (decision 3): the first N distinct selected tables get an
// external-only session (global ∩ every dimension from ANOTHER table) so boards stay
// order-independent. Tables beyond this freeze — their widgets keep their current data and show a
// reference badge — because each extra table costs one more session per interaction. The cap
// counts TABLES, not widgets: same-table dimensions never create a session, they travel as raw
// predicates (dashboardSelectionScope), so five charts over two tables cost two sessions.
const MAX_SELF_EXCLUDING_TABLES = 3;

// ── Per-tab dashboard Configuration (registry, COLORING_SCHEMA_REGISTRY.md + layout.contentWidth) ──
// Unwrap the raw `configuration` value (string | object | null) from a GET/PUT response envelope,
// tolerant of the (Step-1-UNCONFIRMED) shape: httpService may hand back `{ id, configuration }`
// directly or a `{ data: {...} }` wrapper.
function extractRawConfiguration(response) {
    const body = response && response.data;
    if (!body) return null;
    if (body.configuration !== undefined) return body.configuration;
    if (body.data && body.data.configuration !== undefined) return body.data.configuration;
    return null;
}

// Coerce a raw `configuration` (JSON string | object | null) into a plain config object for
// read-modify-write. null / absent / unparseable → {} (never throws), matching
// ColoringSchemasManager.parseConfig so both writers see the same tolerant shape.
function toConfigObject(configuration) {
    if (configuration == null || configuration === '') return {};
    try {
        return typeof configuration === 'string' ? (JSON.parse(configuration) || {}) : configuration;
    } catch (err) {
        error('[CustomDashboard] tab configuration unparseable', err);
        return {};
    }
}

// One shared, frozen empty predicate list. Widgets with no same-table selection all receive THIS
// array, so their `selectionFilters` prop identity never changes and they never refetch for a
// selection that does not concern them.
const NO_SELECTION_FILTERS = Object.freeze([]);

// A layout entity carries its widget's raw `sqlViewConfig` (JSON string from the api, or an
// already-parsed object). Only the physical table name is needed here, and an unparseable /
// absent config is a widget with no SQL source — never a hard failure.
function parseWidgetConfig(sqlViewConfig) {
    if (!sqlViewConfig) return null;
    try {
        return typeof sqlViewConfig === 'string' ? JSON.parse(sqlViewConfig) : sqlViewConfig;
    } catch (err) {
        return null;
    }
}

function parseWidgetTableName(sqlViewConfig) {
    return parseWidgetConfig(sqlViewConfig)?.tableName || null;
}

// Parse a raw `configuration` into the per-tab store shape { librarySchemas, contentWidth }.
// Defensive by contract: librarySchemas is [] on null / absent / unparseable / pre-migration
// (NEVER null — null is reserved as the "still loading" sentinel the reference render-gate keys
// on); contentWidth ALWAYS normalizes (absent / junk → full). Accepts a raw JSON string or an
// already-parsed object.
function parseTabConfigurationFromRaw(configuration) {
    const cfg = toConfigObject(configuration);
    const schemas = cfg && cfg.coloringSchemas;
    return {
        librarySchemas: Array.isArray(schemas) ? schemas : [],
        contentWidth: normalizeContentWidth(cfg && cfg.layout && cfg.layout.contentWidth),
    };
}

// Parse the dedicated GET/refresh response envelope into the per-tab store shape.
function parseTabConfigurationFromResponse(response) {
    return parseTabConfigurationFromRaw(extractRawConfiguration(response));
}

export default {
    components: {
        EditAIWidget,
        EditSqlViewWidget,
        ColoringSchemasManager,
        DashboardWidget,
        GridLayout: VueGridLayout.GridLayout,
        GridItem: VueGridLayout.GridItem,
        DashboardItem,
        ViewersSection,
        ColoringLegend,
        GlobalFiltersPanel,
        SelectionSummaryBar,
        LoadingPopup,
        DashboardCreationChooser,
        RowDetailsDrawer,

    }, data() {
        return {
            queryText: '',
            spatialSelectionNotice: '',
            queryLoading: false,
            selectedFilters: {},
            // Multi-chart selection model (points #7 + #9). Each entry is one DIMENSION
            // (a column on a source widget) holding one or more VALUES; values within a
            // dimension OR, dimensions across charts AND. Keyed by source widget id. Vue 2
            // needs $set/$delete (or wholesale replace) here to keep dependents reactive.
            chartSelections: {},
            // Insertion order of chartSelections keys. Plain-object key order is unreliable
            // for numeric-string ids, so the summary bar, the "and" separators and the
            // self-excluding source cap all read order from this array.
            chartSelectionOrder: [],
            // Transient failure surfaced on the selection bar when a legend session cannot be
            // created (fail-closed api): the offending dimension is named + removed and the
            // prior sessions are kept. Cleared on the next user selection / clear.
            selectionError: null,
            filterSessionId: null,
            // Session ids for the multi-selection model. filterSessionId stays the global-only
            // BASE session (it keeps the existing degraded machinery and is reused when there
            // is no selection or when a table's exclusion empties). fullSid = global ∩ ALL
            // dimensions (widgets on an UNSELECTED table query with it); sidByTableName = the
            // per-table EXTERNAL-ONLY session (global ∩ every dimension from another table),
            // keyed by lowercase physical table name for the first N selected tables.
            //
            // Keyed by physical table, never by widget or sourceKey: sourceKey folds in
            // saved-filter hashes, so two widgets reading one table can carry different keys
            // while sharing exactly the same cross-table scope.
            fullSid: null,
            sidByTableName: {},
            // Create-time tagCount per session id (base + full + each self-excluding). A sid
            // whose session was created with 0 tags is an EMPTY-MATCH (the combined selection
            // matched nothing), not a stale session — widgets on it render empty and never
            // renew. The api cannot distinguish the two at apply time (both return unfiltered
            // rows + 'session-expired'), so this create-time count is the lever (decision 10).
            sidTagCounts: {},
            filterSessionTagCount: 0,
            filterSessionLoading: false,
            // True when server-side BASE (global-only) session creation FAILED while filters
            // are active (distinct from the no-filters state, which leaves this false).
            // Widgets then fall back to per-request raw global filters (degraded mode) instead
            // of silently showing unfiltered data. Cleared on session success or no-filters.
            // Legend session failures do NOT set this — they use dimension removal instead.
            filterSessionError: false,
            viewerOpened: false,
            viewerTab: '3D View',
            // Viewer-local rendering policy. Default ON preserves the current CML behaviour: when a
            // CML has no linked Forge element, its Master Tag geometry can act as the target. The
            // header control can turn that fallback off without changing the selected row scope.
            useMasterTagAsPoiFallback: true,
            // Null inherits the authored typed policy (and legacy compatibility default). Once the
            // viewer switch changes, its boolean is the session-local override for later replays.
            masterTagFallbackSessionOverride: null,
            masterTagFallbackBusy: false,
            rowDetailsOpen: false,
            rowDetailsRow: null,
            rowDetailsConfig: null,
            rowDetailsOwnerWidgetId: null,
            rowDetailsCanIsolate: false,
            // Sticky legend colouring-source pick (D1): the key of the source the user chose in the
            // legend selector, honoured across selection changes while it stays available.
            isolationSourceKey: null,
            // The colouring values the legend is currently refined to, or null for "all values"
            // (the published default). A VIEW refinement over the last published paint — it never
            // reaches chartSelections, so the dashboard's filter chips and queries are untouched.
            legendRefinedValues: null,
            // One latest-wins owner for every chart/grid operation that can write to the shared
            // viewer. Replacing the whole object keeps the claim reactive through DashboardItem.
            activeIsolation: { generation: 0, ownerWidgetId: null },
            // Filter sessions are reusable by 3D isolation only when they were resolved for the
            // same global/selection scope. Until then isolation uses the equivalent raw filters.
            filterScopeRevision: 0,
            resolvedFilterScopeRevision: -1,
            // Monotonic generation for legend cross-filter operations. Every legend
            // set/clear bumps it; async continuations (session refresh, tag resolve)
            // capture it and bail if superseded — prevents a stale response from
            // overwriting a newer filter or re-isolating cleared tags (I-1/I-2).
            crossFilterGen: 0,
            // Latest-wins token for createFilterSession specifically — bumped by EVERY
            // refresh invocation (legend, global-filter, clear), unlike crossFilterGen
            // which only legend ops bump.
            sessionRefreshSeq: 0,
            layout: [],

            // ── edit/view mode ──
            editMode: false,
            // True only during the active exit drain — freezes drag/resize input. A failed
            // drain unfreezes (advisor rule): the user may Retry, Discard, or keep editing.
            isExiting: false,
            // Existing pages are converted to dense integer geometry on first edit. The
            // edit action stays loading until the conversion is safely persisted.
            layoutMigrationLoading: false,
            // Forced-remount key for the grid — bumped by Discard so the restored layout
            // paints regardless of vue-grid-layout's :layout.sync internals.
            gridKey: 0,
            // Settings cog (toolbar): menu open state + the writable model for the
            // browser-local Open-in-3D preference it hosts (write-through on change;
            // isAutoOpenViewerEnabled reads the store fresh at use time).
            settingsMenuOpen: false,
            autoOpenViewer3d: getDashboardPreference(DASHBOARD_PREF.AUTO_OPEN_VIEWER) !== false,
            // Show / hide dashboards (cog menu): this facility's browser-local hidden page ids,
            // read in loadDashboards (utils/hiddenDashboards.js). Only the rendered tab bar
            // filters on it; `pages`, `tab` and the route helpers keep the full index space.
            hiddenDashboardIds: [],
            tabVisibilityDialogOpen: false,
            // Context captured at edit-enter so a late flush targets the edited dashboard,
            // never the live (possibly already-switched) facility/page.
            capturedFacilityId: null,
            capturedPageId: null,

            // ── builder-v1 creation chooser (spec §6) ──
            // `active` = dialog open; `creating` = a transactional creation in flight
            // (double-submit guard). `starterPreflight` holds the current facility's
            // deterministic preflight result the chooser's Starter state reacts to.
            creationChooser: { active: false, creating: false },
            starterPreflight: { status: 'idle', ok: false, reason: '' },

            index: 0,
            tab: 0,
            pageLoading: false,
            noData:false,
            loadError: false,
            pages: [
            ],
            dashboardModal: {
                active: false,
                valid: false,
                loading: false
            },
            selectedPage: {},
            // Per-TAB parsed dashboard Configuration (registry, COLORING_SCHEMA_REGISTRY.md +
            // layout.contentWidth), keyed by DashboardPage id → { librarySchemas, contentWidth }.
            // Seeded SYNCHRONOUSLY from each tab's `configuration` (returned with the tab list — no
            // extra round-trip) and overwritten by refreshActiveTabConfiguration after a manager save
            // or a width pick. An absent key = not seeded yet (the null loading-gate for
            // librarySchemas). Fed to widgets + the wizard as `library-schemas`.
            tabConfigurations: {},
            // True while a content-width pick is being persisted (GET→PUT round trip). Disables the
            // width picker items so a second pick can't race the in-flight save.
            contentWidthSaving: false,
            iconOptions: [
                { text: 'Chart Bar', value: 'mdi-chart-bar' },
                { text: 'Chart Line', value: 'mdi-chart-line' },
                { text: 'Chart Pie', value: 'mdi-chart-pie' },
                { text: 'Chart Area', value: 'mdi-chart-areaspline' },
                { text: 'Chart Donut', value: 'mdi-chart-donut' },
                { text: 'Table', value: 'mdi-table' },
                { text: 'View Dashboard', value: 'mdi-view-dashboard' },
                { text: 'Monitor', value: 'mdi-monitor-dashboard' },
                { text: 'Gauge', value: 'mdi-gauge' },
                { text: 'Finance', value: 'mdi-finance' },
                { text: 'Poll', value: 'mdi-poll' },
                { text: 'Database', value: 'mdi-database' },
                { text: 'Cog', value: 'mdi-cog' },
                { text: 'Lightning Bolt', value: 'mdi-lightning-bolt' },
                { text: 'Map Marker', value: 'mdi-map-marker' },
                { text: 'Account Group', value: 'mdi-account-group' },
                { text: 'Clipboard List', value: 'mdi-clipboard-list' },
                { text: 'Calendar', value: 'mdi-calendar' },
                { text: 'Star', value: 'mdi-star' },
                { text: 'Heart', value: 'mdi-heart' },
            ],
            defaultWidgetWidth: 6,
            defaultWidgetHeight: 4
        }

    },
    computed: {
        page() {
            if (this.pages.length)
                return this.pages[this.tab];
            else return {};
        },
        isDefaultPage() {
            return this.page?.id == 'default';
        },
        // Tab-bar entries ({ page, index } with index into `pages`): the visible pages plus the
        // active one even when hidden, so a deep link to a hidden dashboard stays open and the
        // mandatory tab toggle always has a button for `tab`.
        renderedTabs() {
            return renderedTabEntries(this.pages, this.activeHiddenDashboardIds, this.tab);
        },
        // The stored hidden ids that apply to the current real pages (stale ids dropped; none
        // when they would hide every dashboard). The tab bar, the dialog and the fallbacks all
        // read this one list, so a switch always matches the tab bar.
        activeHiddenDashboardIds() {
            const realPages = this.pages.filter(p => this.isRealPageId(p.id));
            return effectiveHiddenIds(realPages, this.hiddenDashboardIds);
        },
        // Rows for the Show / hide dashboards dialog: real pages only (the default / add
        // pseudo pages are never hideable). `locked` disables the switch of the last visible
        // dashboard so at least one tab always stays.
        hideableTabs() {
            const hidden = new Set(this.activeHiddenDashboardIds);
            const rows = this.pages
                .filter(page => this.isRealPageId(page.id))
                .map(page => ({ page, visible: !hidden.has(String(page.id)) }));
            const visibleCount = rows.filter(row => row.visible).length;
            return rows.map(row => ({ ...row, locked: row.visible && visibleCount <= 1 }));
        },
        facilityId() {
            return this.$route.params.id;
        },
        // The active tab's coloring-schema library, passed down as `library-schemas` to every
        // widget + the wizard. null = "still loading" (the reference render-gate holds neutral);
        // [] = loaded-empty (dangling references legitimately resolve schemaUnavailable). Returns
        // the stored array reference (stable until a refresh) so the engine's content-keyed cache
        // stays a hit across re-renders.
        activeTabColoringSchemas() {
            const tabId = this.page && this.page.id;
            if (!tabId) return null;
            const cached = this.tabConfigurations[tabId];
            // undefined = not seeded yet → null holds the reference render-gate neutral; a parsed
            // store = loaded, so its librarySchemas (incl. []) lets a dangling reference legitimately
            // resolve schemaUnavailable.
            return cached === undefined ? null : cached.librarySchemas;
        },
        // The active tab's content-width preset (narrow | medium | wide | full). Unlike coloring
        // schemas there is no loading gate: an unseeded / absent / junk value ALWAYS normalizes to
        // full (the current, uncapped behaviour). Drives the grid-wrapper max-width cap + the picker.
        activeTabContentWidth() {
            const tabId = this.page && this.page.id;
            const cached = tabId ? this.tabConfigurations[tabId] : undefined;
            return normalizeContentWidth(cached && cached.contentWidth);
        },
        // Frozen, ordered preset list (narrow → full) the width picker renders.
        contentWidthOptions() {
            return CONTENT_WIDTH_OPTIONS;
        },
        // CSS utility class capping .custom-dashboard-grid-wrapper for the active width; null for full
        // (uncapped) and whenever the tab is unseeded — so flag-off stays byte-identical.
        contentWidthWrapperClass() {
            return contentWidthClass(this.activeTabContentWidth);
        },
        generalFilters() {
            const filters = this.$store.getters['GLOBAL_DASHBOARD_FILTERS/GET_FILTERS'];
            // DTO-shaped global filters ({facilityId, dateFrom, dateTo}). The `facility`
            // multi-select was retired (its selection bound to no backend property yet
            // counted as an active filter — see GlobalFiltersPanel); real cross-facility
            // filtering is a separate backlog design (DTO + semantics + authorization).
            return {
                facilityId: this.facilityId,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
            };
        },
        activeFilterCount() {
            const f = this.$store.getters['GLOBAL_DASHBOARD_FILTERS/GET_FILTERS'];
            let count = 0;
            // facility excluded: the control is retired and its value binds to no backend
            // filter, so counting it would report filters that are never applied.
            if (f.dateFrom || f.dateTo) count++;
            return count;
        },
        /** Single source of truth for "are global filters actually in effect". Dates-only,
         *  matching activeFilterCount after the facility retirement, so no path creates a
         *  filter session (or awaits one on tab switch) for a filter that is never sent. */
        hasActiveGlobalFilters() {
            const f = this.$store.getters['GLOBAL_DASHBOARD_FILTERS/GET_FILTERS'];
            return !!(f.dateFrom || f.dateTo);
        },
        /** Raw global filters forwarded to widgets ONLY while the session is degraded, so
         *  they filter via the per-request raw path instead of showing unfiltered data.
         *  Null when the session is healthy (widgets use filterSessionId) — see B1. */
        fallbackGlobalFilters() {
            return this.filterSessionError ? this.generalFilters : null;
        },
        /** True when at least one chart selection (dimension) is active. */
        hasSelections() {
            return this.chartSelectionOrder.length > 0;
        },
        /** Stable empty predicate list for a widget the selection map has not reached yet. */
        noSelectionFilters() {
            return NO_SELECTION_FILTERS;
        },
        /** The selection entries in interaction order — the ONE input every scope decision reads
         *  (session jobs, the table cap, and every raw same-table predicate). */
        orderedSelectionEntries() {
            return this.chartSelectionOrder
                .map(id => this.chartSelections[id])
                .filter(Boolean);
        },
        /** Ordered selection entries the summary bar renders (one chip per dimension). */
        selectionChips() {
            return this.orderedSelectionEntries;
        },
        /** Each widget's PHYSICAL table, parsed once from the layout entity's sqlViewConfig.
         *  Session ids, the freeze cap and the raw same-table predicates are all keyed by physical
         *  table, and the template resolves a widget's table on every render — so parse here, not
         *  in a per-render method call. A widget with no SQL source (AI/legacy content) maps to
         *  null and falls through to the full cross-source session. */
        tableByWidgetId() {
            const map = {};
            this.layout.forEach(item => {
                if (!item || item.id == null) return;
                map[item.id] = normalizeTableName(parseWidgetTableName(item.sqlViewConfig));
            });
            return map;
        },
        /** The TRANSIENT same-table predicates each widget must add to its own query so its rows
         *  are exactly the cross-filtered rows. A source chart self-excludes, so clicking one of
         *  its bars does not erase the rest of its own chart.
         *
         *  Built as ONE cached map rather than a per-widget template call: a method in the binding
         *  would hand every widget a brand-new array on every parent render, and each widget
         *  watches this prop to refetch. Widgets with no predicate share one frozen empty array so
         *  their prop identity never changes either. */
        selectionFiltersByWidgetId() {
            const entries = this.orderedSelectionEntries;
            const map = {};
            this.layout.forEach(item => {
                if (!item || item.id == null) return;
                const filters = entries.length
                    ? selectionFiltersForWidget(entries, item.id, this.tableByWidgetId[item.id])
                    : NO_SELECTION_FILTERS;
                map[item.id] = filters.length ? filters : NO_SELECTION_FILTERS;
            });
            return map;
        },
        /** True when a selection is active but the FULL session (global ∩ all dimensions)
         *  resolved to zero tags — the combined selection matches nothing. Drives the bar's
         *  neutral empty-match notice (decision 10); distinct from selectionError. */
        combinedSelectionEmpty() {
            return this.hasSelections && this.fullSid != null && this.sidTagCounts[this.fullSid] === 0;
        },
        /** Exactly one dimension holding exactly one value — the only shape the single-value 3D
         *  isolate (and the manual "Open in 3D") applies to. */
        isSingleValueSelection() {
            if (this.chartSelectionOrder.length !== 1) return false;
            const entry = this.chartSelections[this.chartSelectionOrder[0]];
            return !!(entry && entry.values && entry.values.length === 1);
        },
        /** True when the current selection can be isolated in 3D: at least one selected chart and
         *  every selected chart shares ONE data source (cross-source union has no single table to
         *  project a colouring column over — out of scope). Drives the manual "Open in 3D" chip. */
        hasIsolatableSelection() {
            const entries = this.orderedSelectionEntries;
            if (!entries.length) return false;
            return new Set(entries.map(e => normalizeTableName(e.tableName))).size === 1;
        },
        /** Show the manual "Open in 3D" action: an isolatable selection while the auto-open
         *  preference is OFF (auto-open handled it otherwise). Uses the reactive mirror, not
         *  the storage read, so the chip appears the moment the cog switch flips. */
        showOpenIn3d() {
            return this.hasIsolatableSelection && this.autoOpenViewer3d === false;
        },
        /** Integer density used by the grid currently rendered in this client. */
        layoutUiDensity() {
            return DENSE_LAYOUT_DENSITY;
        },
        /** Integer density currently persisted for the active dashboard page. */
        layoutStorageDensity() {
            return Number(this.page?.color) === DENSE_LAYOUT_PAGE_MARKER
                ? DENSE_LAYOUT_DENSITY
                : LEGACY_LAYOUT_DENSITY;
        },
        gridColNum() {
            return LEGACY_GRID_COLUMNS * this.layoutUiDensity;
        },
        gridRowHeight() {
            // Preserve current pixel heights after doubling h. The legacy pitch is
            // 100 + 16 = 116px, so the dense pitch is 58px and its row is 42px.
            return ((LEGACY_GRID_ROW_HEIGHT + BUILDER_GRID_MARGIN) / DENSE_LAYOUT_DENSITY)
                - BUILDER_GRID_MARGIN;
        },
        gridMargin() {
            return [BUILDER_GRID_MARGIN, BUILDER_GRID_MARGIN];
        },
        /** Manage-holder: gates the "Edit dashboard" button + create CTAs. */
        canManageDashboards() {
            return $has(permissions.CustomDashboards.Manage);
        },
        /** Authoring chrome visible only in Edit mode. */
        showAuthoring() {
            return this.editMode;
        },
        /** Grid drag/resize: Edit-and-not-exiting. */
        isDraggable() {
            return this.editMode && !this.isExiting;
        },
        isResizable() {
            return this.editMode && !this.isExiting;
        },
        /** Save-status the indicator binds to: idle | saving | saved | error. */
        saveStatus() {
            return this.saveQueue ? this.saveQueue.state.status : 'idle';
        },
        saveStatusGlyph() {
            return { saving: 'mdi-loading', saved: 'mdi-check', error: 'mdi-alert-outline' }[this.saveStatus] || '';
        },
        saveStatusText() {
            return { saving: 'Saving…', saved: 'Saved', error: `Couldn't save` }[this.saveStatus] || '';
        },
        /**
         * Chooser Starter state (spec §6). Disabled + reason when the transactional
         * endpoint is missing OR preflight failed; the endpoint-pending reason takes
         * precedence, so the preflight reason only surfaces once the endpoint ships.
         */
        starterState() {
            if (!TRANSACTIONAL_CREATE_AVAILABLE) {
                return { available: false, reason: CREATE_ENDPOINT_PENDING_REASON, loading: false };
            }
            if (this.starterPreflight.status === 'loading') {
                return { available: false, reason: '', loading: true };
            }
            if (!this.starterPreflight.ok) {
                return { available: false, reason: this.starterPreflight.reason || 'Starter unavailable on this facility.', loading: false };
            }
            return { available: true, reason: '', loading: false };
        },
        /** Chooser Duplicate state: needs the endpoint AND at least one dashboard to copy. */
        duplicateState() {
            if (!TRANSACTIONAL_CREATE_AVAILABLE) {
                return { available: false, reason: CREATE_ENDPOINT_PENDING_REASON };
            }
            const hasDuplicable = this.pages.some(p => this.isRealPageId(p.id));
            if (!hasDuplicable) {
                return { available: false, reason: 'No dashboards to duplicate yet.' };
            }
            return { available: true, reason: '' };
        },
        /**
         * Duplicate-name block for the name dialog (spec §6). Case-insensitive against
         * existing dashboard names on the facility, excluding the page being edited.
         */
        duplicateNameError() {
            const name = (this.selectedPage.name || '').trim().toLowerCase();
            if (!name) return '';
            const clash = this.pages.some(p =>
                this.isRealPageId(p.id) &&
                p.id !== this.selectedPage.id &&
                (p.name || '').trim().toLowerCase() === name);
            return clash ? 'A dashboard with this name already exists on this facility.' : '';
        }
    },
    created() {
        // Instances (not reactive data) — their observable `state` drives the indicator.
        // Guarded so flag-off never touches the layout write path.
        this.saveQueue = createLayoutSaveQueue({
            getLiveLayout: () => this.layout,
            writeWidget: (payload, ctx) => apiService.updateLayout(
                this.toStorageWidgetPayload(payload),
                { facilityId: ctx && ctx.facilityId },
            ),
        });
        this.exitCoordinator = createEditExitCoordinator({
            queue: this.saveQueue,
            isEditing: () => this.editMode,
            setEditing: (v) => this.setEditMode(v),
            setExiting: (v) => { this.isExiting = v; },
            getContext: () => this.getEditContext(),
            restoreLayout: (acked) => this.restoreLayoutFromAcked(acked),
            refetchLayout: () => this.refetchLayoutForDiscard(),
            confirmDiscard: () => this.confirmDiscardDialog(),
        });
        // Per-facility-per-session starter preflight cache (non-reactive — the reactive
        // `starterPreflight` field is what the chooser binds to for the current facility).
        this._preflightCacheByFacility = {};
        // The catalogue's spatial capability per source table (non-reactive — nothing renders from
        // it). Memoises the one `tables()` GET the capability gate needs so a board whose widgets
        // share a source reads the catalogue once, not once per isolate.
        this._spatialByTable = new Map();
        // The catalogue entry per source table for the KPI drill to list (its `entityColumns`).
        // Non-reactive, same memo rules as _spatialByTable: only a usable answer is kept.
        this._drillEntryByTable = new Map();
        // The last PUBLISHED render receipt: its legend entries and the exact scope it was resolved
        // from, so a legend refinement can re-show a NARROWED scope while every row keeps the
        // baseline number it was published with. Non-reactive — the rendered legend is the store
        // model, and re-observing the receipt would cost for nothing.
        this._legendPaint = null;
        // The last exact dashboard intent is retained separately from the legend. A fallback-off
        // render can legitimately be unrenderable and therefore publish no legend; the user must
        // still be able to turn fallback back on and replay that exact same scope.
        this._lastExactFocusPayload = null;
        // The one public spatial seam. Every viewer scene this dashboard owns goes through it; the
        // composable itself owns the per-viewer generation, so this is a façade, not an authority.
        this.focus = useSpatialFocus();
        // True while a tab change ORIGINATES from the route (deep-link / back-forward /
        // deletion normalise) so the tab() watch does not echo a history push back (step 17).
        this._applyingRouteTab = false;
    },
    mounted() {
        // Cross-tree handshake: FacilityDropdown awaits this before window.location.assign.
        this._exitGuardFn = () => this.exitCoordinator.guardExternalNavigation();
        registerDashboardExitGuard(this._exitGuardFn);
        // Cross-tab preference sync: `storage` fires only in OTHER same-origin tabs, so a
        // second dashboard tab flipping the Open-in-3D switch updates this tab's UI mirror
        // (cog switch + manual chip) without a reload. Same-tab flips go through v-model;
        // the auto-open GATE itself reads storage fresh (isAutoOpenViewerEnabled).
        this._prefStorageFn = (e) => {
            if (e.key && e.key !== DASHBOARD_PREFS_STORAGE_KEY) return;
            this.autoOpenViewer3d = getDashboardPreference(DASHBOARD_PREF.AUTO_OPEN_VIEWER) !== false;
        };
        window.addEventListener('storage', this._prefStorageFn);
        // The filter session is settled by the page() watcher before it loads widgets
        // (it fires on the initial {} → first-page transition), so widgets get the right
        // filterSessionId / degraded-raw scope on first render without a second, racing
        // session-create here. See the page() watcher for the settle-then-load sequence.
        this.loadDashboards();
    },
    beforeDestroy() {
        this.invalidateIsolation();
        // Best-effort cleanup only — teardown is never an awaitable exit gate.
        if (this._prefStorageFn) window.removeEventListener('storage', this._prefStorageFn);
        this.cancelFilterSessionRetry();
        this.unregisterUnloadGuard();
        if (this._exitGuardFn) clearDashboardExitGuard(this._exitGuardFn);
        if (this.saveQueue) this.saveQueue.dispose();
    },
    async beforeRouteLeave(to, from, next) {
        // Route leave / browser back-forward off the route funnels through the coordinator.
        // Blocked (failed save) → next(false) + the in-place indicator; the nav is not held
        // pending (a committed popstate URL can't be suspended). W5 revisits in-route history.
        if (!this.editMode) {
            next();
            return;
        }
        const ok = await this.exitCoordinator.drainForRouteLeave();
        next(ok ? undefined : false);
    },
    methods: {
        /** A real, persistable dashboard tab (not the 'default' placeholder or 'add' affordance). */
        isRealPageId(id) {
            return !!id && !PSEUDO_PAGE_IDS.includes(id);
        },
        // Seed the active tab's parsed Configuration store from the tab-list payload. Each DashboardPage
        // now carries its `configuration` (api update 2026-07-17), so this is SYNCHRONOUS — no round
        // trip on tab activation. pseudo-page-guarded; idempotent (skips if already
        // seeded or refreshed, so a tab switch never clobbers a manager refresh). Absent `configuration`
        // (pre-migration old binaries) ≡ null config → { [], full } (loaded-empty, uncapped).
        seedTabConfiguration(page) {
            const tabId = page && page.id;
            if (!this.isRealPageId(tabId)) return;
            if (this.tabConfigurations[tabId] !== undefined) return;
            this.$set(this.tabConfigurations, tabId, parseTabConfigurationFromRaw(page.configuration));
        },
        // The Coloring Schemas manager (registry Step 3) emits the raw config string it just PUT after
        // a create / edit / delete; refresh the active tab's parsed store from it (no extra round-trip)
        // so every widget recolors immediately.
        onColoringSchemasSaved(rawConfiguration) {
            this.refreshActiveTabConfiguration(rawConfiguration);
        },
        // Refresh the ACTIVE tab's parsed Configuration store. CROSS-BUILDER CONTRACT: the "Coloring
        // Schemas" manager dialog (registry Step 3) calls this after a schema create / edit / delete
        // so every widget recolors. Pass the fresh raw `configuration` (from the manager's PUT
        // response) to update with NO round-trip, or call with no args to re-fetch via the dedicated
        // GET. Always targets the current tab; on a GET failure the last-known-good store is kept.
        refreshActiveTabConfiguration(configuration) {
            const tabId = this.page && this.page.id;
            if (!this.isRealPageId(tabId)) return;
            if (configuration !== undefined) {
                this.$set(this.tabConfigurations, tabId, parseTabConfigurationFromRaw(configuration));
                return;
            }
            dashboardConfigurationService.getConfiguration(tabId)
                .then((res) => {
                    this.$set(this.tabConfigurations, tabId, parseTabConfigurationFromResponse(res));
                })
                .catch((err) => {
                    error('[CustomDashboard] tab configuration refresh failed', tabId, err);
                });
        },
        // Apply a content-width preset to the active tab's store WITHOUT a round-trip — used for the
        // instant local reflow on pick and for the revert on save failure. Preserves the existing
        // librarySchemas array reference (keeps the engine's content-keyed cache a hit).
        applyLocalContentWidth(tabId, id) {
            const current = this.tabConfigurations[tabId];
            this.$set(this.tabConfigurations, tabId, {
                librarySchemas: current ? current.librarySchemas : [],
                contentWidth: normalizeContentWidth(id),
            });
        },
        // Persist the picked content width for the ACTIVE tab. Read-modify-write against a FRESH GET
        // so a concurrent schema edit is never clobbered; applies locally FIRST for instant reflow,
        // then GET → merge layout.contentWidth (spreading the whole prior config) → PUT → re-sync from
        // the PUT echo (server truth) when it carries a configuration. On ANY failure the local pick
        // is reverted and an error toast is shown. Guarded by contentWidthSaving against a double pick.
        // Kept as its own GET-merge-PUT rather than the shared updateTabConfiguration helper: that
        // helper shallow-merges (it would replace `layout` wholesale, dropping sibling keys) and does
        // not stamp formatVersion or re-sync from the echo. The shared helper covers the manager path.
        async onContentWidthPick(id) {
            if (this.contentWidthSaving) return;
            const tabId = this.page && this.page.id;
            if (!this.isRealPageId(tabId)) return;
            const previous = this.activeTabContentWidth;
            if (id === previous) return;

            this.applyLocalContentWidth(tabId, id);
            this.contentWidthSaving = true;
            try {
                const res = await dashboardConfigurationService.getConfiguration(tabId);
                const cfg = toConfigObject(extractRawConfiguration(res));
                const nextConfig = {
                    ...cfg,
                    formatVersion: 1,
                    layout: { ...(cfg.layout || {}), contentWidth: id },
                };
                const putRes = await dashboardConfigurationService.putConfiguration(tabId, JSON.stringify(nextConfig));
                // Re-sync from the PUT echo only when it carries a configuration (keeps schemas +
                // width aligned with server truth); if the response omits it, keep the value the
                // server just accepted rather than clobbering it back to defaults.
                const echoed = extractRawConfiguration(putRes);
                if (echoed != null && echoed !== '') {
                    this.$set(this.tabConfigurations, tabId, parseTabConfigurationFromRaw(echoed));
                }
                log('[CustomDashboard] content width saved', tabId, id);
            } catch (err) {
                this.applyLocalContentWidth(tabId, previous);
                error('[CustomDashboard] content width save failed', tabId, id, err);
                this.$dialog.notify.error('Failed to save dashboard width. Please try again.', {
                    position: 'top-right',
                    timeout: 5000,
                });
            } finally {
                this.contentWidthSaving = false;
            }
        },
        loadDashboards() {
            this.loadError = false;
            dashboardPagesService.get(this.facilityId).then(res => {
                if (!res.data.length) {
                    this.noData = true;
                }
                this.pages = [...res.data];
                // Before the route resolves the tab, so a fallback landing skips hidden tabs.
                this.hiddenDashboardIds = readHiddenDashboardIds(this.facilityId);
                this.selectPageByRoute();
            }).catch(err => {
                error('CustomDashboard', 'Failed to load dashboards', err);
                this.loadError = true;
                this.$dialog.notify.error('Failed to load dashboard data. Please try again.', {
                    position: 'top-right',
                    timeout: 5000,
                });
            });
        },
        retryLoad() {
            this.loadDashboards();
        },
        openFilters() {
            this.$refs.globalFilters.open();
        },
        handleFiltersChanges(filters) {
            this.selectedFilters = filters;
            this.filterScopeRevision++;
            this.invalidateIsolation();
            // The rows are about to move to a new dashboard scope. Never leave geometry isolated
            // from the previous scope while the replacement session is being resolved.
            this.resetViewerIsolation();
            // A global filter change redefines every widget's scope, so any active chart
            // selection must be dropped and re-queried under the new scope. skipRefresh=true so
            // the refresh below settles the session once; mirrors the page() watcher precedent.
            // Clear the selection error unconditionally — it can outlive an empty map (the
            // error-only bar state), and selections are transient per tab/filter change.
            this.selectionError = null;
            if (this.hasSelections) this.clearAllSelections(true);
            this.refreshFilterSession();
        },
        // Chart legend/datum click. New payload (multi-select): { widgetId, sourceKey,
        // tableName, filterColumn, tagColumn, filters, values: [{ value, display }] }.
        /**
         * KPI drill to list (AVA-560): a number whose config carries listDrill was clicked. The
         * widget sent its own scope (saved + runtime + selection filters, its session id or the
         * degraded raw filters); this board adds what only it knows - whether the filter session is
         * still being built - then runs the drill: catalogue lookup of what the column identifies,
         * the distinct ids under those filters (capped at the AVA list's id limit), and the
         * registry entry that opens the side-panel list. Every refusal is told to the user.
         * @param {object} payload - from DashboardItem.onListDrill
         * @param {Function} [done] - resets the widget's busy state
         */
        async onListDrill(payload, done) {
            try {
                const request = payload || {};
                const result = await runListDrill({
                    tableName: request.tableName,
                    column: request.column,
                    expectedTotal: request.expectedTotal,
                    numberIgnoresFilters: !!request.numberIgnoresFilters,
                    scopeBlock: listDrillScopeBlock({
                        filtersPending: this.filterSessionLoading,
                        filtersActive: this.hasActiveGlobalFilters || this.hasSelections,
                        globalFiltersActive: this.hasActiveGlobalFilters,
                        selectionsActive: this.hasSelections,
                        filterSessionId: request.filterSessionId,
                        globalFilters: request.globalFilters,
                        selectionFilters: request.selectionFilters,
                    }),
                    valuesRequest: buildListDrillValuesRequest({
                        filterSessionId: request.filterSessionId,
                        globalFilters: request.globalFilters,
                        savedFilters: request.savedFilters,
                        runtimeFilters: request.runtimeFilters,
                        selectionFilters: request.selectionFilters,
                        take: API_LIMITS.MAX_TAG_IDS,
                    }),
                }, {
                    loadTableEntry: tableName => this.ensureDrillTableEntry(tableName),
                    fetchValues: (tableName, column, body) => apiService
                        .columnDistinctValues(tableName, column, body)
                        .then(res => res.data),
                    registry: LIST_DRILL_REGISTRY,
                    notify: this.listDrillNotifier(),
                    log: (message, details) => warn('CustomDashboard', message, details),
                });
                if (result.outcome === LIST_DRILL_OUTCOME.FAILED) {
                    error('CustomDashboard', 'KPI drill to list failed', result.error);
                } else {
                    log('CustomDashboard', 'KPI drill to list', {
                        widgetId: request.widgetId, outcome: result.outcome, total: result.total,
                    });
                }
            } finally {
                if (typeof done === 'function') done();
            }
        },
        /** The app's notify convention, shaped for runListDrill. */
        listDrillNotifier() {
            const options = { position: 'top-right', timeout: 5000 };
            return {
                info: message => this.$dialog.notify.info(message, options),
                warning: message => this.$dialog.notify.warning(message, options),
                error: message => this.$dialog.notify.error(message, options),
            };
        },
        /**
         * The catalogue entry of a source table, for its `entityColumns`. One existing GET
         * (`apiService.tables()`), memoised per facility and table: only an entry
         * that knows entity columns is kept, so a source republished with them is seen on the next
         * click, and a failed read rejects (the drill reports it) and is retried next time.
         * @param {string} tableName
         * @returns {Promise<?object>}
         */
        ensureDrillTableEntry(tableName) {
            const table = String(tableName || '').toLowerCase();
            if (!table) return Promise.resolve(null);
            // The catalogue is facility-scoped, so the facility is part of the key.
            const key = `${this.facilityId || ''}|${table}`;
            if (!this._drillEntryByTable.has(key)) {
                const pending = apiService.tables().then((res) => {
                    const entry = findTableEntry(Array.isArray(res?.data) ? res.data : [], tableName);
                    if (!entry || !Array.isArray(entry.entityColumns) || !entry.entityColumns.length) {
                        this._drillEntryByTable.delete(key);
                    }
                    return entry;
                }, (err) => {
                    this._drillEntryByTable.delete(key);
                    throw err;
                });
                this._drillEntryByTable.set(key, pending);
            }
            return this._drillEntryByTable.get(key);
        },
        // values: [] clears that widget's entry; a null payload clears everything. Falls back
        // to the legacy single-value shape so integration order with the chart side is safe.
        onLegendFilter(payload) {
            if (!payload) {
                this.clearAllSelections();
                return;
            }
            const widgetId = payload.widgetId;
            if (widgetId == null) return;

            const hasValuesArray = Array.isArray(payload.values);
            if (hasValuesArray && payload.values.length === 0) {
                this.removeSelection(widgetId);
                return;
            }
            // Normalize values to [{ value, display }]. value = server filterValue (null for
            // 'Unset'); display = human label. Legacy emits carry filterValue/displayValue.
            const values = hasValuesArray
                ? payload.values
                : [{
                    value: payload.filterValue,
                    display: payload.displayValue != null ? payload.displayValue : payload.filterValue,
                  }];
            this.setSelection(widgetId, {
                widgetId,
                sourceKey: payload.sourceKey || null,
                tableName: payload.tableName,
                filterColumn: payload.filterColumn,
                tagColumn: payload.tagColumn,
                filters: payload.filters || null,
                // Carry the widget's 3D-isolation colouring so the isolate can paint by value: legacy
                // static map (`coloring`) + the chart fill's live schema colouring
                // (`coloringSource`/`schemaSelection`). buildColoringSpec() picks schema over static.
                coloring: payload.coloring || null,
                coloringSource: payload.coloringSource || null,
                schemaSelection: payload.schemaSelection || null,
                renderedValueColors: payload.renderedValueColors || null,
                // Which of the two colour definitions the widget declared as the winner. Absent for
                // widgets saved before that control existed → buildColoringSpec keeps the legacy order.
                highlightColorSource: payload.highlightColorSource || null,
                // The emitting chart's category domain (prepareChartData().labels). An 'auto' isolate
                // ranks colours over THIS set so its hue matches the chart fill (buildColoringSpec/
                // currentIsolationSources carry it to openViewerWithCrossFilter).
                labelDomain: Array.isArray(payload.labelDomain) ? payload.labelDomain : [],
                // The number each category showed ON the chart, plus how that chart names its
                // group-by column and itself — the 3D legend is a caption for this widget, so it
                // reports the chart's own count and vocabulary rather than re-deriving either.
                datumValues: payload.datumValues || null,
                filterColumnLabel: payload.filterColumnLabel || null,
                widgetTitle: payload.widgetTitle || null,
                values,
            });
        },
        // ── chartSelections mutations ─────────────────────────────────────────────────
        // Every mutation does the same three things: bump crossFilterGen (supersede any
        // in-flight 3D isolation, I-1/I-2), schedule the debounced session refresh, and
        // reconcile viewer isolation. Route ALL selection changes through these so no path
        // forgets one. Vue 2: $set/$delete keep object-key adds/removes reactive.
        setSelection(widgetId, entry) {
            this.selectionError = null;      // a fresh user selection clears the prior error
            this._legendRebuildTried = false; // and re-arms the one-shot legend rebuild
            this.lastMutatedWidgetId = widgetId;
            this.filterScopeRevision++;
            this.invalidateIsolation();
            if (!(widgetId in this.chartSelections)) this.chartSelectionOrder.push(widgetId);
            this.$set(this.chartSelections, widgetId, entry);
            this.afterSelectionMutation();
        },
        removeSelection(widgetId) {
            this.filterScopeRevision++;
            this.invalidateIsolation();
            if (widgetId in this.chartSelections) this.$delete(this.chartSelections, widgetId);
            const i = this.chartSelectionOrder.indexOf(widgetId);
            if (i !== -1) this.chartSelectionOrder.splice(i, 1);
            this.afterSelectionMutation();
        },
        removeSelectionValue(widgetId, value) {
            const entry = this.chartSelections[widgetId];
            if (!entry) return;
            const remaining = entry.values.filter(v => v.value !== value);
            if (!remaining.length) {
                this.removeSelection(widgetId); // last value gone → drop the whole dimension
                return;
            }
            this.filterScopeRevision++;
            this.invalidateIsolation();
            this.$set(this.chartSelections, widgetId, { ...entry, values: remaining });
            this.afterSelectionMutation();
        },
        onRemoveSelectionValue({ widgetId, value }) {
            this.removeSelectionValue(widgetId, value);
        },
        // skipRefresh (STRICT === true only, so template @event payloads never trigger it)
        // lets the page() watcher / global-filter change clear selections without scheduling
        // the debounced refresh — those call sites settle the session themselves, and a second
        // refresh here would create a duplicate session (and a duplicate widget query).
        clearAllSelections(skipRefresh) {
            this.spatialSelectionNotice = '';
            this.filterScopeRevision++;
            this.invalidateIsolation();
            this.chartSelections = {};
            this.chartSelectionOrder = [];
            this.selectionError = null;
            this._legendRebuildTried = false;
            if (skipRefresh !== true) this.refreshFilterSession();
            this.reconcileViewerIsolation();
        },
        // Common tail for the upsert/remove mutations: settle the sessions, reconcile 3D.
        afterSelectionMutation() {
            this.spatialSelectionNotice = '';
            this.refreshFilterSession();
            this.reconcileViewerIsolation();
        },
        // User-driven filter changes (global filters, legend set/clear) funnel through this
        // debounced wrapper. It resets the degraded-mode retry budget (a fresh change starts a
        // clean retry sequence) then delegates to the plain doRefreshFilterSession(). Vue 2's
        // initMethods bind() STRIPS lodash .cancel/.flush from methods-declared debounced
        // functions, so nothing may call those through `this.refreshFilterSession` — the plain
        // method exists precisely so tab-switch (B3) can await one attempt directly.
        refreshFilterSession: debounce(function () {
            this.cancelFilterSessionRetry();
            this.doRefreshFilterSession();
        }, 300),
        /**
         * Creates (or clears) the server-side filter session for the current global/legend
         * filters. Three terminal outcomes, mutually exclusive:
         *   - no filters active → filterSessionId null, filterSessionError false
         *   - session created   → filterSessionId set,  filterSessionError false (retries cancelled)
         *   - creation failed    → filterSessionId null, filterSessionError true → widgets fall
         *                          back to raw global filters; quiet notice + background retry.
         * @param {{ background?: boolean }} [opts] background retries skip the spinner + the notice.
         */
        async doRefreshFilterSession({ background = false } = {}) {
            // Latest-wins guard for the WHOLE batch: bump + capture the sequence before any
            // await, independent of crossFilterGen (which guards the 3D isolation flow). One
            // refresh fires up to (base + full + N self-excluding) creates; a superseded batch
            // is dropped wholesale so widgets never get a half-updated set of sids.
            const gen = ++this.sessionRefreshSeq;
            const scopeRevision = this.filterScopeRevision;
            const isolationGeneration = Number(this.activeIsolation?.generation || 0);
            const order = this.chartSelectionOrder.slice();
            const hasSelections = order.length > 0;

            if (!this.hasActiveGlobalFilters && !hasSelections) {
                // No filters at all → clear every session artifact (byte-compatible terminal
                // branch). This clear is the LATEST intent, so it settles the spinner even
                // though a superseded in-flight response is gen-guarded out of touching it.
                this.cancelFilterSessionRetry();
                this.filterSessionId = null;
                this.fullSid = null;
                this.sidByTableName = {};
                this.sidTagCounts = {};
                this.filterSessionTagCount = 0;
                this.filterSessionError = false;
                this.filterSessionLoading = false;
                this.resolvedFilterScopeRevision = scopeRevision;
                return;
            }

            if (!background) this.filterSessionLoading = true;

            // ── BASE session: global-only, no legend. Anchors the existing degraded machinery
            //    (filterSessionError → fallbackGlobalFilters → retry) EXACTLY as before; legend
            //    never rides on it. Reused as the self-excluding session for a lone source
            //    (whose exclusion empties to global-only).
            let baseSid = null;
            let baseTagCount = 0;
            let baseFailed = false;
            if (this.hasActiveGlobalFilters) {
                try {
                    const res = await apiService.createFilterSession({
                        facilityId: this.facilityId,
                        globalFilters: this.generalFilters,
                    });
                    if (gen !== this.sessionRefreshSeq || scopeRevision !== this.filterScopeRevision) return;
                    baseSid = res.data.filterSessionId;
                    baseTagCount = res.data.tagCount || 0;
                    this.filterSessionId = baseSid;
                    this.filterSessionTagCount = baseTagCount;
                    this.filterSessionError = false;
                    this.cancelFilterSessionRetry();
                } catch (err) {
                    if (gen !== this.sessionRefreshSeq || scopeRevision !== this.filterScopeRevision) return;
                    error('CustomDashboard', 'Filter session creation failed; widgets degraded to raw filters', err);
                    baseFailed = true;
                    this.filterSessionId = null;
                    this.filterSessionTagCount = 0;
                    this.filterSessionError = true;
                    if (!background) {
                        // Quiet, non-blocking notice on the first (foreground) failure only —
                        // background retries recover silently to avoid toast spam.
                        this.$dialog.notify.warning('Filters are running in degraded mode. Retrying in the background.', {
                            position: 'top-right',
                            timeout: 4000,
                        });
                    }
                    this.scheduleFilterSessionRetry();
                }
            } else {
                // No global filters → no base session; the global-degraded machinery stays idle.
                this.filterSessionId = null;
                this.filterSessionTagCount = 0;
                this.filterSessionError = false;
                this.cancelFilterSessionRetry();
            }

            // No selections → the base session is the whole story (today's behavior): every
            // widget queries with filterSessionId.
            if (!hasSelections) {
                this.fullSid = baseSid;
                this.sidByTableName = {};
                this.sidTagCounts = baseSid != null ? { [baseSid]: baseTagCount } : {};
                this.resolvedFilterScopeRevision = scopeRevision;
                if (!background && gen === this.sessionRefreshSeq) this.filterSessionLoading = false;
                return;
            }

            // Base infra failed while selections exist (rare — a global-filter change clears
            // selections, so they only coexist when infra drops mid-session). Keep the
            // last-good legend sids and let the base retry chain re-run this whole refresh; do
            // NOT run the dimension-removal cascade, this is not a bad-dimension case.
            if (baseFailed) {
                this.fullSid = null;
                this.sidByTableName = {};
                this.sidTagCounts = {};
                this.resolvedFilterScopeRevision = scopeRevision;
                if (!background && gen === this.sessionRefreshSeq) this.filterSessionLoading = false;
                return;
            }

            // ── LEGEND sessions: full (global ∩ all dimensions) + one EXTERNAL-ONLY session per
            //    distinct selected table (global ∩ every dimension from ANOTHER table) for the
            //    first N tables. Built as one Promise.allSettled batch under the same gen. A table
            //    whose exclusion empties reuses baseSid rather than creating a duplicate
            //    global-only session.
            //
            //    Same-table dimensions are deliberately absent from every session: many rows can
            //    share one Master Tag, so a tag intersection is WIDER than the rows the user
            //    selected. Those dimensions ride as raw predicates instead (selectionFiltersFor*).
            const gf = this.generalFilters;
            const { jobs } = buildTableSessionJobs(this.orderedSelectionEntries, MAX_SELF_EXCLUDING_TABLES);

            const settled = await Promise.allSettled(jobs.map(job =>
                job.reuseBase
                    ? Promise.resolve({ __reuseBase: true })
                    : apiService.createFilterSession({ facilityId: this.facilityId, globalFilters: gf, legendFilters: job.dims })
            ));
            if (gen !== this.sessionRefreshSeq || scopeRevision !== this.filterScopeRevision) return;

            if (settled.some(s => s.status === 'rejected')) {
                // Fail-closed: a dimension could not resolve. Keep the previous sessions, remove
                // the offending dimension, surface it, and rebuild once (handler below).
                if (!background && gen === this.sessionRefreshSeq) this.filterSessionLoading = false;
                this.handleLegendSessionFailure();
                return;
            }

            // Commit — all sessions resolved. Widgets on an UNSELECTED table get fullSid; each
            // selected table gets its external-only sid (baseSid when its exclusion emptied).
            // Record every sid's create-time tagCount so a 0-tag session reads as empty-match,
            // not stale.
            const nextSidByTableName = {};
            const nextSidTagCounts = {};
            if (baseSid != null) nextSidTagCounts[baseSid] = baseTagCount;
            let nextFullSid = baseSid;
            settled.forEach((s, i) => {
                const job = jobs[i];
                const sid = job.reuseBase ? baseSid : s.value.data.filterSessionId;
                if (!job.reuseBase) nextSidTagCounts[sid] = s.value.data.tagCount || 0;
                if (job.target === '__full__') nextFullSid = sid;
                else nextSidByTableName[job.target] = sid;
            });
            this.fullSid = nextFullSid;
            this.sidByTableName = nextSidByTableName;
            this.sidTagCounts = nextSidTagCounts;
            this.resolvedFilterScopeRevision = scopeRevision;
            this._legendRebuildTried = false; // a clean success re-arms the one-shot rebuild
            if (!background && gen === this.sessionRefreshSeq) this.filterSessionLoading = false;
            // The isolate scope carries the table's session id, and that id only becomes usable at
            // commit (isolationScopeFor gates on resolvedFilterScopeRevision). A selection made
            // before the sessions settled therefore isolated with the raw-global fallback; re-run
            // the reconcile now that the exact session exists. Resume only if no newer viewer
            // intent replaced the generation that began this session; grid clicks and viewer-close
            // always win.
            if (order.length > 1) {
                const isolationStillWaiting = Number(this.activeIsolation?.generation) === isolationGeneration
                    && this.activeIsolation?.ownerWidgetId == null;
                if (isolationStillWaiting) this.reconcileViewerIsolation();
            }
        },
        /** Fail-closed legend-session recovery. Selections are built incrementally, so the
         *  just-added dimension is the culprit by construction: remove it, name it on the bar,
         *  rebuild once. A second failure clears all selections rather than leaving a board
         *  filtered by a broken session. No search loop. */
        handleLegendSessionFailure() {
            const culprit = this.lastMutatedWidgetId;
            if (!this._legendRebuildTried && culprit != null && culprit in this.chartSelections) {
                this._legendRebuildTried = true;
                const label = this.selectionColumnLabel(culprit);
                this.selectionError = { message: `Couldn't apply the ${label} selection, so it was removed.` };
                this.removeSelection(culprit); // drops the dimension + schedules the rebuild
                return;
            }
            this._legendRebuildTried = false;
            this.selectionError = { message: `Couldn't apply the chart selection, so selections were cleared.` };
            this.clearAllSelections();
        },
        /** Column label for a selection's error message (falls back to a generic word). */
        selectionColumnLabel(widgetId) {
            const entry = this.chartSelections[widgetId];
            return (entry && entry.filterColumn) || 'chart';
        },
        /** The session id a widget on `tableName` should query with: the base session when there
         *  is no selection (today's behavior), that table's EXTERNAL-ONLY session when the table
         *  carries a dimension, otherwise the full cross-source session. A widget with no physical
         *  table (AI/legacy content) also falls through to the full session. */
        sidForTable(tableName) {
            if (!this.chartSelectionOrder.length) return this.filterSessionId;
            const table = normalizeTableName(tableName);
            if (table && table in this.sidByTableName) return this.sidByTableName[table];
            return this.fullSid;
        },
        /** True for a selected table beyond the session cap: its widgets freeze (keep their
         *  current data, show a reference badge) instead of re-querying. A widget with no table,
         *  or on a table nothing is selected on, is never frozen. */
        isFrozenTable(tableName) {
            return isFrozenTable(this.orderedSelectionEntries, tableName, MAX_SELF_EXCLUDING_TABLES);
        },
        /** Fail-closed: the api refused a same-table selected column on a target widget's table
         *  (the column is no longer admitted there). The predicate is NEVER dropped and the query
         *  is never retried without it — that would silently widen the widget to unfiltered rows.
         *  Name the failure and drop the previous scene instead. */
        onSelectionFilterRejected(payload) {
            const column = (payload && payload.column) || null;
            const label = column ? formatHeaderName(column) : 'chart';
            this.selectionError = {
                message: `The ${label} selection can't be applied to every widget on this dashboard.`,
            };
            this.invalidateIsolation();
            this.resetViewerIsolation();
        },
        /** A widget queried with a sid whose rows were purged (session-expired): renew once.
         *  Single-flight so a wall of widgets hitting the stale sid triggers ONE refresh. */
        onWidgetSessionExpired() {
            if (this._sessionRenewInFlight) return;
            this._sessionRenewInFlight = true;
            Promise.resolve(this.doRefreshFilterSession({ background: true }))
                .finally(() => { this._sessionRenewInFlight = false; });
        },
        /** True when the widget's assigned session was created with ZERO tags — an empty-match
         *  (the combined selection matched nothing), not a stale session. Widgets on such a sid
         *  render empty and never renew (decision 10). A null sid (no session) is NOT an
         *  empty-match — that widget is intentionally unfiltered. */
        sessionEmptyMatchForTable(tableName) {
            const sid = this.sidForTable(tableName);
            return sid != null && this.sidTagCounts[sid] === 0;
        },
        /** Start one viewer-writing intent. Every later intent replaces this immutable claim. */
        beginIsolationClaim(ownerWidgetId, operation = null) {
            this.prepareIsolationOwnershipChange();
            const generation = Number(this.activeIsolation?.generation || 0) + 1;
            const claim = {
                generation,
                ownerWidgetId: ownerWidgetId == null ? null : String(ownerWidgetId),
                operation,
            };
            this.activeIsolation = claim;
            this.crossFilterGen = generation;
            return { ...claim };
        },
        /** Invalidate pending work without granting viewer ownership to another widget. */
        invalidateIsolation() {
            this.prepareIsolationOwnershipChange();
            const generation = Number(this.activeIsolation?.generation || 0) + 1;
            this.activeIsolation = { generation, ownerWidgetId: null };
            this.crossFilterGen = generation;
            return { ...this.activeIsolation };
        },
        /** Let the current owner synchronously restore transient POI pulse colors while its
         *  claim is still valid. Running this after replacing activeIsolation would be too late. */
        prepareIsolationOwnershipChange() {
            const items = this.$refs?.dashboardItems;
            if (!items) return;
            const list = Array.isArray(items) ? items : [items];
            list.forEach(item => item?.prepareIsolationOwnershipChange?.());
        },
        isIsolationClaimCurrent(claim) {
            if (!claim || claim.ownerWidgetId == null) return false;
            return Number(claim.generation) === Number(this.activeIsolation?.generation)
                && String(claim.ownerWidgetId) === String(this.activeIsolation?.ownerWidgetId);
        },
        /** Choose a session only when it was created for this exact selection/global scope.
         *  Otherwise send the equivalent raw global filters; session and raw paths stay exclusive.
         *
         *  The scope is keyed by the isolate's PHYSICAL TABLE, and the table's session is
         *  external-only, so it never re-applies a same-table dimension the caller is already
         *  sending as an exact raw predicate. */
        isolationScopeFor(tableName) {
            if (this.resolvedFilterScopeRevision === this.filterScopeRevision) {
                const sid = this.sidForTable(tableName);
                if (sid) {
                    return {
                        filterSessionId: sid,
                        sessionEmptyMatch: this.sidTagCounts[sid] === 0,
                    };
                }
            }
            return {
                globalFilters: this.hasActiveGlobalFilters ? this.generalFilters : null,
                sessionEmptyMatch: false,
            };
        },
        /** Auto-open-viewer preference (browser-local, default ON). The pref gates ONLY viewer
         *  auto-opening — never selection state, session creation, or widget filtering. Reads
         *  storage FRESH at use time: the pref can be flipped from another dashboard tab, and a
         *  mount-time snapshot kept gating on the stale value until a full reload. The reactive
         *  mirror (autoOpenViewer3d) only drives UI (cog switch + manual chip) and is synced by
         *  v-model in this tab and the storage listener for other tabs. */
        isAutoOpenViewerEnabled() {
            return getDashboardPreference(DASHBOARD_PREF.AUTO_OPEN_VIEWER) !== false;
        },
        /** Settings-cog toggle write-through: persist the browser-local preference; the reactive
         *  mirror (v-model) already carries the new value. */
        onAutoOpenViewerToggle(value) {
            setDashboardPreference(DASHBOARD_PREF.AUTO_OPEN_VIEWER, value);
        },
        /** Manual "Open in 3D" from the selection bar — isolates the CURRENT selection (single or
         *  multi value, single or multi chart on one source), invoked explicitly when the auto-open
         *  preference is OFF. Same generalized isolate the auto-open path runs.
         *
         *  A same-table multi-chart selection no longer waits for a session: its dimensions are
         *  exact raw predicates, so the payload is complete the moment the user selects. A null
         *  payload now means only "nothing isolatable" (no selection, or a cross-table selection
         *  with no single projection table), never "still resolving". */
        openSingleSelectionInViewer() {
            const payload = this.currentIsolationPayload();
            if (!payload) return;
            this.openViewerWithCrossFilter(payload);
        },
        /** Schedules ONE bounded background retry of the session (2s → 8s → 30s, then stops).
         *  Invariant: at most one pending retry timer. Cancelled on success, no-filters, or a
         *  fresh user filter change; never blocks any UI flow. */
        scheduleFilterSessionRetry() {
            const delays = [2000, 8000, 30000];
            if (this._filterRetryTimer) clearTimeout(this._filterRetryTimer);
            if (!this._filterRetryAttempt) this._filterRetryAttempt = 0;
            if (this._filterRetryAttempt >= delays.length) return; // budget exhausted — stay degraded
            const delay = delays[this._filterRetryAttempt];
            this._filterRetryAttempt++;
            this._filterRetryTimer = setTimeout(() => {
                this._filterRetryTimer = null;
                if (!this.filterSessionError) return; // already recovered by another path
                this.doRefreshFilterSession({ background: true });
            }, delay);
        },
        /** Clears the pending retry timer and resets the retry budget. */
        cancelFilterSessionRetry() {
            if (this._filterRetryTimer) {
                clearTimeout(this._filterRetryTimer);
                this._filterRetryTimer = null;
            }
            this._filterRetryAttempt = 0;
        },
        dashboardUpdateHandler() {
            // Duplicate-name block (spec §6).
            if (this.duplicateNameError) return;
            this.dashboardModal.loading = true;
            var payload = { ...this.selectedPage, facilityId: this.facilityId };
            if (!this.selectedPage.id) payload.color = DENSE_LAYOUT_PAGE_MARKER;
            if (this.selectedPage.id) {
                dashboardPagesService.update(payload).then((res) => {
                    var index = this.pages.findIndex(e => e.id == this.selectedPage.id);
                    // Update the page's fields reactively in place: an index write
                    // (pages[i] = x) is invisible to Vue 2, so the cached renderedTabs kept the
                    // old name; swapping the object would re-fire the page() watcher (widget
                    // reload + selection clear) for a rename.
                    if (index > -1) {
                        const target = this.pages[index];
                        Object.keys(res.data || {}).forEach(key => this.$set(target, key, res.data[key]));
                    }
                    if (this.tab == index) {
                        // Rename does not change the page id, so the ?tab= deep link stays
                        // valid — re-assert it.
                        this.$router.replace({
                            name: 'custom-dashboard',
                            params: { id: this.facilityId },
                            query: { [TAB_QUERY_KEY]: String(this.pages[this.tab]?.id) }
                        });

                    }
                    this.$dialog.notify.success('Page updated successfully.', {
                  position: 'top-right',
                  timeout: 3000,
                });
                }).catch((err) => {
                    this.$dialog.notify.error(err.request.response, {
                        position: 'top-right',
                        timeout: 3000,
                    });
                }).finally(() => {
                    this.dashboardModal.loading = false;
                    this.dashboardModal.active = false;
                })
            }
            else {

                dashboardPagesService.create(payload).then((res) => {
                    this.$dialog.notify.success('Page added successfully.', {
                  position: 'top-right',
                  timeout: 3000,
                });
                    this.pages.push(res.data);
                    this.noData=false;
                }).catch((err) => {
                    this.$dialog.notify.error(err.request.response, {
                        position: 'top-right',
                        timeout: 3000,
                    });
                }).finally(() => {
                    this.dashboardModal.loading = false;
                    this.dashboardModal.active = false;
                })
            }

        },
        closeDashboardModal() {
            this.dashboardModal.active = false;
        },
        deletePage(id) {

            this.$dialog.warning({
                text: `Are you sure you want to delete this Dashboard?`,
                title: `Delete Dashboard?`,
                color: 'error',
                persistent: true,
                actions: {
                    false: {
                        text: 'Cancel',
                    },
                    true: {
                        text: 'Confirm',
                        color: 'error',
                        handle: () => {
                            // Deletion-specific branch: drop the deleted page's diffs, settle
                            // in-flight non-blocking, focus left-adjacent. The dialog closes
                            // immediately (returning a promise would make it wait).
                            this.performTabDeletion(id);
                        },
                    },
                },
            });

        },
        addPage() {

            this.selectedPage = {
                icon: 'mdi-view-dashboard',
                color: DENSE_LAYOUT_PAGE_MARKER,
            };
            this.dashboardModal.active = true;

        },
        updatePage(id) {
            var page = this.pages.find(e => e.id == id);
            this.selectedPage = structuredClone(page);
            this.dashboardModal.active = true;
        },
        query(id) {
            return apiService.query(id).then(res => {
                resolve(res.data);
            }).catch(err => {
                reject(err);
            });
        },

        getWidgets(pageId) {
            return apiService.widgets(pageId);
        },
        /** Scale a widget's x/y/width/height by `ratio`, rounding to the target grid's integer
         *  cells and clamping width/height to at least 1. Live w/h takes precedence over the
         *  persisted width/height so one helper serves both density directions. */
        scaleGeometry(widget, ratio) {
            const width = widget.w ?? widget.width;
            const height = widget.h ?? widget.height;
            return {
                ...widget,
                x: Math.round((Number(widget.x) || 0) * ratio),
                y: Math.round((Number(widget.y) || 0) * ratio),
                width: Math.max(1, Math.round((Number(width) || 1) * ratio)),
                height: Math.max(1, Math.round((Number(height) || 1) * ratio)),
            };
        },
        /** Convert persisted integer geometry into the active client's grid density. */
        toUiWidgetGeometry(widget, storageDensity = this.layoutStorageDensity) {
            return this.scaleGeometry(widget, this.layoutUiDensity / storageDensity);
        },
        /** Convert live w/h grid geometry to the page's persisted integer density. */
        toStorageWidgetPayload(widget, storageDensity = this.layoutStorageDensity) {
            return this.scaleGeometry(widget, storageDensity / this.layoutUiDensity);
        },
        getWidgetsData() {
            if (this.isDefaultPage) {
                this.pageLoading = false;
                return Promise.resolve();
            }

            // Returned so the Discard best-effort refetch can await/settle on it.
            const pageId = this.page.id;
            return this.getWidgets(pageId).then(res => {
                // A late answer for a dashboard the user already left must not replace the
                // widgets of the one now shown (that page's own load owns the layout).
                if (this.page.id !== pageId) return;
                this.layout = []
                this.layout.push(...res.data.map((e, i) => this.mapEntity(e, i)));

            }).finally(() => {
                if (this.page.id === pageId) this.pageLoading = false;
            })
        },
        addWidgetHandler() {
            this.$refs.editWidget.open();
        },
        /** Open the Add-Widget wizard seeded from a grid's data source (create path,
         *  so the new chart is a sibling that shares the grid's filterSessionId). */
        onCreateChartFromGrid(seed) {
            this.$refs.editSqlViewWidget.openFromSource(seed);
        },
        /**
         * Save from a widget editor. `settle` (passed by the SQL-view wizard) is called with no
         * argument on success and with the error on failure, so the wizard can stay open and show
         * what the server rejected. Editors that pass no callback get a toast instead.
         */
        editAIWidgetHandler(widget, settle) {
            if (widget.id) {
                this.updateContentHandler(widget, settle);
            } else {
                this.createWidgetHandler(widget, settle);
            }
        },
        /**
         * Create a widget. `settle` reports the SAVE only: it runs as soon as the server accepted
         * the widget (or the request failed / could not be built), never after the follow-up work,
         * so a created widget is never reported as "not saved".
         */
        createWidgetHandler(widget, settle) {
            let request;
            try {
                // A count/KPI is a single number — default it to a compact tile (grid 5x2 ≈
                // 340x200px on the 18-col / 100px-row layout) so the value reads as a KPI card
                // instead of a number lost in a large empty tile. Count-only so charts/tables/grids
                // keep the standard default. Only affects NEW widgets.
                const isCompactKpi = widget.messageType === 'count';
                const density = this.layoutUiDensity;
                const defaultWidth = this.defaultWidgetWidth * density;
                const defaultHeight = this.defaultWidgetHeight * density;
                const payload = {
                    ...widget,
                    x: (this.layout.length * defaultWidth) % this.gridColNum,
                    y: (this.layout.length + LEGACY_GRID_COLUMNS) * density, // puts it at the bottom
                    width: isCompactKpi ? 5 * density : defaultWidth,
                    height: isCompactKpi ? 2 * density : defaultHeight,
                    i: this.layout.length,
                    aiGenerated: true,
                    pageId: this.page.id
                };
                if (payload.legend) {
                    payload.legend = JSON.stringify(payload.legend);
                }
                request = apiService.create(this.toStorageWidgetPayload(payload));
            } catch (err) {
                error('[CustomDashboard] widget create failed', err);
                this.settleWidgetSave(settle, err);
                return;
            }
            // Two-argument then: a follow-up failure must never reach the save-failure handler.
            request.then(res => {
                this.settleWidgetSave(settle);
                this.widgetSaveFollowUp(() => {
                    this.layout.push(this.mapEntity(res.data));
                });
            }, err => {
                error('[CustomDashboard] widget create failed', err);
                this.settleWidgetSave(settle, err);
            });
        },
        movedHandler(i, newX, newY) {
            var widget = this.layout.find(e => e.i == i);
            if (!widget) return;
            // Never persist outside an active edit session — a vue-grid-layout emit from a
            // remount/compaction in view mode would otherwise flush against an empty
            // baseline (every widget "changed" → spurious mass-write).
            if (!this.editMode) return;
            // Coalesced autosave: a resumed drag cancels a stuck exit, then buffers — the
            // diff at settle captures every compaction-moved neighbour.
            this.exitCoordinator.notifyEditingResumed();
            this.saveQueue.scheduleFlush(this.getEditContext());
        },
        resizedHandler(i, newH, newW, newHPx, newWPx) {
            var widget = this.layout.find(e => e.i == i);
            if (!widget) return;
            if (!this.editMode) return;
            this.exitCoordinator.notifyEditingResumed();
            this.saveQueue.scheduleFlush(this.getEditContext());
        },
        // ── builder-v1 edit/view mode + exit coordination ──────────────────────────
        setEditMode(v) {
            this.editMode = v;
            if (v) this.registerUnloadGuard();
            else this.unregisterUnloadGuard();
            log('CustomDashboard', `edit mode → ${v ? 'ON' : 'OFF'}`);
        },
        // beforeunload durability: while editing, a hard unload (F5 / tab close) can happen with a
        // coalesced layout autosave still buffered (the trailing timer not yet fired) or a PUT in
        // flight. Registered on edit-enter, removed on exit AND in beforeDestroy.
        registerUnloadGuard() {
            if (this._unloadGuardFn) return;
            this._unloadGuardFn = (e) => this.onBeforeUnloadDuringEdit(e);
            window.addEventListener('beforeunload', this._unloadGuardFn);
        },
        unregisterUnloadGuard() {
            if (!this._unloadGuardFn) return;
            window.removeEventListener('beforeunload', this._unloadGuardFn);
            this._unloadGuardFn = null;
        },
        onBeforeUnloadDuringEdit(e) {
            if (!this.saveQueue || !this.saveQueue.hasPending()) return;
            // Fire the buffered diff immediately (no await, the page is leaving) so the PUTs go out,
            // then ask for the browser's native leave prompt so the in-flight requests get a moment
            // to land before the tab closes. Best-effort only; no custom dialog.
            this.saveQueue.flushForExit(this.getEditContext());
            log('CustomDashboard', 'unload during edit with pending layout, flushing before leave');
            e.preventDefault();
            e.returnValue = '';
        },
        async migrateActivePageToDenseLayout() {
            if (this.layoutStorageDensity === DENSE_LAYOUT_DENSITY) return true;
            if (this.layoutMigrationLoading) return false;

            this.layoutMigrationLoading = true;
            const pageId = this.page?.id;
            const pageIndex = this.tab;
            const context = { facilityId: this.facilityId, pageId };
            const densePayloads = this.layout.map(item =>
                this.toStorageWidgetPayload(item, DENSE_LAYOUT_DENSITY));
            const legacyPayloads = this.layout.map(item =>
                this.toStorageWidgetPayload(item, LEGACY_LAYOUT_DENSITY));

            try {
                // Write every widget first. The page remains marked legacy until the complete
                // conversion succeeds, so a reload cannot interpret a partial conversion as dense.
                for (const payload of densePayloads) {
                    await apiService.updateLayout(payload, context);
                }
                await dashboardPagesService.update({
                    ...this.page,
                    facilityId: this.facilityId,
                    color: DENSE_LAYOUT_PAGE_MARKER,
                });
                this.$set(this.pages[pageIndex], 'color', DENSE_LAYOUT_PAGE_MARKER);
                log('CustomDashboard', 'layout density migrated', { pageId, widgets: densePayloads.length });
                return true;
            } catch (err) {
                // A response can be lost after the page marker commits. Confirm the server state
                // before rolling widget geometry back, otherwise a successful migration could be
                // mistaken for a failure and leave dense-marked pages with legacy coordinates.
                try {
                    const pagesResponse = await dashboardPagesService.get(this.facilityId);
                    const remotePage = (pagesResponse.data || []).find(p => p.id === pageId);
                    if (Number(remotePage?.color) === DENSE_LAYOUT_PAGE_MARKER) {
                        this.$set(this.pages[pageIndex], 'color', DENSE_LAYOUT_PAGE_MARKER);
                        return true;
                    }
                } catch (verifyErr) {
                    warn('CustomDashboard', 'layout density verification failed', verifyErr);
                }

                // Best-effort idempotent rollback. All payloads are written, not only the ones
                // known to have completed, because a timed-out request may still have committed.
                await Promise.allSettled(legacyPayloads.map(payload =>
                    apiService.updateLayout(payload, context)));
                error('CustomDashboard', 'layout density migration failed', err);
                this.$dialog.notify.error('Could not prepare the finer layout grid. Please try again.', {
                    position: 'top-right',
                    timeout: 5000,
                });
                return false;
            } finally {
                this.layoutMigrationLoading = false;
            }
        },
        async enterEditMode() {
            if (!await this.migrateActivePageToDenseLayout()) return false;
            // Capture the context + baseline the whole session (and its final diff) writes against.
            this.capturedFacilityId = this.facilityId;
            this.capturedPageId = this.page?.id;
            this.saveQueue.initAcknowledged(this.layout);
            // Entry snapshot for a true Cancel: autosave advances the save-queue baseline as
            // drags persist, so this is the ONLY record of the pre-edit positions.
            this._editEntryLayout = this.layout.map(w => ({ ...w }));
            this.setEditMode(true);
            return true;
        },
        getEditContext() {
            return { facilityId: this.capturedFacilityId, pageId: this.capturedPageId };
        },
        onDoneClick() {
            this.exitCoordinator.requestExit(null);
        },
        onCancelClick() {
            // Cancel reverts the rearrangement (position/size), NOT widget membership: creates
            // and deletes persist immediately during a session, so restoring the whole snapshot
            // would drop session-created widgets (still on the server) and resurrect deleted ones
            // (gone from the server) — a desync. Merge positions by id for widgets that still
            // exist; keep created widgets in place; never resurrect deleted ones. The coordinator's
            // final diff then PUTs the reverted positions, undoing autosaved moves.
            if (this._editEntryLayout) {
                const entry = new Map(this._editEntryLayout.map(w => [w.id, w]));
                this.layout = this.layout.map((item) => {
                    const s = entry.get(item.id);
                    return s ? { ...item, x: s.x, y: s.y, w: s.w, h: s.h } : item;
                });
            }
            this.exitCoordinator.requestExit(null);
        },
        onBandAddDashboard() {
            // Creating a new dashboard while editing must not escape edit mode uncoordinated —
            // drain + exit the current edit session first, then open the creation chooser.
            if (this.editMode) {
                this.exitCoordinator.requestExit(() => this.onAddDashboardClick());
                return;
            }
            this.onAddDashboardClick();
        },
        onTabClick(index) {
            // Clicking the already-active tab is a no-op — it must never trigger an exit
            // (in edit mode that was flipping edit → view after the kebab menu closed).
            if (index === this.tab) return;
            if (this.editMode) {
                this.exitCoordinator.requestExit(() => { this.tab = index; });
                return;
            }
            this.tab = index;
        },
        onRetrySave() {
            this.exitCoordinator.retry();
        },
        onDiscardSave() {
            this.exitCoordinator.discard();
        },
        async onEmptyAddWidget() {
            // Editor empty-state CTA: enter Edit (if needed) then open the Add-Widget wizard.
            if (!this.editMode && !await this.enterEditMode()) return;
            this.$refs.editSqlViewWidget.open();
        },
        // ── builder-v1 creation chooser (spec §6) ──────────────────────────────────
        /** "Add Dashboard" entry point: opens the creation chooser. */
        onAddDashboardClick() {
            this.openCreationChooser();
        },
        openCreationChooser() {
            this.creationChooser.active = true;
            log('CustomDashboard', 'creation chooser opened', {
                starterDisabledReason: this.starterState.available ? null : this.starterState.reason,
                duplicateDisabledReason: this.duplicateState.available ? null : this.duplicateState.reason,
            });
            this.runStarterPreflight();
        },
        onChooserInput(v) {
            this.creationChooser.active = v;
        },
        onChooserSelect(kind) {
            if (kind === 'blank') {
                this.creationChooser.active = false;
                this.addPage();
                return;
            }
            // Starter/Duplicate are transactional-endpoint-only and render disabled until
            // it ships, so a selection should never reach here today — guard defensively.
            if (!TRANSACTIONAL_CREATE_AVAILABLE) {
                warn('CustomDashboard', `creation "${kind}" ignored — transactional endpoint unavailable`);
                return;
            }
            if (kind === 'starter') {
                this.createTransactional(this.buildStarterOperation());
            }
            // Duplicate's source-picker journey lands with the endpoint (out of W4 scope).
        },
        /**
         * Deterministic starter preflight (spec §6): resolve the 5 sources 1:1 against the
         * live catalog, fetch columns for the resolved views, validate columns/type/geometry.
         * Fails CLOSED — any fetch/network error disables the Starter with a reason.
         * Cached per facility per session; a facility switch during the await is dropped.
         */
        async runStarterPreflight() {
            const facilityId = this.facilityId;
            const cached = this._preflightCacheByFacility[facilityId];
            if (cached) {
                this.starterPreflight = { ...cached };
                log('CustomDashboard', 'starter preflight (cached)', { facilityId, ok: cached.ok, reason: cached.reason });
                return;
            }
            this.starterPreflight = { status: 'loading', ok: false, reason: '' };
            let result;
            try {
                const tablesRes = await apiService.tables();
                const catalog = tablesRes.data || [];
                const resolution = resolveStarterSources(catalog);
                const columnsByTable = {};
                if (resolution.ok) {
                    await Promise.all(resolution.tableNames.map(async (t) => {
                        const colRes = await apiService.tableColumns(t);
                        columnsByTable[t] = colRes.data || [];
                    }));
                }
                const preflight = preflightStarter(catalog, columnsByTable);
                result = {
                    status: 'ready',
                    ok: preflight.ok,
                    reason: preflight.reason || '',
                    resolvedManifest: preflight.resolvedManifest || null,
                };
            } catch (err) {
                // A network/fetch error is a preflight failure, not a surfaced exception.
                error('CustomDashboard', 'starter preflight failed', err);
                result = { status: 'ready', ok: false, reason: 'Starter unavailable: couldn’t verify data sources on this facility.', resolvedManifest: null };
            }
            if (this.facilityId !== facilityId) return; // facility switched mid-flight — drop
            this._preflightCacheByFacility[facilityId] = result;
            this.starterPreflight = { ...result };
            log('CustomDashboard', 'starter preflight resolved', { facilityId, ok: result.ok, reason: result.reason });
        },
        /** Build the starter creation operation from the resolved preflight manifest. */
        buildStarterOperation() {
            const manifest = (this._preflightCacheByFacility[this.facilityId] || {}).resolvedManifest;
            return { name: manifest && manifest.name, icon: manifest && manifest.icon, widgets: (manifest && manifest.widgets) || [] };
        },
        /**
         * Transactional create (spec §6 ship criterion) — wired for when the endpoint
         * ships. Defense-in-depth (gate round-2 fix #5): one in-flight guard, a per-op
         * operationId so Retry re-runs the COMPLETE operation, a single surfaced error;
         * the endpoint's own atomic rollback is the real all-or-nothing guarantee.
         * Unreachable while TRANSACTIONAL_CREATE_AVAILABLE is false (service stub rejects).
         */
        createTransactional(operation) {
            if (this.creationChooser.creating) return;
            this.creationChooser.creating = true;
            const request = {
                operationId: `cdb-create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                facilityId: this.facilityId,
                ...operation,
            };
            dashboardPagesService.createWithWidgets(request).then((res) => {
                this.creationChooser.active = false;
                this.pages.push(res.data);
                this.noData = false;
                this.$dialog.notify.success('Dashboard created successfully.', { position: 'top-right', timeout: 3000 });
            }).catch((err) => {
                error('CustomDashboard', 'transactional create failed', err);
                this.$dialog.notify.error('Couldn’t create the dashboard. Nothing was saved — please retry.', { position: 'top-right', timeout: 5000 });
            }).finally(() => {
                this.creationChooser.creating = false;
            });
        },
        restoreLayoutFromAcked(acked) {
            // Discard: paint the acknowledged geometry back and force the grid to remount.
            const byId = new Map((acked || []).map(a => [a.id, a]));
            this.layout = this.layout.map(item => {
                const a = byId.get(item.id);
                if (!a) return item;
                return { ...item, x: a.x, y: a.y, w: a.w, h: a.h, width: a.w, height: a.h };
            });
            this.gridKey++;
        },
        refetchLayoutForDiscard() {
            // Best-effort authoritative reload after a Discard exit (never blocks the exit).
            this.pageLoading = true;
            return this.getWidgetsData();
        },
        confirmDiscardDialog() {
            return new Promise(resolve => {
                this.$dialog.warning({
                    text: 'Discard unsaved layout changes? Your recent drag/resize changes will be lost and the last saved layout restored.',
                    title: 'Discard unsaved layout?',
                    color: 'error',
                    persistent: true,
                    actions: {
                        false: { text: 'Cancel', handle: () => resolve(false) },
                        true: { text: 'Discard', color: 'error', handle: () => resolve(true) },
                    },
                });
            });
        },
        resizeHandler(i, newH, newW, newHPx, newWPx) {
            var widget = this.layout.find(e => e.i == i);
        },
        update(widget) {

            widget.width = widget.w;
            widget.height = widget.h;
            this.updateWidget(widget).then(res => {

            }).finally(() => {

            });
        },

        updateContentHandler(widget, settle) {
            let request;
            try {
                // Editing a widget may replace its source/tag linkage. No in-flight isolate from the
                // previous configuration may finish against the shared viewer.
                this.invalidateIsolation();
                const existing = this.layout.find(e => e.id == widget.id);
                const payLoad = {
                    ...existing,
                    ...structuredClone(widget),
                    width: existing?.w || existing?.width,
                    height: existing?.h || existing?.height,
                    x: existing?.x,
                    y: existing?.y,
                    pageId: existing?.pageId || this.page?.id,
                };
                request = this.updateContent(payLoad);
            } catch (err) {
                error('[CustomDashboard] widget update failed', err);
                this.settleWidgetSave(settle, err);
                return;
            }
            // Settle on the server's answer, before the refresh (see createWidgetHandler).
            request.then(() => {
                this.settleWidgetSave(settle);
                this.widgetSaveFollowUp(() => {
                    this.pageLoading = true;
                    return this.getWidgetsData();
                });
            }, err => {
                error('[CustomDashboard] widget update failed', err);
                this.settleWidgetSave(settle, err);
            });
        },
        /**
         * Run the work that follows an ACCEPTED widget save (show the widget, refresh the page).
         * The save already succeeded, so a failure here (thrown or rejected) is logged and toasted
         * as a refresh problem and clears the page spinner; it is never reported as a failed save.
         */
        widgetSaveFollowUp(step) {
            const fail = (err) => {
                error('[CustomDashboard] widget saved, but the dashboard did not refresh', err);
                this.pageLoading = false;
                this.$dialog.notify.error('The widget was saved, but the dashboard could not refresh. Reload the page to see it.', {
                    position: 'top-right',
                    timeout: 8000,
                });
            };
            try {
                return Promise.resolve(step()).catch(fail);
            } catch (err) {
                fail(err);
                return Promise.resolve();
            }
        },
        /** Report a widget save result to the editor that asked for it, or toast a failure. */
        settleWidgetSave(settle, err) {
            if (typeof settle === 'function') {
                settle(err);
                return;
            }
            if (err) {
                this.$dialog.notify.error(widgetSaveErrorMessages(err).join(' '), {
                    position: 'top-right',
                    timeout: 8000,
                });
            }
        },
        updateContent(widget){
            const payload = this.toStorageWidgetPayload(widget);
            if(payload.legend){
                payload.legend=JSON.stringify(payload.legend);
            }
          return  apiService.updateContent(payload);
        },
        duplicate(id) {
            var widget = this.layout.find(e => e.id == id);
            this.duplicateWidget(id).then(res => {
                this.$dialog.notify.success('widget duplicated successfully.', {
                  position: 'top-right',
                  timeout: 3000,
                });
                this.layout.push(this.mapEntity(res.data));
            })


        },
        duplicateWidget(id){
            return apiService.duplicate(id);
        },
        deleteWidget(id){
          return apiService.del(id);
         },
        del(id) {
            this.invalidateIsolation();

            this.$dialog.warning({
                text: `Are you sure you want to delete this widget?`,
                title: `Delete Widget?`,
                color: 'error',
                persistent: true,
                actions: {
                    false: {
                        text: 'Cancel',
                    },
                    true: {
                        text: 'Confirm',
                        color: 'error',
                        handle: () => {
                            this.deleteWidget(id).then(res => {
                                var index = this.layout.findIndex(e => e.id == id);

                                if (index > -1) {
                                    this.layout.splice(index, 1);
                                    this.pageLoading = true;
                                    this.getWidgetsData();
                                }

                            }).finally(() => {

                            });

                        },
                    },
                },
            });


        },
        openRowDetails(details) {
            if (!details?.row) return;
            this.rowDetailsRow = details.row;
            this.rowDetailsConfig = details.config || null;
            this.rowDetailsOwnerWidgetId = details.widgetId ?? null;
            this.rowDetailsCanIsolate = !!details.canIsolate;
            this.rowDetailsOpen = true;
        },
        isolateDetailsRow(row) {
            if (!this.rowDetailsCanIsolate || !row) return;
            const items = this.$refs.dashboardItems;
            const list = Array.isArray(items) ? items : (items ? [items] : []);
            const owner = list.find(item => String(item?.message?.id) === String(this.rowDetailsOwnerWidgetId));
            this.rowDetailsOpen = false;
            this.$nextTick(() => owner?.isolateDetailsRow?.(row));
        },
        onWidgetOpenViewer(ownerWidgetId, operation = null) {
            if (operation?.isolationClaim && !this.isIsolationClaimCurrent(operation.isolationClaim)) {
                this.cancelPendingWidgetIsolation(ownerWidgetId, operation);
                return Promise.resolve();
            }
            // A concrete grid operation supersedes any chart/KPI replay payload left from the
            // previous scene. Its own grid component retains the exact typed scope for replay.
            if (operation) this._lastExactFocusPayload = null;
            const claim = operation?.isolationClaim
                ? { ...operation.isolationClaim, operation }
                : this.beginIsolationClaim(ownerWidgetId, operation);
            this.viewerOpened = true;
            this.viewerTab = '3D View';
            return new Promise(resolve => {
                this.$nextTick(() => {
                    if (!this.isIsolationClaimCurrent(claim)) {
                        this.cancelPendingWidgetIsolation(ownerWidgetId, claim.operation);
                        resolve();
                        return;
                    }
                    Promise.resolve(this.$refs.viewersSection?.load3dViewer()).then(() => {
                        if (this.isIsolationClaimCurrent(claim)) {
                            this.isolateSelectedWidgetTag(ownerWidgetId, claim);
                        } else {
                            this.cancelPendingWidgetIsolation(ownerWidgetId, claim.operation);
                        }
                        resolve();
                    });
                });
            });
        },
        // ── 3D isolation: build the request from the current selection ────────────────
        /** One WHERE-predicate per selected dimension over its selected values. The single
         *  implementation lives in the pure scope helper so the widget query, the grid datasource
         *  and spatial focus can never drift to different operators. */
        buildSelectionFilters(entries) {
            return buildSelectionFilters(entries);
        },
        /** The colouring spec + per-tag colouring column for a selection entry.
         *
         *  A widget can carry TWO unrelated colour definitions at once — the chart's series colour
         *  SCHEMA (a shared named registry) and the PROPERTY colours (`Colour models by`, an inline
         *  map built from the column's own distinct values). They routinely disagree: the same value
         *  can be green in one and orange in the other, and a schema's fixed vocabulary may not even
         *  contain a value the data has.
         *
         *  Which one wins used to be decided HERE, silently — schema always beat property. The user
         *  could configure both and nothing on screen said which governed. Now the widget states it
         *  (`highlightColorSource`), and this function obeys:
         *    'schema'   → the live registry schema (schema edits recolour the isolate)
         *    'property' → the inline value→colour map
         *  ABSENT ⇒ the legacy schema-wins order, so widgets saved before the control existed keep
         *  painting exactly as they do today.
         *
         *  A schema colours by the group-by value == filterColumn, so that is the per-tag column
         *  requested; the property map colours by its own column. Neither available ⇒ plain isolate.
         */
        buildColoringSpec(entry) {
            if (!entry) return { spec: null, coloringColumn: null, authority: null };

            const renderedValueColors = entry.renderedValueColors
                && typeof entry.renderedValueColors === 'object'
                && Object.keys(entry.renderedValueColors).length
                ? entry.renderedValueColors
                : null;
            const renderedSchemaSpec = (entry.coloringSource === COLORING_SOURCE.SCHEMA && renderedValueColors)
                ? {
                    spec: { mode: 'static', valueColors: renderedValueColors },
                    coloringColumn: entry.filterColumn,
                    authority: HIGHLIGHT_COLOR_SOURCE.SCHEMA,
                }
                : null;
            const liveSchemaSpec = (entry.coloringSource === COLORING_SOURCE.SCHEMA && entry.schemaSelection)
                ? {
                    spec: { mode: 'schema', schemaSelection: entry.schemaSelection },
                    coloringColumn: entry.filterColumn,
                    authority: HIGHLIGHT_COLOR_SOURCE.SCHEMA,
                }
                : null;
            const propertySpec = (entry.coloring && entry.coloring.column)
                ? {
                    spec: { mode: 'static', valueColors: entry.coloring.valueColors || {} },
                    coloringColumn: entry.coloring.column,
                    authority: HIGHLIGHT_COLOR_SOURCE.PROPERTY,
                }
                : null;

            const chosen = normalizeHighlightColorSource(entry.highlightColorSource);
            // An explicit choice is honoured whenever that source actually exists. If the user picked
            // a source the widget no longer carries (schema cleared, colouring column removed), fall
            // through to whatever remains rather than painting nothing — a missing colour definition
            // must never make the isolate vanish.
            if (chosen === HIGHLIGHT_COLOR_SOURCE.PROPERTY && propertySpec) return propertySpec;
            const schemaSpec = renderedSchemaSpec || liveSchemaSpec;
            if (chosen === HIGHLIGHT_COLOR_SOURCE.SCHEMA && schemaSpec) return schemaSpec;

            return schemaSpec || propertySpec || { spec: null, coloringColumn: null, authority: null };
        },
        /** A numeric schema authors the chart's category fills semantically even though the resolver
         *  still reads the physical group-by category. Keep those two names separate: only rendered
         *  schema colors earn the schema name, and an explicit property authority remains physical. */
        numericSchemaColoringDisplayName(entry, resolvedAuthority) {
            if (!entry || entry.coloringSource !== COLORING_SOURCE.SCHEMA) return null;
            if (resolvedAuthority === HIGHLIGHT_COLOR_SOURCE.PROPERTY) return null;
            const rendered = entry.renderedValueColors;
            if (!rendered || typeof rendered !== 'object' || !Object.keys(rendered).length) return null;
            const definition = resolveDefinition(entry.schemaSelection, this.activeTabColoringSchemas);
            if (!definition || definition.appliesTo !== APPLIES_TO.NUMBER) return null;
            const name = typeof definition.name === 'string' ? definition.name.trim() : '';
            return name || null;
        },
        /** Context the schema resolver needs, sourced exactly like the chart fill (dashboard-widget):
         *  the active tab's registry schemas + the vuetify light/dark theme. */
        isolateColorContext() {
            return {
                librarySchemas: this.activeTabColoringSchemas,
                theme: (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark) ? THEME.DARK : THEME.LIGHT,
            };
        },
        /** The colouring sources the user can pick from for the current selection: one per DISTINCT
         *  group-by DIMENSION of the selected charts (Severity, System, Line, ...), keyed by column.
         *  Colours come from a chart that colours BY that dimension (its schema / static spec); a
         *  dimension with no scheme (e.g. System) gets an auto categorical palette. */
        currentIsolationSources(entries) {
            const byCol = new Map();
            entries.forEach(entry => {
                const col = entry.filterColumn;
                if (!col) return;
                const { spec, coloringColumn, authority } = this.buildColoringSpec(entry);
                const realSpec = (spec && coloringColumn === col) ? spec : null;
                const coloringDisplayName = this.numericSchemaColoringDisplayName(
                    entry,
                    realSpec ? authority : null,
                );
                // The emitting chart's category domain (prepareChartData().labels) for this dimension.
                // An 'auto' source ranks its colours over it so the isolate hue matches the chart fill.
                const labelDomain = Array.isArray(entry.labelDomain) ? entry.labelDomain : [];
                const datumValues = this.selectionDatumValues(entry);
                // The values the user actually SELECTED on this dimension — the legend's value domain.
                // A selected value that resolves to no geometry still belongs in the legend (as a
                // zero-tag row), and only the selection knows it: the chart's label domain also holds
                // the categories the user did not pick.
                const values = this.selectionValueLabels(entry);
                const existing = byCol.get(col);
                if (!existing) {
                    const label = this.isolationSourceLabel(entry, col);
                    const spec0 = this.withRenderedFills(realSpec, entry) || this.renderedAutoSpec(entry);
                    byCol.set(col, {
                        key: col,
                        label,
                        coloringColumn: col,
                        coloringDisplayName,
                        spec: spec0,
                        labelDomain,
                        datumValues,
                        values,
                    });
                } else {
                    // A later chart on the same dimension may supply a real scheme (upgrade the auto
                    // entry) and/or a non-empty domain (keep the first non-empty one).
                    const adoptsNextSpec = Boolean(realSpec) && existing.spec.mode === 'auto';
                    const nextSpec = adoptsNextSpec ? realSpec : existing.spec;
                    const nextDisplayName = adoptsNextSpec
                        ? coloringDisplayName
                        : existing.coloringDisplayName;
                    const nextDomain = existing.labelDomain.length ? existing.labelDomain : labelDomain;
                    const nextDatumValues = Object.keys(existing.datumValues).length ? existing.datumValues : datumValues;
                    // Two charts on one dimension isolate the UNION of their selected values, so the
                    // legend's value domain is the union too — order-preserving, no duplicates.
                    const nextValues = [...existing.values, ...values.filter(v => !existing.values.includes(v))];
                    byCol.set(col, {
                        key: col,
                        label: existing.label,
                        coloringColumn: col,
                        coloringDisplayName: nextDisplayName,
                        spec: nextSpec,
                        labelDomain: nextDomain,
                        datumValues: nextDatumValues,
                        values: nextValues,
                    });
                }
            });
            return [...byCol.values()];
        },
        /** The chart's ON-SCREEN category->fill map turned into an 'auto' colouring spec: every value
         *  the chart actually painted keeps that exact fill in 3D (a teal slice stays teal), and any
         *  value it never drew falls through to the ranked auto palette unchanged. Reading the
         *  rendered option is the only truthful source — horizontal bars reverse their prepared rows
         *  and schema fills are applied while building the option, so re-deriving the hue can drift. */
        renderedAutoSpec(entry) {
            const rendered = entry && entry.renderedValueColors;
            const hasColors = rendered && typeof rendered === 'object' && Object.keys(rendered).length;
            return hasColors ? { mode: 'auto', valueColors: rendered } : { mode: 'auto' };
        },
        /** Lay the chart's ON-SCREEN fills over a stored value->colour map, so a value the chart
         *  actually painted cannot disagree with its own 3D element; values the chart never drew keep
         *  the stored colour. Deliberately NOT applied when the widget explicitly declared 'property'
         *  as its highlight source — that control exists to say "paint 3D from my map, not the chart"
         *  — and a live-schema spec carries no map to lay them over. */
        withRenderedFills(spec, entry) {
            if (!spec || spec.mode !== 'static') return spec;
            if (normalizeHighlightColorSource(entry && entry.highlightColorSource) === HIGHLIGHT_COLOR_SOURCE.PROPERTY) return spec;
            const rendered = entry && entry.renderedValueColors;
            if (!rendered || typeof rendered !== 'object' || !Object.keys(rendered).length) return spec;
            return { mode: 'static', valueColors: { ...(spec.valueColors || {}), ...rendered } };
        },
        /** The category -> charted number map for a selection, or {} when the chart shipped none. */
        selectionDatumValues(entry) {
            const values = entry && entry.datumValues;
            return (values && typeof values === 'object') ? values : {};
        },
        /** A selection's chosen values as legend labels — the same category-label convention the
         *  paint resolves each tag through, so a selected value and its painted row are one key. */
        selectionValueLabels(entry) {
            const values = (entry && Array.isArray(entry.values)) ? entry.values : [];
            return values.map(v => toCategoryLabel(v && v.display !== undefined ? v.display : (v && v.value)));
        },
        /** How the legend NAMES a colouring source: the emitting widget's authored display name,
         *  else the same camelCase -> Title Case the datagrid headers use. Never the raw identifier. */
        isolationSourceLabel(entry, col) {
            return (entry && entry.filterColumnLabel) || formatHeaderName(col);
        },
        /** The physical category's display label, retained for facets and as the legend fallback.
         *  Numeric schemas carry their semantic authority in `coloringDisplayName` instead. */
        isolationLegendTitle(source) {
            return source ? source.label : null;
        },
        /** The widget/chart name that OWNS a scene. Falls back to the coloured dimension only when a
         *  descriptor carries no title at all, so the legend always names something the user can see. */
        isolationOwnerTitle(entry, source) {
            return (entry && entry.widgetTitle)
                || (source && source.label)
                || 'Isolated selection';
        },
        /** Attach the number the CHART reported for each category, when the chart shipped one.
         *
         *  On the exact path this is a cross-check rather than a second story: the receipt's
         *  `records` come from the same exact row scope the chart aggregated, so the two agree by
         *  construction and a disagreement is worth seeing. On the whole-tag path the two genuinely
         *  differ (one tag can serve many rows), which is why the chart number is still shown.
         *  A category the chart never reported keeps its own number alone — never a guessed one. */
        withLegendDataCounts(entries, datumValues) {
            if (!datumValues || typeof datumValues !== 'object') return entries;
            return entries.map(entry => {
                const dataCount = datumValues[entry.value];
                return Number.isFinite(dataCount) ? { ...entry, dataCount } : entry;
            });
        },
        /** Add a row for every value the legend must list that resolved NOTHING — the user's selected
         *  values on a slice-click isolate, the chart's own categories on the widget-header one.
         *  Dropping them made the legend disagree with the chart it captions: a status with 4 records
         *  simply disappeared. Such a row keeps its real charted number and reports zero targets;
         *  `paintable: false` marks it as a row with nothing on screen, so the renderer mutes it and
         *  refinement clicks skip it. Only values the chart actually reported a number for are added
         *  — a row with no number to show would say nothing. */
        withLegendDomainEntries(entries, payload, resolveColor) {
            const domain = (payload && Array.isArray(payload.legendValues)) ? payload.legendValues : [];
            const datumValues = payload && payload.datumValues;
            if (!domain.length || !datumValues || typeof datumValues !== 'object') return entries;
            // `seen` starts as the published rows and grows as rows are added, so a value repeated in
            // the selection (two charts on one dimension) still yields exactly one legend row.
            const seen = new Set(entries.map(entry => entry.value));
            const missing = domain
                .filter(value => {
                    if (seen.has(value) || !Number.isFinite(datumValues[value])) return false;
                    seen.add(value);
                    return true;
                })
                .map(value => ({
                    key: value,
                    value,
                    label: value,
                    color: resolveColor ? resolveColor(value) : UNMAPPED_ISOLATE_COLOR,
                    state: 'target',
                    records: 0,
                    targets: 0,
                    geometryObjects: 0,
                    dataCount: datumValues[value],
                    paintable: false,
                }));
            return missing.length ? [...entries, ...missing] : entries;
        },
        /** The isolate request for the CURRENT selection, or null when nothing is isolatable (no
         *  selection, or a cross-source selection with no single projection table). Colouring is the
         *  user's sticky legend pick when still available, else the last-interacted chart's source;
         *  `sources` + `selectedKey` feed the legend selector (shown when >1 source). */
        currentIsolationPayload() {
            const entries = this.orderedSelectionEntries;
            if (!entries.length) return null;
            const tables = [...new Set(entries.map(e => normalizeTableName(e.tableName)))];
            if (tables.length !== 1) return null; // same-source only
            const tableName = entries[0].tableName;
            const sources = this.currentIsolationSources(entries);
            const driving = this.chartSelections[this.lastMutatedWidgetId] || entries[entries.length - 1];
            const drivingKey = driving ? driving.filterColumn : null;
            const selectedKey = (this.isolationSourceKey && sources.some(s => s.key === this.isolationSourceKey))
                ? this.isolationSourceKey
                : drivingKey;
            const selected = sources.find(s => s.key === selectedKey) || sources[0] || null;
            const ownerWidgetId = driving?.widgetId ?? this.lastMutatedWidgetId;
            // The table's session is EXTERNAL-ONLY, so it no longer carries these dimensions —
            // and it must not, or many rows sharing one Master Tag would widen the paint past the
            // grid. Every same-table dimension is replayed as an exact raw predicate instead
            // (the viewer is never self-excluding), which is also why this no longer waits for a
            // session before it can isolate.
            const scope = this.isolationScopeFor(tableName);
            const selectedEntry = (selected && entries.find(entry => entry.filterColumn === selected.key))
                || driving;
            // Saved/runtime widget filters stay the DRIVING widget's own scope. ANDing every
            // selected chart's saved filters onto one row would narrow the isolate by filters the
            // user never applied to this view (and can empty it outright).
            const filters = [
                ...(Array.isArray(selectedEntry?.filters) ? selectedEntry.filters : []),
                ...selectionFiltersForViewer(entries, tableName),
            ];
            if (!filters.length) return null;
            return {
                ownerWidgetId,
                tableName,
                filters,
                ...scope,
                coloringColumn: selected ? selected.coloringColumn : null,
                coloringDisplayName: selected ? selected.coloringDisplayName : null,
                coloringSpec: selected ? selected.spec : null,
                // The selected source's chart label domain — feeds the 'auto' rank in
                // openViewerWithCrossFilter so its colours match that chart's fill.
                labelDomain: selected ? selected.labelDomain : null,
                // The legend's two lines: the owning widget is primary, the coloured property
                // secondary ("Colored by <display name>").
                widgetTitle: this.isolationOwnerTitle(selectedEntry, selected),
                coloringLabel: this.isolationLegendTitle(selected),
                // The charted number per category for the selected source. The legend reports it
                // beside the painted-geometry count so the two are never silently different numbers.
                datumValues: selected ? selected.datumValues : null,
                // Every value the legend must list, whatever the paint resolves: here the user's
                // selected values on the colouring dimension (the widget-header isolate path passes
                // the chart's own categories). A value that resolved no geometry stays visible as a
                // zero-tag row instead of silently vanishing from the receipt.
                legendValues: selected ? selected.values : null,
                sources,
                selectedKey: selected ? selected.key : null,
            };
        },
        // Centralized 3D isolation reconcile — runs after EVERY chartSelections mutation and is
        // generation-guarded (the mutation bumped crossFilterGen; openViewerWithCrossFilter captures
        // it and bails if superseded). Isolates the current selection (single/multi value, single or
        // multi chart on one source); an empty / cross-source selection clears isolation. Auto-OPEN
        // is gated by the preference (a manually opened viewer is never yanked; the bar's manual
        // "Open in 3D" covers pref-OFF) — the pref gates ONLY opening, not the selection/session.
        reconcileViewerIsolation() {
            const payload = this.currentIsolationPayload();
            if (!payload) {
                this.isolationSourceKey = null; // fresh selection starts with the default source
                this.resetViewerIsolation();
                return;
            }
            if (this.isAutoOpenViewerEnabled()) {
                this.openViewerWithCrossFilter(payload);
            }
        },
        /** Legend colouring-source selector change (D1): remember the pick and re-isolate the current
         *  selection coloured by it. Always re-paints (explicit user action, pref-independent). */
        onLegendSourceChange(key) {
            this.isolationSourceKey = key;
            const payload = this.currentIsolationPayload();
            if (!payload) return;
            this.openViewerWithCrossFilter(payload);
        },
        /** Click on a legend value: FORCE the paint to that value alone; clicking the value that is
         *  already forced releases the refinement. A row with nothing to paint (a selected value that
         *  resolved no geometry) is inert — forcing it would isolate an empty model.
         *
         *  Deliberately NOT routed through setSelection/removeSelectionValue: those bump the filter
         *  scope, invalidate the isolation and schedule a session refresh, i.e. they change what the
         *  DASHBOARD is filtered to. A legend refinement only changes what the current paint shows,
         *  so the filter chips and every widget query stay exactly as they were. */
        onLegendRefineValue(value) {
            const baseline = this._legendPaint;
            if (!baseline || !baseline.refinable) return;
            const entry = baseline.entries.find(candidate => candidate.value === value);
            // A row the receipt proved renders NOTHING is inert: forcing it would clear the scene
            // and report `unrenderable`, which is a worse answer than leaving the paint alone.
            if (!entry || entry.paintable === false || entry.targets === 0) return;
            const current = this.legendRefinedValues;
            const forced = !!(current && current.length === 1 && current[0] === value);
            this.applyLegendRefinement(forced ? null : [value]);
        },
        /** Restore the published defaults: every value paints again with its original count. Touches
         *  the paint and the legend only — the dashboard's selection chips were never involved. */
        onLegendResetRefinement() {
            if (!this._legendPaint) return;
            this.applyLegendRefinement(null);
        },
        /**
         * Re-show the published scope NARROWED to `values` (null = the published default).
         *
         * A refinement is a smaller SCOPE, not a client-side re-paint. The old implementation
         * reassigned dbIds between colouring values under its own conflict rule ("the first selected
         * value in legend order claims it") — a second arbitration, on the bare dbId identity this
         * plan forbids. Adding one predicate on the colouring column and asking the composable again
         * keeps every decision in one place: the arbitrator still resolves shared geometry to MIXED,
         * and the scene stays exactly as truthful as the first render.
         *
         * The BASELINE rows are what the legend keeps showing (see `coloringLegendModel`), so a muted
         * row still reports the number it had when the whole selection painted.
         */
        applyLegendRefinement(values) {
            const baseline = this._legendPaint;
            if (!baseline || !baseline.refinable || !baseline.payload) return;
            const refined = Array.isArray(values) && values.length ? [...values] : null;
            this.legendRefinedValues = refined;
            const payload = refined
                ? {
                    ...baseline.payload,
                    filters: [
                        ...(baseline.payload.filters || []),
                        ...buildSelectionFilters([{
                            filterColumn: baseline.payload.coloringColumn,
                            values: refined,
                        }]),
                    ],
                }
                : baseline.payload;
            this.openViewerWithCrossFilter({ ...payload, legendRefinement: refined });
        },
        /** The stored legend for the published receipt under `refinedValues` (null = the defaults).
         *
         *  Every published row keeps its BASELINE number — the one it has when the whole selection
         *  renders. `selected` alone says what is on screen right now and the renderer mutes the
         *  rest; overwriting a hidden row's number with 0 destroyed the one thing it was still there
         *  to say ("Blocked 1" became "Blocked 0" and read as data loss, not as a filter). */
        coloringLegendModel(refinedValues) {
            const baseline = this._legendPaint;
            if (!baseline) return null;
            const refined = Array.isArray(refinedValues) && refinedValues.length ? refinedValues : null;
            return {
                ...baseline.legend,
                entries: baseline.entries.map(entry => ({
                    ...entry,
                    selected: entry.paintable !== false && (!refined || refined.includes(entry.value)),
                })),
                sources: baseline.sources,
                selectedKey: baseline.selectedKey,
                facets: baseline.facets,
                refinable: baseline.refinable,
                refined: !!refined,
            };
        },
        /**
         * Publish the render RECEIPT as the legend, decorated with the interaction affordances the
         * dashboard owns.
         *
         * The split is deliberate and load-bearing. The receipt is the source of every ENTRY, COUNT
         * and STATE — it is what the viewer actually drew, and the composable already committed it
         * so nothing renders blank if this method never runs. The dashboard adds only what the
         * receipt cannot know: which colouring SOURCES the user may switch between, which values the
         * chart listed that resolved nothing, the charted number per category, and the `selected`
         * flags a refinement toggles. No presentation concept travels back into the composable.
         */
        publishReceiptLegend(outcome, payload) {
            const receipt = outcome.legend;
            if (!receipt) {
                // Nothing on screen carries a colour — the composable already published null.
                this.clearLegendRefinement();
                return;
            }
            const resolveColor = this.focusEncoding(payload).colorFor;
            const entries = this.withLegendDomainEntries(
                this.withLegendDataCounts(receipt.entries, payload.datumValues),
                payload,
                resolveColor,
            );
            const refinement = Array.isArray(payload.legendRefinement) && payload.legendRefinement.length
                ? payload.legendRefinement
                : null;
            // A refinement re-renders a NARROWER scope, so its receipt describes fewer rows. The
            // published baseline is what the legend keeps quoting; only `selected` changes.
            if (!refinement || !this._legendPaint) {
                this._legendPaint = {
                    legend: receipt,
                    entries,
                    sources: payload.sources || [],
                    selectedKey: payload.selectedKey || null,
                    facets: payload.coloringColumn
                        ? [{
                            kind: LEGEND_FACET_COLORED,
                            column: payload.coloringColumn,
                            label: payload.coloringLabel || payload.coloringColumn,
                            values: entries.map(entry => entry.value),
                        }]
                        : [],
                    // Refining re-resolves the same scope with one more predicate, so it is offered
                    // whenever there is a colouring column to predicate on. A truncated receipt is
                    // excluded: its rows are the first N, and narrowing them would claim a
                    // completeness the server already said it could not give.
                    refinable: !!payload.coloringColumn && !receipt.truncated,
                    payload: { ...payload, legendRefinement: undefined },
                };
            } else {
                this._legendPaint = { ...this._legendPaint, legend: receipt };
            }
            this.legendRefinedValues = refinement;
            this.$store.commit('SET_COLORING_LEGEND', this.coloringLegendModel(refinement));
        },
        /** Drop the published receipt — every path that clears the legend also clears what a
         *  refinement would re-show, so a stale baseline can never outlive the scene it describes. */
        clearLegendRefinement() {
            this._legendPaint = null;
            this.legendRefinedValues = null;
        },
        /** Per-chart "Isolate in 3D" (the chart analogue of the datagrid Isolate All): isolate ALL of
         *  the chart's tags — its scope filters only, no slice value — coloured by the chart's own
         *  colouring (live schema when schema-driven, else the static map). Always opens the viewer:
         *  the button IS the explicit open intent (independent of the auto-open preference). */
        onIsolateWidget(descriptor) {
            if (!descriptor || !descriptor.tableName) return;
            const configuredColoring = this.buildColoringSpec(descriptor);
            const presentation = descriptor.presentation ? { ...descriptor.presentation } : null;
            // A chart without an explicit schema/property map still has its stable category palette.
            // Keep the 3D action truthful: resolve the group-by value per tag and apply that same
            // automatic palette instead of silently degrading the "coloured" action to plain isolate.
            // The palette comes from the chart's RENDERED fills, exactly as a slice click does, so
            // this button and a datum click paint one widget the same colour rather than two.
            //
            // The widget's OWN aggregation dimension is the colouring column. A configured map is
            // adopted only when it actually colours that dimension — the identical guard the slice
            // path applies (currentIsolationSources: `spec && coloringColumn === col`). Without it a
            // widget carrying a legacy Link-3D map on an unrelated column (e.g. "Colour models by
            // MasterTagName" on a chart grouped by CmlType) isolated coloured by THAT column: the
            // legend headed "Master Tag Name", listed one row per tag, and lost the chart's dual
            // counts and zero-tag rows — while clicking a slice of the same chart stayed correct.
            // The two entry points must not disagree about what a chart is coloured by.
            // Fall back to the configured column only for a widget that has NO aggregation dimension
            // at all, so a non-grouped widget keeps painting exactly as it does today.
            const coloringColumn = presentation
                ? null
                : (descriptor.filterColumn || configuredColoring.coloringColumn || null);
            // Adopt the configured map only when it colours the column we are actually colouring by.
            // Compared against the RESOLVED column, not the chart's: an ungrouped widget colours by
            // its configured column, and there the stored value->colour map is still the right answer.
            const configuredSpec = (!presentation
                && configuredColoring.spec
                && configuredColoring.coloringColumn === coloringColumn)
                ? configuredColoring.spec
                : null;
            // The SAME overlay a slice click applies: the chart's on-screen fills win over a stored
            // value->colour map (unless the widget declared 'property' as its highlight source), so
            // this button and a slice click paint one widget the same colours instead of two. Only
            // the fallback was rendered-aware before, which left every widget carrying a legacy
            // Link-3D map painting the ranked palette rather than its own fills.
            const spec = presentation
                ? null
                : (this.withRenderedFills(configuredSpec, descriptor)
                    || (coloringColumn ? this.renderedAutoSpec(descriptor) : null));
            const coloringDisplayName = presentation
                ? null
                : this.numericSchemaColoringDisplayName(
                    descriptor,
                    configuredSpec ? configuredColoring.authority : null,
                );
            // Self-excluding, exactly as before: this widget's own dimension must not filter its own
            // isolate, but every OTHER same-table selection still scopes it. Those dimensions used to
            // ride in the widget's session; now they are exact raw predicates and the session carries
            // only the other TABLES.
            const filters = [
                ...(Array.isArray(descriptor.filters) ? descriptor.filters : []),
                ...selectionFiltersForWidget(this.orderedSelectionEntries, descriptor.widgetId, descriptor.tableName),
            ];
            const scope = this.isolationScopeFor(descriptor.tableName);
            const labelDomain = Array.isArray(descriptor.labelDomain) ? descriptor.labelDomain : [];
            // The chart's numbers and labels describe its GROUP-BY dimension. A widget coloured by
            // some other column (an inline property map) must not borrow them — the dual count would
            // quote another dimension's number — so that legend keeps painted counts alone.
            const chartDimension = !!coloringColumn && coloringColumn === descriptor.filterColumn;
            const source = coloringColumn
                ? {
                    label: chartDimension
                        ? this.isolationSourceLabel(descriptor, coloringColumn)
                        : formatHeaderName(coloringColumn),
                  }
                : null;
            this.openViewerWithCrossFilter({
                ownerWidgetId: descriptor.widgetId,
                tableName: descriptor.tableName,
                filters,
                ...scope,
                coloringColumn,
                coloringDisplayName,
                coloringSpec: spec,
                presentation,
                labelDomain,
                // Same legend enrichment as a chart selection: the widget's own title as the legend
                // owner, the coloured property as the subtitle, the chart's number per category for
                // the cross-check, and every charted value listed — including the ones that resolve
                // no geometry, which are muted rather than dropped.
                widgetTitle: this.isolationOwnerTitle(descriptor, source),
                coloringLabel: presentation?.displayName || this.isolationLegendTitle(source),
                datumValues: chartDimension ? this.selectionDatumValues(descriptor) : null,
                legendValues: chartDimension ? labelDomain : null,
                seedColumn: coloringColumn
                    || descriptor.filterColumn
                    || descriptor.tagLink?.column
                    || descriptor.tagColumn,
            });
        },
        /**
         * The one entry point for every chart-driven viewer scene, and the one place that decides
         * whether a source can state an exact scene. The decision is taken here, before any
         * resolver request goes out. A dashboard gesture has one render authority only:
         * `useSpatialFocus`.
         *
         *   capable     -> `useSpatialFocus.show()`. Exact occurrences, gray context, MIXED
         *                  conflicts, markers, pulse and a render-receipt legend, all owned by the
         *                  composable. This is the ONLY path that paints a point of interest.
         *   not capable -> clear the previous spatial scene and explain that the source must be
         *                  republished with target/context roles. It must never be reinterpreted as
         *                  a whole-tag request because that would reopen the stale-scene defect.
         */
        async openViewerWithCrossFilter(payload) {
            log('CustomDashboard', 'openViewerWithCrossFilter', payload);
            this.spatialSelectionNotice = '';
            const claim = payload.isolationClaim && this.isIsolationClaimCurrent(payload.isolationClaim)
                ? { ...payload.isolationClaim }
                : this.beginIsolationClaim(payload.ownerWidgetId ?? this.lastMutatedWidgetId ?? '__dashboard__');
            // A legend refinement changes the visible subset, not the baseline category totals.
            const legendPaint = payload.legendRefinement ? this._legendPaint : null;
            const legendRefinedValues = payload.legendRefinement ? this.legendRefinedValues : null;
            this.resetViewerIsolation(claim, 'selectionChanged');
            if (legendPaint) {
                this._legendPaint = legendPaint;
                this.legendRefinedValues = legendRefinedValues;
            }

            // A server-created zero-tag session is a proven empty result, not an expired session.
            // Fail closed before resolving so the endpoint can never widen it to unfiltered rows.
            if (payload.sessionEmptyMatch) {
                this.resetViewerIsolation(claim);
                this.spatialSelectionNotice = 'No 3D locations match the current filters. Adjust the filters to choose another selection.';
                return;
            }

            const spatial = await this.ensureSpatialCapability(payload.tableName);
            if (!this.isIsolationClaimCurrent(claim)) return;
            if (!hasExactSpatialCapability(spatial)) {
                this.refuseSpatialFocus(
                    'spatialCapabilityMissing',
                    '3D locations are not available for this data source. You can still view its records and values.',
                    claim,
                );
                return;
            }
            const widgetFocus = this.widgetSpatialFocus(payload.ownerWidgetId, spatial);
            const policy = resolveSpatialFocusSessionPolicy(
                spatial,
                widgetFocus,
                this.masterTagFallbackSessionOverride,
            );
            if (policy.error) {
                this.refuseSpatialFocus(policy.error.code, policy.error.message, claim);
                return;
            }
            this.useMasterTagAsPoiFallback = spatialFocusFallbackEnabled(
                spatial,
                widgetFocus,
                this.masterTagFallbackSessionOverride,
            );
            if (payload.globalFilters) {
                this.refuseSpatialFocus(
                    'spatialGlobalFiltersUnsupported',
                    'This filtered selection cannot be shown exactly in 3D until its dashboard filter session is available. Refresh the dashboard and try again.',
                    claim,
                );
                return;
            }
            await this.showExactSpatialFocus(payload, spatial, policy, claim);
        },
        widgetSpatialFocus(widgetId, spatial) {
            const widget = this.layout.find(item => String(item?.id) === String(widgetId));
            const config = parseWidgetConfig(widget?.sqlViewConfig);
            return config?.spatialFocus || legacySpatialFocusOverride(spatial, config);
        },
        /**
         * Set the viewer's CML fallback policy and replay the current exact focus once.
         *
         * The replay goes through the same server resolver and composable as the original click; it
         * never repaints cached dbIds or widens to the legacy whole-tag path. With no exact focus on
         * screen, the option simply applies to the next one.
         */
        async setMasterTagPoiFallback(enabled) {
            if (this.masterTagFallbackBusy) return;

            const next = enabled !== false;
            if (next === this.useMasterTagAsPoiFallback) return;

            this.useMasterTagAsPoiFallback = next;
            this.masterTagFallbackSessionOverride = next;
            const payload = this._lastExactFocusPayload;
            if (!this.viewerOpened) return;

            this.masterTagFallbackBusy = true;
            try {
                if (payload) {
                    await this.openViewerWithCrossFilter({
                        ...payload,
                        filters: Array.isArray(payload.filters)
                            ? payload.filters.map(filter => ({ ...filter }))
                            : [],
                    });
                } else {
                    await this.replayActiveGridSpatialFocus(next);
                }
            } finally {
                this.masterTagFallbackBusy = false;
            }
        },
        /**
         * The catalogue's spatial capability for a source table, read once per table per session.
         *
         * One existing GET (`apiService.tables()`); the entry already carries the server-owned
         * `spatial` descriptor.
         *
         * ONLY a capability is memoised. A null answer forgets itself, for two different reasons
         * that both end the same way: a failed read is transient, and a source that is not capable
         * YET becomes capable the moment it is republished with a spatial contract. Caching that
         * null for the life of the board would keep refusing the source after it was repaired. The
         * cost of re-reading is one GET per isolate gesture on a source that stays non-capable.
         * @param {string} tableName
         * @returns {Promise<?object>} `TableViewInfo.spatial`
         */
        ensureSpatialCapability(tableName) {
            const key = String(tableName || '').toLowerCase();
            if (!key) return Promise.resolve(null);
            if (!this._spatialByTable.has(key)) {
                this._spatialByTable.set(key, (async () => {
                    try {
                        const viewsRes = await apiService.tables();
                        const views = Array.isArray(viewsRes?.data) ? viewsRes.data : [];
                        const match = views.find(v => String(v?.name).toLowerCase() === key);
                        const spatial = match?.spatial || null;
                        if (!spatial) {
                            log('CustomDashboard', 'catalogue has no spatial capability — refusing spatial focus', {
                                tableName,
                                known: views.length,
                            });
                            this._spatialByTable.delete(key); // a republish can grant one mid-session
                        }
                        return spatial;
                    } catch (err) {
                        error('CustomDashboard', 'spatial capability lookup failed — exact focus off', err);
                        this._spatialByTable.delete(key); // let a later isolate retry
                        return null;
                    }
                })());
            }
            return this._spatialByTable.get(key);
        },
        /**
         * The viewer a scene will be painted into. `viewerOpened` was set synchronously above, so
         * the section may not be rendered yet on the first open — hence the tick before reaching for
         * the ref. Returns null when the claim went stale while the viewer was loading, so a
         * superseded selection can never paint.
         */
        async ensureFocusViewer(claim) {
            await this.$nextTick();
            if (!this.isIsolationClaimCurrent(claim)) return null;
            // Expand moves the shared viewer out of this panel. Reattach it even when
            // the model is already loaded, just as the grid-selection path does.
            await this.$refs.viewersSection?.load3dViewer();
            if (!this.isIsolationClaimCurrent(claim)) return null;
            return this.$store.state.viewer || null;
        },
        /**
         * Hand one exact scope to the composable and render whatever it reports back.
         *
         * The dashboard supplies INTENT only: who owns the scene, which rows it covers, and how a
         * value becomes a colour. It supplies no dbIds, no gray context, no target precedence, no
         * pulse set and no legend counts — those are the composable's, and re-deriving any of them
         * here would be the second authority this plan removes.
         */
        async showExactSpatialFocus(payload, spatial, policy, claim) {
            const filters = Array.isArray(payload.filters) ? payload.filters : [];
            const coloringColumn = payload.coloringColumn || null;
            const scope = buildSpatialQueryScope({
                tableName: payload.tableName,
                // Every column this scope references. `seedColumn` covers the ungrouped
                // "isolate all of this widget's rows" case, which carries no filter at all.
                columns: [...filters.map(f => f.column), coloringColumn, payload.seedColumn],
                filters,
                filterSessionId: payload.filterSessionId,
                expectedSourceRevision: spatial.sourceRevision,
                encodingColumn: coloringColumn,
                // A chart's Top/Bottom windows GROUPS, not source rows, so a chart focus is always
                // all-matching; the exactness rides on the rendered-label predicate instead.
                rowWindow: payload.rowWindow || allMatchingRowWindow(),
            });
            if (!scope) {
                // A capable source whose scope will not state itself is a contract failure. Clear
                // the previous exact scene through its owner and report it; never widen the same
                // gesture through the retired whole-tag resolver.
                warn('CustomDashboard', 'exact query focus unavailable — refusing spatial focus', {
                    tableName: payload.tableName,
                    expectedSourceRevision: spatial?.sourceRevision ?? null,
                });
                this.refuseSpatialFocus(
                    'spatialScopeUnavailable',
                    'This selection cannot be shown in 3D because its exact spatial scope could not be stated.',
                    claim,
                );
                return;
            }

            const envelope = buildSpatialResolveRequest({
                scope,
                focus: policy.focus,
                ...(policy.legacy
                    ? { useMasterTagAsPoiFallback: this.useMasterTagAsPoiFallback }
                    : {}),
            });
            if (envelope.error) {
                this.refuseSpatialFocus(envelope.error.code, envelope.error.message, claim);
                return;
            }

            let resolvedResult;
            try {
                const response = await apiService.resolveSpatialFocus(envelope.request);
                if (!this.isIsolationClaimCurrent(claim)) return;
                resolvedResult = response.data;
            } catch (err) {
                if (!this.isIsolationClaimCurrent(claim)) return;
                this.spatialSelectionNotice = 'The available 3D locations could not be checked. Please try again.';
                return;
            }
            if (!hasDisplayTarget(resolvedResult)) {
                this.spatialSelectionNotice = 'No individual record locations could be shown for this selection. Its values remain included in the dashboard.';
                this.resetViewerIsolation(claim);
                return;
            }
            this.viewerOpened = true;
            this.viewerTab = '3D View';
            const viewer = await this.ensureFocusViewer(claim);
            if (!viewer || !this.isIsolationClaimCurrent(claim)) return;
            this.clearCrossFilterTheming();

            this._lastExactFocusPayload = {
                ...payload,
                filters: filters.map(filter => ({ ...filter })),
            };

            const outcome = await this.focus.show({
                owner: this.focusOwner(payload),
                request: envelope.request,
                resolvedResult,
                scope,
                encoding: this.focusEncoding(payload),
                presentation: payload.presentation || null,
                viewer,
            });
            if (!this.isIsolationClaimCurrent(claim)) return;
            this.applyFocusOutcome(outcome, payload);
        },
        /** The legend's PRIMARY line is the widget that owns the scene, never the property. */
        focusOwner(payload) {
            return {
                widgetId: payload.ownerWidgetId ?? this.lastMutatedWidgetId ?? null,
                title: payload.widgetTitle || payload.coloringLabel || 'Isolated selection',
            };
        },
        /**
         * The widget's live value -> colour definition. `colorFor` is the SAME resolver the chart
         * fill uses, so a teal slice stays teal in 3D. With no colouring spec there is no resolver
         * and the targets render as a plain, uncoloured isolate.
         */
        focusEncoding(payload) {
            const column = payload.coloringColumn || null;
            const displayName = payload.coloringDisplayName || payload.coloringLabel || column;
            if (!column || !payload.coloringSpec) return { column, displayName };
            const autoValues = Array.isArray(payload.labelDomain) ? payload.labelDomain : [];
            const colorFor = makeIsolateColorResolver(payload.coloringSpec, {
                ...this.isolateColorContext(),
                autoValues,
            });
            return {
                column,
                displayName,
                // The arbitrator hands raw stored values; the chart's palette is keyed by the
                // normalized category label, which is the same key the legend rows carry.
                colorFor: value => colorFor(toCategoryLabel(value)),
            };
        },
        /**
         * Render the typed outcome. `empty` and `unrenderable` are DIFFERENT failures and are told
         * apart by the status the composable reported, never re-derived from the counts:
         *   empty        — the scope matched no rows at all;
         *   unrenderable — it matched rows and then lost every one of them at the viewer (a model
         *                  mismatch, an unplaceable point, a context with no visible target).
         * Both clear the previous scene; only the second has a row count worth quoting.
         */
        applyFocusOutcome(outcome, payload) {
            const status = outcome && outcome.status;
            // A newer caller already owns the scene — leaving it alone is the correct answer.
            if (status === 'aborted' || status === 'superseded') return;

            if (status === 'rendered') {
                this.publishReceiptLegend(outcome, payload);
                return;
            }

            this.clearLegendRefinement();
            const readerMessage = focusOutcomeMessage(outcome);
            if (readerMessage) {
                this.$store.commit('SET_COLORING_LEGEND', {
                    owner: outcome.owner || null,
                    title: '3D view', notice: readerMessage, entries: [],
                });
                return;
            }
        },
        /** Drop any 3D theming left by a previous coloured cross-filter isolate (mirrors the grid's
         *  clearStaleTheming) so a plain isolate / cleared selection shows the model's true colours. */
        clearCrossFilterTheming() {
            const viewer = this.$store.state.viewer;
            const themed = this.$store.getters['GET_THEMED_DB_IDS'];
            if (themed?.length && viewer) clearThemingColors(viewer);
        },
        /**
         * Refuse one dashboard spatial gesture without leaving the previous gesture's markers,
         * theming or isolation behind. The shared focus adapter is the authoritative ledger, so
         * the reset must go through it before the warning is shown.
         */
        refuseSpatialFocus(reason, message, claim) {
            if (claim && !this.isIsolationClaimCurrent(claim)) return true;
            this.resetViewerIsolation(claim, reason);
            warn('CustomDashboard', '3D selection unavailable', { reason, message });
            this.spatialSelectionNotice = spatialRefusalMessage(reason);
            return true;
        },
        // Show the full model again — used when a cross-filter is cleared or when a new
        // cross-filter has no 3D matches / fails to resolve (a stale isolation must not
        // outlive the filter that created it).
        resetViewerIsolation(claim, reason = 'selectionCleared') {
            if (claim && !this.isIsolationClaimCurrent(claim)) return;
            this._lastExactFocusPayload = null;
            const viewer = this.$store.state.viewer;
            const forgeService = this.$store.state.forgeService;
            if (viewer) {
                // Drop the composable's own scene first. It owns its theming, markers and pulse, so
                // the legacy clear below cannot reach them — and a clear that names no owner is
                // unconditional by design (the board, not one widget, is being reset).
                this.focus.clear({ viewer, reason });
            }
            if (viewer && forgeService) {
                this.clearCrossFilterTheming();
                if (claim && !this.isIsolationClaimCurrent(claim)) return;
                forgeService.getMainExtension().isolateDbIds([], viewer.model);
            }
            // A cleared isolate must not leave a coloured-isolation legend behind.
            this.clearLegendRefinement();
            this.$store.commit('SET_COLORING_LEGEND', null);
        },
        isolateSelectedWidgetTag(ownerWidgetId, claim) {
            if (claim && !this.isIsolationClaimCurrent(claim)) return;
            // Reset the coloured-isolation legend at the shared entry point so every new 3D
            // isolation starts clean. A coloured widget isolate re-publishes it afterwards
            // (via its async resolve → publishColoringLegend); a plain one leaves it cleared.
            this.clearLegendRefinement();
            this.$store.commit('SET_COLORING_LEGEND', null);
            const items = this.$refs.dashboardItems;
            if (!items) return;
            const arr = Array.isArray(items) ? items : [items];
            const owner = arr.find(item => String(item?.message?.id) === String(ownerWidgetId));
            owner?.isolateSelectedTag?.(
                claim?.operation || null,
                this.masterTagFallbackSessionOverride,
                (enabled) => {
                    if (!claim || this.isIsolationClaimCurrent(claim)) {
                        this.useMasterTagAsPoiFallback = enabled;
                    }
                },
            );
        },
        replayActiveGridSpatialFocus(spatialFocusSessionOverride) {
            const items = this.$refs.dashboardItems;
            if (!items) return false;
            const arr = Array.isArray(items) ? items : [items];
            const ownerId = this.activeIsolation?.ownerWidgetId;
            const owner = arr.find(item => String(item?.message?.id) === String(ownerId));
            return owner?.replaySpatialFocusForFallback?.(spatialFocusSessionOverride) || false;
        },
        cancelPendingWidgetIsolation(ownerWidgetId, operation = null) {
            const items = this.$refs.dashboardItems;
            if (!items) return;
            const arr = Array.isArray(items) ? items : [items];
            const owner = arr.find(item => String(item?.message?.id) === String(ownerWidgetId));
            owner?.cancelPendingIsolation?.(operation);
        },
        closeViewer() {
            this.invalidateIsolation();
            const viewer = this.$store.state.viewer;
            // The viewer going away is not a widget opinion, so this clear is unconditional.
            if (viewer) this.focus.clear({ viewer, reason: 'viewerClosed' });
            this.viewerOpened = false;
            this.$store.commit('SET_VIEWER_TYPE', '2d');
            // Drop any active coloured-isolation legend so it can't linger on reopen.
            this.clearLegendRefinement();
            this.$store.commit('SET_COLORING_LEGEND', null);
        },
        selectPageByRoute() {
            this.selectTabByRouteQuery();
        },
        // ── builder-v1 deep-linkable tabs (spec §7/§10, plan step 17) ──────────────────
        /**
         * Deep-link resolution on load. The active tab's page id lives in ?tab=. A matching
         * id selects it (refresh keeps place); an unknown/deleted id (or none) falls back to
         * the default/first tab and — only when an id was actually asked for — surfaces a
         * non-blocking notice. The URL is normalised to the resolved tab so refresh is stable.
         */
        selectTabByRouteQuery() {
            const requestedId = this.$route.query[TAB_QUERY_KEY];
            const matched = resolveTabIndexFromQuery(this.pages, this.$route.query);
            if (matched > -1) {
                this.applyRouteTab(matched);
                log('CustomDashboard', 'deep-link tab resolved', { id: requestedId, index: matched });
                return;
            }
            const fallback = this.preferVisibleTab(fallbackTabIndex(this.pages));
            if (fallback > -1) {
                this.applyRouteTab(fallback);
                this.replaceTabQuery(this.pages[fallback] && this.pages[fallback].id);
            }
            if (requestedId != null && requestedId !== '') {
                log('CustomDashboard', 'deep-link tab missing — fell back to default/first', { requestedId, fallback });
                this.$dialog.notify.info('That dashboard is no longer available — showing the default instead.', {
                    position: 'top-right',
                    timeout: 4000,
                });
            }
        },
        /**
         * Set the active tab from a route-driven source (deep-link / back-forward / deletion)
         * WITHOUT echoing a new history push — syncTabToRoute is suppressed while the guard is
         * up, and the URL is either already correct (popstate) or normalised by the caller.
         */
        applyRouteTab(idx) {
            if (idx < 0 || idx === this.tab) return;
            this._applyingRouteTab = true;
            this.tab = idx;
            this.$nextTick(() => { this._applyingRouteTab = false; });
        },
        /**
         * Normalise the URL bar to a tab id WITHOUT a router navigation (no history entry) —
         * used by the deep-link fallback + deletion. A `$router.replace` here would hit
         * `router.beforeEach → httpService.cancelOnNavigation()`, which aborts the in-flight
         * mount load burst (forge/tags/filter-session) and surfaces spurious "Error receiving a
         * response!" toasts — this only bit the fallback path, which is the sole load-time
         * normalise. `history.replaceState` rewrites the bar in place; `window.history.state`
         * is preserved so vue-router's navigation key stays intact. The in-memory `$route.query`
         * is briefly stale but self-heals on the next real navigation (all post-load, safe).
         */
        replaceTabQuery(id) {
            if (id == null) return;
            if (String(this.$route.query[TAB_QUERY_KEY]) === String(id)) return;
            try {
                const href = this.$router.resolve({
                    name: 'custom-dashboard',
                    params: { id: this.facilityId },
                    query: { [TAB_QUERY_KEY]: String(id) },
                }).href;
                window.history.replaceState(window.history.state, '', href);
                log('CustomDashboard', 'tab URL normalised (in place)', { id });
            } catch (err) {
                warn('CustomDashboard', 'tab URL normalise failed', err);
            }
        },
        /**
         * Push the active tab's id into the route — one history entry per user tab switch, so
         * browser back/forward walks the tab history. Skipped when the change came FROM the
         * route (guard) or the URL is already in sync (the real echo-loop guard).
         */
        syncTabToRoute() {
            if (this._applyingRouteTab) return;
            const id = this.pages[this.tab] && this.pages[this.tab].id;
            if (id == null) return;
            if (String(this.$route.query[TAB_QUERY_KEY]) === String(id)) return;
            this.$router.push({
                name: 'custom-dashboard',
                params: { id: this.facilityId },
                query: { [TAB_QUERY_KEY]: String(id) },
            }).catch(() => {});
            log('CustomDashboard', 'tab → route', { index: this.tab, id });
        },
        /**
         * Back/forward (and any in-route ?tab= change) handler (facilitator ruling #1). The
         * tab id is read from the committed query; an unknown id falls back to default/first.
         * While editing, the switch routes through the exit coordinator (never bypassing a
         * pending drain) — a clean drain applies the tab and drops to View, a blocked drain
         * holds it as an exit intent that Retry/Discard fulfils. In View it applies directly.
         */
        onRouteTabChange() {
            const matched = resolveTabIndexFromQuery(this.pages, this.$route.query);
            const target = matched > -1 ? matched : this.preferVisibleTab(fallbackTabIndex(this.pages));
            if (target < 0 || target === this.tab) return;
            const applyTab = () => this.applyRouteTab(target);
            if (this.editMode) {
                log('CustomDashboard', 'route tab change during edit — routing through coordinator', { target });
                this.exitCoordinator.requestExit(applyTab);
                return;
            }
            applyTab();
        },
        /**
         * builder-v1 delete-a-tab flow (spec §7, plan step 17 deletion branch). Deletion is
         * immediate and stays OUTSIDE the failed-save block. Deleting the ACTIVE tab runs the
         * deletion-specific coordinator branch first: freeze → DROP the deleted (captured)
         * page's buffered + pending diffs → settle any already-issued write WITHOUT its
         * failure blocking the delete → NO final-diff enqueue. Then delete, remove the tab,
         * and focus the left-adjacent tab (fallback default/first) in View. A NON-active tab
         * delete leaves the active tab's edit session + pending drag untouched — only its
         * index shifts.
         */
        async performTabDeletion(id) {
            const deletedIndex = this.pages.findIndex(p => p.id == id);
            const isActiveTab = this.page && this.page.id === id;
            if (isActiveTab && this.editMode) {
                log('CustomDashboard', 'delete active tab — deletion branch (drop diffs, settle non-blocking)', { id });
                await this.exitCoordinator.prepareForDeletion();
            }
            try {
                await dashboardPagesService.del(id);
            } catch (err) {
                error('CustomDashboard', 'delete dashboard failed', err);
                this.$dialog.notify.error('Couldn’t delete the dashboard. Please try again.', {
                    position: 'top-right',
                    timeout: 5000,
                });
                return;
            }
            if (deletedIndex > -1) this.pages.splice(deletedIndex, 1);
            if (!this.pages.length) {
                this.noData = true;
                if (this.editMode) this.setEditMode(false);
                return;
            }
            if (isActiveTab) {
                // Deleting the active tab ends its edit session; land on the left-adjacent tab
                // in View (matches the reload-lands-in-view precedent — avoids re-capturing the
                // save baseline against a tab whose widgets have not loaded yet).
                if (this.editMode) this.setEditMode(false);
                const next = this.preferVisibleTab(tabIndexAfterDeletion(this.pages, deletedIndex));
                this.applyRouteTab(next);
                this.replaceTabQuery(this.pages[next] && this.pages[next].id);
                log('CustomDashboard', 'active tab deleted — focused left-adjacent', { deletedIndex, next });
            } else if (deletedIndex < this.tab) {
                // A tab left of the active one was removed → the active page's index shifts left
                // by one; keep it selected without a route echo or a widget reload.
                this.applyRouteTab(this.tab - 1);
            }
        },
        /** Redirect wheel/trackpad intent into the tab strip only while tabs overflow. */
        onTabStripWheel(event) {
            if (scrollTabStripFromWheel(this.$refs.tabBar, event)) event.preventDefault();
        },
        /** Tab-key navigation must reveal the whole focused tab without moving the page. */
        onTabStripFocus(event) {
            const tab = event.target && event.target.closest
                ? event.target.closest('#custom-dashboard-tabs > .v-btn')
                : null;
            if (tab) revealTabInStrip(this.$refs.tabBar, tab);
        },
        /** Deep links and programmatic tab changes must reveal the selected tab too, not only
         *  keyboard focus. Resolve the Vuetify component ref after the tab DOM has updated. */
        revealActiveTab() {
            // Looked up by its `pages` index, not by v-for ref order: hidden dashboards leave
            // gaps, so the n-th rendered button is not necessarily page n.
            const strip = this.$refs.tabBar;
            const tab = strip && strip.querySelector(`#custom-dashboard-tabs > .v-btn[data-page-index="${this.tab}"]`);
            if (tab) revealTabInStrip(strip, tab);
        },
        // ── Show / hide dashboards (browser-local, utils/hiddenDashboards.js) ─────────────
        /** Fallback landings (no / unknown ?tab=, deletion) prefer a visible tab. A matched
         *  deep link never goes through here, so a link to a hidden dashboard still opens it. */
        preferVisibleTab(index) {
            return preferVisibleTabIndex(this.pages, this.activeHiddenDashboardIds, index);
        },
        openTabVisibilityDialog() {
            this.settingsMenuOpen = false;
            this.tabVisibilityDialogOpen = true;
        },
        /**
         * One dialog switch: write the new hidden list through to localStorage (a failed write
         * still applies for this session) and, when the ACTIVE tab was just hidden, move to the
         * first visible tab via onTabClick so edit mode still drains through the coordinator.
         */
        onTabVisibilityChange(page, visible) {
            // Built on the effective list, so stale ids are dropped from storage on the next write.
            const next = withDashboardVisibility(this.activeHiddenDashboardIds, page.id, !!visible);
            // The last visible switch is disabled in the dialog; this guards the state too.
            if (!this.pages.some(p => this.isRealPageId(p.id) && !next.includes(String(p.id)))) return;
            const remaining = visiblePageIndices(this.pages, next);
            this.hiddenDashboardIds = next;
            if (!writeHiddenDashboardIds(this.facilityId, next)) {
                warn('CustomDashboard', 'hidden dashboards not saved (storage unavailable)', { facilityId: this.facilityId });
                this.$dialog.notify.info('This browser can’t save the choice; it applies until you reload.', {
                    position: 'top-right',
                    timeout: 4000,
                });
            }
            if (!visible && this.page && String(this.page.id) === String(page.id) && remaining.length) {
                this.onTabClick(remaining[0]);
            }
        },
        updateWidget(widget){
            return apiService.update(this.toStorageWidgetPayload(widget));
        },
        mapEntity(widget, gridIndex = widget.i) {
        widget = this.toUiWidgetGeometry({ ...widget });
        widget.w = widget.width;
        widget.h = widget.height;
        widget.i = gridIndex;
        widget.static = false;

try {
  if (widget.content)
    widget.content = JSON.parse(widget.content);
    // API may return {Content:[...], Series:[], TotalCount:N} wrapper — unwrap to flat array
    if (widget.content && !Array.isArray(widget.content) && Array.isArray(widget.content.Content)) {
        widget.content = widget.content.Content;
    }
    if (widget.legend)
      widget.legend = JSON.parse(widget.legend);

} catch (err) { }

return widget;
},


    },
    watch: {
        // Seed the active tab's parsed Configuration store on tab activation (immediate covers the
        // first page once the tab list loads). Reads this.page for its `configuration`; page.id is
        // undefined for the empty / default / add pseudo-pages → seedTabConfiguration guards it to a
        // no-op.
        'page.id': {
            immediate: true,
            handler() {
                this.seedTabConfiguration(this.page);
            },
        },
        async page() {
            this.filterScopeRevision++;
            this.invalidateIsolation();
            // Chart selections belong to the dashboard where they were made. Clear them
            // before loading another page so an empty dashboard never presents a stale
            // selection bar or an unexplained filtered tag count. skipRefresh=true: this
            // watcher settles the session below, so clearAllSelections must not also
            // schedule its debounced refresh (that would create a second session + query).
            // Clear the error unconditionally too — it can outlive an empty map, and a stale
            // error must not ride onto an unrelated tab.
            this.selectionError = null;
            if (this.hasSelections) this.clearAllSelections(true);
            this.pageLoading = true;
            // Guards a rapid A→B→C switch: only the latest transition loads widgets, so the
            // await below can't let a superseded switch fire a second getWidgetsData.
            const pageGen = this._pageSwitchGen = (this._pageSwitchGen || 0) + 1;
            // Tab-transition session settle (B3): drop the previous tab's session id first
            // so no widget can query with a stale scope. Start a clean retry budget for this
            // switch, then — when global filters are active — await ONE fresh session attempt
            // before loading widgets so they never mount against the old scope. A failed
            // attempt flips to degraded-raw synchronously inside doRefreshFilterSession and
            // its background retry recovers later; the switch never waits on the retry chain.
            this.cancelFilterSessionRetry();
            this.filterSessionId = null;
            if (this.hasActiveGlobalFilters || this.hasSelections) {
                try {
                    await this.doRefreshFilterSession();
                } catch (err) {
                    // doRefreshFilterSession owns its own failure handling; guard only against
                    // an unexpected throw so widgets always load (never a blank dashboard).
                    error('CustomDashboard', 'Filter session settle threw on tab switch', err);
                }
            } else {
                // No filters remain on the new tab (a cross-filter, if any, was just cleared).
                // Route through the same refresh so its no-filters terminal branch clears any
                // degraded state carried over from the previous tab's failed session and bumps
                // the session sequence, so an in-flight request from that tab cannot re-degrade
                // this clean one. That branch is synchronous, so no await is needed here.
                this.doRefreshFilterSession();
            }
            // A newer switch started while we awaited — let it own the load (it also owns
            // pageLoading, so leaving it true here is correct: the latest switch clears it).
            if (pageGen !== this._pageSwitchGen) return;
            this.getWidgetsData();
        },
        $route() {
            // In-route ?tab= change (browser back/forward) — coordinated when editing.
            this.onRouteTabChange();
        },
        tab() {
            this.rowDetailsOpen = false;
            this.syncTabToRoute();
            this.$nextTick(() => this.revealActiveTab());
        }
    }
}
</script>

<style lang="scss">
/* =============================================
   Page Shell (matches inspection-dashboard-space)
   ============================================= */
.custom-dashboard-space {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    overflow-y: auto;
}

.custom-dashboard-content {
    padding: 0px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* =============================================
   Header (matches inspection-dashboard-header)
   ============================================= */
.custom-dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid rgba(79, 79, 79, 0.2);
}

.custom-dashboard-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 24px;
    font-weight: 700;
}

.custom-dashboard-header-options {
    display: flex;
    align-items: center;
    gap: 10px;
}

/* Show / hide dashboards dialog: a long tab list scrolls inside the body, the header
   and Done footer stay put. */
.cdb-tab-visibility-list {
    max-height: 60vh;
}

/* =============================================
   Save-status indicator (builder-v1)
   Flat industrial: 1px border, neutral surface, status read from glyph + text.
   No colored accent bar, no tinted wash (standing design rule).
   ============================================= */
.cdb-save-status {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid rgba(79, 79, 79, 0.28);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: inherit;
}

.cdb-save-status__glyph {
    opacity: 0.75;
}

.cdb-save-status--error .cdb-save-status__glyph {
    color: var(--v-error-base);
}

.cdb-save-status__text {
    white-space: nowrap;
}

.cdb-save-status__action {
    min-width: 0 !important;
    padding: 0 6px !important;
    font-weight: 600;
}

.cdb-spin {
    animation: cdb-spin 0.9s linear infinite;
}

@keyframes cdb-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Compact viewer policy entry point. The explanation belongs in the popover, not in the already
   crowded window-control strip. */
.cdb-viewer-settings-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: color 150ms ease, background-color 150ms ease;
}

.cdb-viewer-settings-btn .v-icon {
    color: inherit !important;
}

.cdb-viewer-settings-btn:hover,
.cdb-viewer-settings-btn[aria-expanded='true'] {
    background: rgba(0, 120, 100, 0.09);
    color: var(--v-primary-base);
}

.cdb-viewer-settings-btn:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 1px;
}

.theme--dark .cdb-viewer-settings-btn {
    color: rgba(255, 255, 255, 0.58);
}

.cdb-viewer-settings-card {
    width: 310px;
    padding: 14px 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.cdb-viewer-settings-card__eyebrow {
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.54);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1;
    text-transform: uppercase;
}

.cdb-viewer-settings-card__row {
    display: flex;
    align-items: center;
    gap: 14px;
}

.cdb-viewer-settings-card__copy {
    min-width: 0;
    flex: 1 1 auto;
}

.cdb-viewer-settings-card__title {
    color: rgba(0, 0, 0, 0.82);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
}

.cdb-viewer-settings-card__description {
    margin-top: 3px;
    color: rgba(0, 0, 0, 0.56);
    font-size: 10px;
    line-height: 1.4;
}

.cdb-viewer-settings-card__switch {
    flex: 0 0 auto;
    margin: 0 !important;
    padding: 0 !important;
}

.cdb-viewer-settings-card__switch .v-input__slot {
    margin: 0;
}

.theme--dark.cdb-viewer-settings-card {
    border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark.cdb-viewer-settings-card .cdb-viewer-settings-card__eyebrow,
.theme--dark.cdb-viewer-settings-card .cdb-viewer-settings-card__description {
    color: rgba(255, 255, 255, 0.62);
}

.theme--dark.cdb-viewer-settings-card .cdb-viewer-settings-card__title {
    color: rgba(255, 255, 255, 0.88);
}

.theme--dark .cdb-save-status {
    border-color: rgba(255, 255, 255, 0.18);
}

/* =============================================
   Empty State
   ============================================= */
.custom-dashboard-empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 420px;
    padding: 48px 24px;
}

.cdb-dashboard-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 68vh;
    padding: 32px 24px;
    text-align: center;
    color: var(--v-text-base);
}

.cdb-dashboard-empty-state__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin-bottom: 16px;
    border: 1px solid rgba(27, 52, 45, 0.14);
    border-radius: 8px;
    background: rgba(27, 52, 45, 0.04);
}

.cdb-dashboard-empty-state__icon .v-icon {
    font-size: 26px;
    color: var(--v-primary-base);
}

.cdb-dashboard-empty-state h2 {
    margin: 0;
    font-family: var(--ds-font-family-heading);
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
    color: inherit;
}

.cdb-dashboard-empty-state p {
    max-width: 54ch;
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.55;
    color: var(--v-text-base);
}

.cdb-dashboard-empty-state__action {
    margin-top: 18px;
}

.cdb-dashboard-page-empty-state {
    min-height: clamp(360px, calc(100vh - 250px), 640px);
    padding: clamp(48px, 8vh, 80px) 24px;
}

.cdb-dashboard-page-empty-state .cdb-dashboard-empty-state__icon {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
    border-color: rgba(3, 98, 76, 0.2);
    background: rgba(3, 98, 76, 0.07);
}

.cdb-dashboard-page-empty-state .cdb-dashboard-empty-state__icon .v-icon {
    font-size: 32px;
}

.cdb-dashboard-page-empty-state h2 {
    font-size: 22px;
}

.cdb-dashboard-page-empty-state p {
    max-width: 48ch;
    font-size: 14px;
}

.cdb-dashboard-page-empty-state__viewer-note {
    margin-top: 18px !important;
}

.theme--dark .cdb-dashboard-empty-state__icon {
    border-color: rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.045);
}

.theme--dark .cdb-dashboard-page-empty-state .cdb-dashboard-empty-state__icon {
    border-color: rgba(44, 194, 149, 0.34);
    background: rgba(44, 194, 149, 0.1);
}

/* =============================================
   Widget Bar (replaces v-sheet elevation-6)
   ============================================= */
.custom-dashboard-widget-bar {
    display: flex;
    align-items: center;
    padding: 8px 0;
}

/* =============================================
   Content Area (inline styles moved to classes)
   ============================================= */
.custom-dashboard-grid-area {
    overflow: auto;
    max-height: calc(100vh - 250px);
    padding: 0 !important;
}

.custom-dashboard-grid-wrapper {
    min-height: 69vh;
}

/* builder-v1: drop the calc(100vh - 250px) cap so the grid area fills the flex-filled
   row to the viewport bottom (that cap left ~120px dead space below the last widget). */
.cdb-builder-v1 .custom-dashboard-grid-area {
    max-height: none;
    height: 100%;
}
.cdb-builder-v1 .custom-dashboard-grid-wrapper {
    min-height: 100%;
}

.custom-dashboard-widget-wrapper {
    width: 100%;
    height: 100%;
}

.custom-dashboard-loading {
    min-height: 50vh;
}

/* =============================================
   Dark Mode (matches inspection dashboard)
   ============================================= */
.theme--dark .custom-dashboard-header {
    border-bottom-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .custom-dashboard-title {
    color: #fff;
}

.dark-background {
    background: var(--v-darkGrey-2-base);
}

.vue-grid-layout {
    background: var(--v-darkGrey-2-base);
    /* background: gray; */
}

.vue-grid-item:not(.vue-grid-placeholder) {
    /* background: #1E1E1E; */
    /* background: red; */
    /* border: 1px solid black; */
     background: var(--v-bg2-base);
    padding: 1rem;

    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    box-shadow: 0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 13px 19px 2px rgba(0, 0, 0, 0.14), 0px 5px 24px 4px rgba(0, 0, 0, 0.12) !important;
}
.vue-grid-placeholde{
    background-color: var(--v-primary-base);
}
.vue-grid-item .resizing {
    opacity: 0.7;
}

.vue-grid-item .static {
    /* background: black; */
}



.vue-grid-item .no-drag {
    height: 100%;
    width: 100%;
}

.vue-grid-item .minMax {
    font-size: 100px;
}

.vue-grid-item .add {
    cursor: pointer;
}

.vue-draggable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0;
    left: 0;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10'><circle cx='5' cy='5' r='5' fill='#999999'/></svg>") no-repeat;
    background-position: bottom right;
    padding: 0 8px 8px 0;
    background-repeat: no-repeat;
    background-origin: content-box;
    box-sizing: border-box;
    cursor: pointer;
}

.layoutJSON {
    background: #ddd;
    border: 1px solid black;
    margin-top: 10px;
    padding: 10px;
}

.columns {
    -moz-columns: 120px;
    -webkit-columns: 120px;
    columns: 120px;
}

.vue-grid-item>.vue-resizable-handle {
    /* color: white !important; */
    background: url('../../../../public/icons/resize-handle-svgrepo-com-20px.svg') !important;
}



#custom-dashboard-tabs {
    font-family: 'Poppins', sans-serif;

    & .v-btn {
        text-transform: none !important;
        border-radius: 8px !important;
        letter-spacing: normal !important;
        font-weight: 500;
        font-size: 13px;
        height: 40px;
        padding: 0 16px;
        transition: all 0.2s ease;

        background-color: #f5f5f5 !important;
        border: 1px solid #e0e0e0 !important;
        color: #666 !important;

        & i {
            color: #666 !important;
        }

        &:before {
            display: none !important;
        }

        &:hover:not(.v-btn--active) {
            background-color: #eeeeee !important;
            border-color: #d0d0d0 !important;
        }

        &.v-btn--active {
            background-color: transparent !important;
            border: 2px solid var(--v-primary-base) !important;
            color: var(--v-primary-base) !important;
            font-weight: 600;
            box-shadow: none !important;

            & i {
                color: var(--v-primary-base) !important;
            }
        }

        // Nested menu activator button — remove tab button styling
        & .v-btn {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
            min-width: 0 !important;
            padding: 0 !important;
            height: auto !important;
        }
    }
}

.theme--dark #custom-dashboard-tabs {
    & .v-btn {
        background-color: rgba(255, 255, 255, 0.08) !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
        color: rgba(255, 255, 255, 0.7) !important;

        & i {
            color: rgba(255, 255, 255, 0.7) !important;
        }

        &:hover:not(.v-btn--active) {
            background-color: rgba(255, 255, 255, 0.12) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
        }

        &.v-btn--active {
            background-color: transparent !important;
            border-color: var(--v-primary-base) !important;
            color: var(--v-primary-base) !important;

            & i {
                color: var(--v-primary-base) !important;
            }
        }
    }
}

.custom-dashboard-tab-items {
    margin-top: 1rem;
}

/* =============================================
   Dashboard tab bar. Tabs keep a useful content width; when the set outgrows the band,
   the native horizontal strip remains reachable by scrollbar, wheel/trackpad and focus.
   ============================================= */
.cdb-tab-bar {
    display: contents;
}

/* =============================================
   W8 — compact single-band top bar (showcase variant C + grafts).
   Fuses title | underline tabs | actions into one ~52px band so the grid
   reclaims the vertical space; flag-off keeps the display:contents wrapper
   (two-row header + pill tabs), pixel-identical.
   ============================================= */
.cdb-tab-bar.cdb-tab-bar--v1 {
    display: flex;
    align-items: stretch;
    gap: 0;
    height: 52px;
    min-height: 52px;
    padding: 0 18px;
    /* bleed over the content's 15px padding so the band is a flush full-width toolbar */
    background: var(--ds-surface-elevated, #fcfdfc);
    border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.10));
    box-shadow: 0 1px 2px rgba(16, 32, 28, 0.05);
}

/* title cluster — far left, glyph in a filled brand badge (prominent, like the showcase) */
.cdb-band-title {
    display: flex;
    align-items: center;
    gap: 11px;
    flex: 0 0 auto;
    padding: 0 18px 0 2px;
    margin-right: 12px;
    border-right: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
    font-family: var(--ds-font-family-heading);
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #1f2937;
    white-space: nowrap;
}
.cdb-band-title .v-icon {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    /* quieter tinted badge — castleton icon on a castleton-tint field (showcase variant C),
       not a filled solid badge with a white icon */
    background: rgba(3, 98, 76, 0.08);
    color: var(--v-primary-base) !important;
    font-size: 16px !important;
}
.theme--dark .cdb-band-title .v-icon {
    background: rgba(44, 194, 149, 0.14);
    color: var(--v-secondary-base) !important;
}

/* tabs wrapper — a real flex box only inside the band (overflow measures ITS width);
   layout-transparent in flag-off so the toggle promotes to the content column as today. */
.cdb-band-tabs {
    display: contents;
}
.cdb-tab-bar--v1 .cdb-band-tabs {
    display: flex;
    align-items: stretch;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-inline: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--ds-border-subtle, rgba(0, 0, 0, 0.20)) transparent;
    -webkit-overflow-scrolling: touch;
}
.cdb-tab-bar--v1 .cdb-band-tabs::-webkit-scrollbar {
    height: 6px;
}
.cdb-tab-bar--v1 .cdb-band-tabs::-webkit-scrollbar-track {
    background: transparent;
}
.cdb-tab-bar--v1 .cdb-band-tabs::-webkit-scrollbar-thumb {
    background: var(--ds-border-subtle, rgba(0, 0, 0, 0.20));
    border-radius: var(--ds-radius-full, 999px);
}

/* underline tabs — neutralize the Vuetify btn-toggle pills into flat underline items */
.cdb-tab-bar--v1 #custom-dashboard-tabs.v-btn-toggle {
    display: flex !important;
    flex: 0 0 max-content;
    flex-wrap: nowrap !important;
    align-items: stretch;
    width: max-content;
    min-width: max-content;
    gap: 2px !important;
    height: 100%;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn {
    flex: 0 0 auto;
    height: 100% !important;
    min-width: 0 !important;
    max-width: 260px;
    margin: 0 !important;
    padding: 0 15px !important;
    border: 0 !important;
    border-radius: 6px 6px 0 0 !important;
    background: transparent !important;
    color: #6b7280 !important;
    font-family: var(--ds-font-family-body);
    font-size: 14.5px;
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none;
    box-shadow: none !important;
    overflow: hidden !important;
    transition: color 0.18s ease-out, background-color 0.18s ease-out, box-shadow 0.18s ease-out;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn > .v-btn__content {
    /* Vuetify 2 defaults this to flex: 1 0 auto, so long labels keep their intrinsic width and
       paint across adjacent capped tabs. Let the content shrink; only the label clips. */
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow: visible;
    justify-content: flex-start;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn > .v-btn__content > .v-icon,
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn > .v-btn__content > .cdb-tab-kebab {
    flex: 0 0 auto;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .cdb-tab-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
/* underline indicator on ::after (showcase .tab::after) — inset 10px each side, 2px tall,
   transparent until active so it fades in; unlike the old full-width inset box-shadow it
   only spans the tab's inner width. */
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn::after {
    content: "";
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: transparent;
    pointer-events: none;
    transition: background-color 0.18s ease-out;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn:hover {
    color: #1f2937 !important;
    background: rgba(0, 0, 0, 0.04) !important;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active {
    color: var(--v-primary-base) !important;
    font-weight: 600;
    background: transparent !important;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active::after {
    background: var(--v-primary-base);
}
/* Vuetify paints its hover/active overlay via ::before — keep it invisible so only our
   underline reads as selection (no pill wash). */
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn::before {
    opacity: 0 !important;
}
/* a11y: suppressing ::before removed Vuetify's keyboard-focus cue, so restore an explicit
   focus ring for keyboard users (:focus-visible = keyboard only, not on mouse click).
   Inset so the tab strip's scroll viewport never clips it. */
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn:focus-visible {
    outline: 2px solid var(--v-primary-base) !important;
    outline-offset: -2px;
    color: #1f2937 !important;
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn:focus-visible {
    outline-color: var(--v-secondary-base) !important;
    color: #e6ecea !important;
}

/* tab icon sizing (inherits tab color; active tab icon is primary green) */
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn .v-icon {
    font-size: 16px !important;
    color: #6b7280 !important;
    margin-right: 2px;
}
.cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active .v-icon {
    color: var(--v-primary-base) !important;
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active .v-icon {
    color: var(--v-secondary-base) !important;
}

/* per-tab kebab (builder-v1) — a non-button activator styled as an icon button */
.cdb-tab-bar--v1 .cdb-tab-kebab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin-left: 4px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.15s ease-out;
}
.cdb-tab-bar--v1 .cdb-tab-kebab .v-icon {
    color: #9ca3af !important;
}
.cdb-tab-bar--v1 .cdb-tab-kebab:hover {
    background: rgba(0, 0, 0, 0.06);
}
.cdb-tab-bar--v1 .cdb-tab-kebab:hover .v-icon {
    color: #1f2937 !important;
}
.cdb-tab-bar--v1 .cdb-tab-kebab:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 1px;
}

/* "Add Widget" button (edit mode, below the band) — match the band's sentence-case,
   hairline, 32px aesthetic instead of the default uppercase pill */
.cdb-builder-v1 .custom-dashboard-widget-bar .v-btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    border-radius: 7px !important;
    height: 32px !important;
    font-weight: 500 !important;
    font-size: 13px !important;
}

/* actions cluster — far right */
.cdb-band-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding-left: 14px;
    margin-left: 10px;
    border-left: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.07));
}

/* action buttons — override Vuetify's uppercase pills into the showcase's crafted
   sentence-case controls: 32px, 7px radius, hairline ghost vs solid primary. */
.cdb-band-actions .v-btn {
    height: 32px !important;
    min-height: 32px !important;
    min-width: 0 !important;
    padding: 0 14px !important;
    border-radius: 7px !important;
    font-family: var(--ds-font-family-body) !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    letter-spacing: normal !important;
    text-transform: none !important;
    box-shadow: none !important;
}
/* icon buttons in the band (edit pencil) — square, not the default round v-btn--icon */
.cdb-band-actions .v-btn.v-btn--icon,
.cdb-band-actions .cdb-edit-icon {
    width: 32px !important;
    padding: 0 !important;
    border-radius: 7px !important;
    border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.14)) !important;
    color: #1f2937 !important;
}
.cdb-band-actions .v-btn.v-btn--icon:hover {
    background: rgba(0, 0, 0, 0.04) !important;
    border-color: rgba(0, 0, 0, 0.26) !important;
}
/* cross-filter selection chip — squared off, clearable x */
.cdb-band-actions .cdb-legend-chip.v-chip {
    height: 30px !important;
    border-radius: 6px !important;
    font-family: var(--ds-font-family-body);
    font-size: 12.5px;
}
.cdb-band-actions .cdb-legend-chip.v-chip .v-chip__close {
    font-size: 16px;
    margin-left: 4px;
}
/* the filters count chip stays a small pill; everything else squared */
.cdb-band-actions .v-btn > .v-chip {
    border-radius: 6px !important;
}
/* add-dashboard "+" beside the tabs — square icon button */
.cdb-tab-bar--v1 .cdb-add-tab.v-btn {
    width: 30px !important;
    height: 30px !important;
    align-self: center;
    margin-left: 6px;
    border-radius: 6px !important;
    border: 1px dashed var(--ds-border-subtle, rgba(0, 0, 0, 0.18)) !important;
    flex: 0 0 auto;
}
.cdb-tab-bar--v1 .cdb-add-tab.v-btn .v-icon {
    color: #6b7280 !important;
    font-size: 18px !important;
}
.cdb-tab-bar--v1 .cdb-add-tab.v-btn:hover {
    border-color: var(--v-primary-base) !important;
    background: rgba(3, 98, 76, 0.06) !important;
}
.cdb-tab-bar--v1 .cdb-add-tab.v-btn:hover .v-icon {
    color: var(--v-primary-base) !important;
}
.cdb-band-actions .v-btn .v-icon {
    font-size: 17px !important;
}
/* ghost / outlined (Filters, Edit dashboard, Clear cross-filter) — neutral, hairline */
.cdb-band-actions .v-btn.v-btn--outlined {
    border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.14)) !important;
    background: transparent !important;
    color: #1f2937 !important;
}
.cdb-band-actions .v-btn.v-btn--outlined:hover {
    background: rgba(0, 0, 0, 0.04) !important;
    border-color: rgba(0, 0, 0, 0.26) !important;
}
.cdb-band-actions .v-btn.v-btn--outlined .v-icon {
    color: #6b7280 !important;
}
/* solid primary (Add Dashboard, Done) — the dominant action */
.cdb-band-actions .v-btn.primary:not(.v-btn--outlined) {
    font-weight: 600 !important;
    color: #fff !important;
    box-shadow: 0 1px 2px rgba(16, 32, 28, 0.10) !important;
}
.cdb-band-actions .v-btn.primary:not(.v-btn--outlined):hover {
    box-shadow: 0 2px 8px rgba(16, 32, 28, 0.16) !important;
}
.cdb-band-actions .v-btn.primary:not(.v-btn--outlined) .v-icon {
    color: #fff !important;
}
/* filters count badge — compact pill on primary */
.cdb-band-actions .v-btn .v-chip.v-size--x-small {
    height: 17px !important;
    min-width: 17px;
    border-radius: 9px !important;
    font-size: 11px;
}
/* a11y: explicit keyboard-focus ring on the restyled action buttons */
.cdb-band-actions .v-btn:focus-visible {
    outline: 2px solid var(--v-primary-base) !important;
    outline-offset: 2px;
}
.theme--dark .cdb-band-actions .v-btn:focus-visible {
    outline-color: var(--v-secondary-base) !important;
}

/* the band already spaces itself; drop the legacy tab-items top gap under the flag */
.cdb-builder-v1 .custom-dashboard-tab-items {
    margin-top: 0;
}

/* dark theme */
.theme--dark .cdb-tab-bar.cdb-tab-bar--v1 {
    background: var(--ds-surface-elevated, #161f1c);
    border-color: rgba(255, 255, 255, 0.12);
}
.theme--dark .cdb-band-title {
    color: #e6ecea;
    border-right-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .cdb-band-actions {
    border-left-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn {
    color: #9aa8a3 !important;
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn:hover {
    color: #e6ecea !important;
    background: rgba(255, 255, 255, 0.06) !important;
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active {
    color: var(--v-secondary-base) !important;
}
.theme--dark .cdb-tab-bar--v1 #custom-dashboard-tabs .v-btn.v-btn--active::after {
    background: var(--v-secondary-base);
}

    .vue-grid-item.vue-grid-placeholder{
        background:green !important;
    }

/* =============================================
   Touch targets (builder-v1, spec §9 / plan step 19)
   Edit-mode chrome reaches a >=44px hit area on touch-capable pointers WITHOUT enlarging the
   visual glyph. Scoped under .cdb-builder-v1 so flag-off rendering stays byte-identical;
   @media (pointer: coarse) leaves mouse users today's compact chrome. Refresh/filter are
   view-mode consumption controls and stay as-is (out of step 19 scope).
   ============================================= */
@media (pointer: coarse) {
    .cdb-builder-v1 {
        /* Widget drag grip — glyph stays put; padding grows the grab zone, negative margin
           cancels the layout impact so neighbours don't move (drag-allow-from still matches). */
        .widget-drag-handle {
            padding: 12px;
            margin: -12px;
        }

        /* Widget kebab — 44px square hit area, small centred glyph unchanged. */
        .cdb-touch-target.v-btn--icon {
            width: 44px !important;
            height: 44px !important;
        }

        /* Resize corner — enlarge the grabbable box; keep the SVG glyph small and pinned to
           the bottom-right visual corner (library positions the handle bottom:0/right:0). */
        .vue-grid-item > .vue-resizable-handle {
            width: 44px !important;
            height: 44px !important;
            background-size: 20px 20px !important;
            background-position: bottom right !important;
            background-repeat: no-repeat !important;
        }
    }

    /* Tab kebab lives under #custom-dashboard-tabs, whose nested-.v-btn reset forces
       height:auto at id-level specificity — beat it with an id-scoped rule so the tab kebab
       reaches 44px too. */
    .cdb-builder-v1 #custom-dashboard-tabs .cdb-touch-target.v-btn--icon {
        width: 44px !important;
        height: 44px !important;
        min-width: 44px !important;
    }
}

/* =====================================================================================
   Showcase restyle (builder-v1, plan W7 / step 22)
   Closes the visual gap to .claude/showcases/dashboard-manager.html +
   dashboard-builder-{view,edit}-mode.html. ALL rules are scoped under .cdb-builder-v1 so
   flag-off renders byte-for-byte as today. Values come from --ds-* / --v-* design tokens
   (the showcase :root is "duplicated verbatim from _design-tokens.scss") — no parallel
   tokens, no raw hex. Impeccable bans honoured: flat 1px-border surfaces, hover = shadow
   depth only (no lifts), accent (castleton) reserved for actions/selection/state, no
   side-stripe accents, no gradient text, no glassmorphism, 150-250ms state transitions.
   ===================================================================================== */

/* Body font for the dashboard subtree — .custom-dashboard-space hardcodes Poppins on
   everything (line ~1614); PRODUCT.md + the showcase are explicit that Poppins is
   headings-only and IBM Plex Sans carries body/data/tables. Restore the split under
   the flag; the global heading whitelist keeps true headings on Poppins. */
.custom-dashboard-space.cdb-builder-v1 {
    font-family: var(--ds-font-family-body);
    background: var(--ds-brand-anti-flash);
}
.theme--dark .custom-dashboard-space.cdb-builder-v1 {
    background: var(--v-bg1-base);
}
.cdb-builder-v1 .custom-dashboard-title {
    font-family: var(--ds-font-family-heading);
}

/* Widget card anatomy — the biggest lever. The base .vue-grid-item carries a heavy triple
   Material shadow + centred content; the showcase card is a flat surface, 1px border,
   subtle shadow that deepens on hover. Higher specificity + !important beats the base
   !important shadow; flag-off .vue-grid-item (legacy DashboardGrid.vue included) is
   untouched. */
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder) {
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-md);
    box-shadow: var(--ds-shadow-1) !important;
    transition: box-shadow var(--ds-transition-default);
    padding: 12px;
    align-items: stretch;
    justify-content: flex-start;
}
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):hover {
    box-shadow: var(--ds-shadow-2) !important;
}

/* The AG Grid showcase uses a green-tinted neutral card boundary. Keep this override scoped
   to the one grid item that actually contains the dashboard datagrid variant. */
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-dashboard-grid-widget) {
    background: #fcfdfc;
    border-color: rgba(27, 52, 45, 0.14);
    border-radius: 10px;
}

/* Ghost "add widget" tile — a presentational dashed card that ends the grid in edit mode
   (showcase dashboard-builder-edit-mode.html .ghost-tile). It is NOT a vue-grid-layout item,
   so it never enters `layout` and is never persisted; it only opens the Add-Widget wizard.
   Tokens (not literals) so the tile is theme-aware in dark mode. */
.cdb-builder-v1 .cdb-ghost-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 9px;
    width: 100%;
    max-width: 320px;
    min-height: 150px;
    /* left gutter matches the vue-grid-layout margin [16,16] so the tile lines up with the
       grid items' left edge (measured: items sit 16px right of the grid-wrapper edge). */
    margin: 16px 0 0 16px;
    padding: 18px;
    text-align: center;
    background: var(--ds-surface-elevated);
    border: 1.5px dashed var(--ds-border-subtle);
    border-radius: var(--ds-radius-md);
    color: var(--v-text-base);
    font-family: var(--ds-font-family-body);
    cursor: pointer;
    transition: border-color 0.15s ease-out, color 0.15s ease-out;
}
.cdb-builder-v1 .cdb-ghost-tile:hover {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
}
.cdb-builder-v1 .cdb-ghost-tile:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 2px;
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
}
.cdb-builder-v1 .cdb-ghost-tile__plus {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1.5px dashed currentColor;
}
.cdb-builder-v1 .cdb-ghost-tile__plus .v-icon {
    font-size: 20px;
    color: inherit !important;
}
.cdb-builder-v1 .cdb-ghost-tile__label {
    font-size: 13px;
    font-weight: 600;
    color: inherit;
}
.cdb-builder-v1 .cdb-ghost-tile__hint {
    max-width: 190px;
    font-size: 11px;
    line-height: 1.4;
    color: var(--v-text-base);
}

/* Grid + panel backgrounds go transparent so the flat cards float on the page's neutral
   surface (showcase: cards on --anti-flash, no grid fill). */
.cdb-builder-v1 .vue-grid-layout,
.cdb-builder-v1 .dark-background {
    background: transparent;
}
.cdb-builder-v1 .custom-dashboard-grid-area .v-card,
.cdb-builder-v1 .custom-dashboard-grid-area.v-card,
.cdb-builder-v1 .custom-dashboard-tab-items .v-card {
    background: transparent;
    box-shadow: none;
}

/* Widget toolbar icons (refresh / filter / kebab) — showcase resting state is muted,
   castleton on hover. */
.cdb-builder-v1 .vue-grid-item .v-btn--icon .v-icon {
    color: var(--v-text-base);
}
.cdb-builder-v1 .vue-grid-item .v-btn--icon:hover .v-icon {
    color: var(--v-primary-base);
}

/* Widget card header — showcase pattern (title + uppercase source micro-label). Markup
   lives in dashboard-widget.vue behind the :builderHeader prop that ONLY DashboardItem
   passes, so these classes mount only in the flag-on custom-dashboard context; every
   other consumer of dashboard-widget.vue is untouched. Title in Poppins 600 at the
   unified tier sizes (14/15/17, see .cdb-widget-title below), source micro-label
   uppercase-muted. */
.cdb-builder-v1 .cdb-widget-head {
    /* Matches the showcase header->body rhythm: card__head padding-bottom 6px + card__body
       padding-top 2px = 8px between the source micro-label and the chart. */
    margin-bottom: 8px;
}
.cdb-builder-v1 .cdb-widget-title {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    /* Unified header typography (user decision 2026-07-18): EVERY widget family follows the
       datagrid header look - Poppins at the grid's tier sizes (normal 15px) - replacing the
       old 13px body-font chart titles. Tier rules below scale it (small 14 / large 17). */
    font-family: var(--ds-font-family-heading) !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    line-height: 1.3;
    letter-spacing: -0.012em;
    color: var(--v-text-base);
}
.cdb-builder-v1 .cdb-widget-sub {
    display: inline-block;
    /* 2px title->sub gap mirrors the showcase .card__sub margin-top. */
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.35;
    color: var(--v-text-base);
}

/* Datagrid cards use the enlarged, edge-to-edge header anatomy from the approved AG Grid
   screenshot. Other chart/KPI headers keep their compact dashboard treatment. */
.cdb-builder-v1 .cdb-widget-head--dashboard-grid {
    min-height: 63px;
    margin-bottom: 8px;
    padding-right: 8px;
}
/* Datagrid header title/sub previously carried their own enlarged typography; the unified
   .cdb-widget-title/.cdb-widget-sub rules above now govern every family identically (the
   grid inherits the same tier sizes as charts and tables). */

/* Widget size - ONE header scale for every family (the grid's): the shell carries
   .cdb-widget--size-<tier> (dashboard-widget.vue root). Normal = the 15/12px base above;
   tiers below adjust title/sub together: small 14/11, large 17/13. */
.cdb-builder-v1 .cdb-widget--size-small .cdb-widget-title {
    font-size: 14px !important;
}
.cdb-builder-v1 .cdb-widget--size-small .cdb-widget-sub {
    font-size: 11px;
}
.cdb-builder-v1 .cdb-widget--size-large .cdb-widget-title {
    font-size: 17px !important;
}
.cdb-builder-v1 .cdb-widget--size-large .cdb-widget-sub {
    font-size: 13px;
}
/* KPI / count widget (builder-v1): the ratified A+ .kpi2 card (kpi-card-options.html:78-98)
   mapped to the widget. The grid-item (real board) or the wizard preview stage supplies the
   flat card surface + 12px inset, so this block adds the showcase's remaining 3/5/2px to reach
   the 15/17/14 card padding, then lays out the three rows — label, big value,
   hairline-topped meta — as a full-height flex column so the meta bottom-anchors and the tile
   never reads as an empty box. A count carries no severity signal, so the value takes
   theme-aware ink (the mockup's accent-less default); never a GUID-hash color (the removed
   anti-pattern) and never brand castleton (fails contrast on the dark card the app can render).
   Muted label/meta use ink at reduced opacity - the canonical secondary text token (#C8C8C8)
   fails contrast on the white card. The value's font-family needs !important to beat Vuetify's
   global body-font rule (same pattern as .cdb-widget-title). */
.cdb-builder-v1 .cdb-kpi2 {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 3px 5px 2px;
}
.cdb-builder-v1 .cdb-kpi2__top {
    /* The label is LEFT-aligned (no space-between) so the top-RIGHT
       corner stays empty for DashboardItem's absolutely-overlaid widget controls (filter /
       refresh / kebab). Mirrors the chart header pattern (.cdb-widget-title). */
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 15px;
}
.cdb-builder-v1 .cdb-kpi2__label {
    /* 0 1 auto: never grow (keeps the badge tight against the label instead of pushed to the
       far right), but shrink + ellipsis when space is tight so the label never underlaps the
       badge or the overlaid controls. */
    flex: 0 1 auto;
    min-width: 0;
    /* Unified header typography (user decision 2026-07-18): the KPI label follows the same
       widget-title language as every other family (Poppins, tier sizes 14/15/17) instead of
       the old 10.5px uppercase micro-label. */
    /* !important: the design-tokens global body-font rule out-specifies this selector. */
    font-family: var(--ds-font-family-heading) !important;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.012em;
    color: var(--v-text-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cdb-builder-v1 .cdb-kpi2__val {
    display: flex;
    align-items: baseline;
    gap: 5px;
    margin-top: 11px;
}
.cdb-builder-v1 .cdb-kpi2:not(.cdb-kpi2--empty) .cdb-kpi2__val {
    margin-top: auto;
    margin-bottom: auto;
}
.cdb-builder-v1 .cdb-kpi2__num {
    font-family: var(--ds-font-family-heading) !important;
    font-weight: 600;
    font-size: 37px;
    line-height: 1;
    letter-spacing: -0.02em;
    color: var(--v-text-base);
    font-variant-numeric: tabular-nums;
}
/* Optional unit after the value (showcase .kpi2__val .unit): smaller, muted, on the value's
   baseline with the val row's 5px gap. Heading font with !important for the same reason the
   number needs it (beat Vuetify's global body-font rule) so "80 yr" reads as one typographic
   unit. */
.cdb-builder-v1 .cdb-kpi2__unit {
    font-family: var(--ds-font-family-heading) !important;
    font-weight: 700;
    text-transform: capitalize;
    font-size: 15px;
    line-height: 1;
    letter-spacing: 0;
    color: var(--v-text-base);
    opacity: 0.6;
}
/* 3-class specificity (like the old .cdb-kpi-body .cdb-kpi-icon) so the muted ink reliably
   beats Vuetify's .theme--light.v-icon color regardless of stylesheet source order. margin-inline-start:
   auto pushes the icon to the trailing edge of the .cdb-kpi2__val row (flush right in LTR, flush
   left in RTL) instead of sitting inline right after the unit label; the num/unit pair keeps its
   tight 5px gap since the auto margin only applies to the icon. Logical property (not margin-left)
   so this flips automatically under dir="rtl", matching the .ds-content-width margin-inline
   convention in _component-patterns.scss. Cards without an icon render no .cdb-kpi2__icon element,
   so the row's layout (num [+ unit]) is byte-identical whether or not the icon exists — nothing is
   reserved. */
.cdb-builder-v1 .cdb-kpi2__val .cdb-kpi2__icon {
    align-self: center;
    margin-inline-start: auto;
    color: var(--v-text-base);
    opacity: 0.55;
}
/* KPI drill to list: the value row is a <button> that opens the records behind the number.
   Button chrome is reset so the card reads exactly as before; the affordance is the pointer, a
   soft hover surface and a trailing list glyph that strengthens on hover/focus. Negative inline
   margin + matching padding keep the number where it was while giving the hover surface room. */
.cdb-builder-v1 .cdb-kpi2__val--drill {
    width: calc(100% + 2 * var(--ds-space-xs));
    margin-inline: calc(-1 * var(--ds-space-xs));
    padding: 2px var(--ds-space-xs);
    border: 0;
    border-radius: var(--ds-radius-sm);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: start;
    cursor: pointer;
    transition: background-color 0.15s ease;
}
.cdb-builder-v1 .cdb-kpi2__val--drill:hover {
    background-color: var(--ds-surface-hover);
}
.cdb-builder-v1 .cdb-kpi2__val--drill:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 2px;
}
.cdb-builder-v1 .cdb-kpi2__val--drill[aria-busy="true"] {
    cursor: progress;
}
.cdb-builder-v1 .cdb-kpi2__val .cdb-kpi2__drill-icon {
    align-self: center;
    margin-inline-start: auto;
    color: var(--v-text-base);
    opacity: 0.35;
    transition: opacity 0.15s ease;
}
/* With a status icon (which already takes the trailing edge) the list glyph sits right after it. */
.cdb-builder-v1 .cdb-kpi2__val .cdb-kpi2__icon ~ .cdb-kpi2__drill-icon {
    margin-inline-start: 0;
}
.cdb-builder-v1 .cdb-kpi2__val--drill:hover .cdb-kpi2__drill-icon,
.cdb-builder-v1 .cdb-kpi2__val--drill:focus-visible .cdb-kpi2__drill-icon {
    opacity: 0.8;
}
/* Severity tone: the value + trailing icon take a status color so the number reads as good,
   caution or critical at a glance. Color never stands alone; it pairs with the trailing icon
   and the value weight (design-system principle 2). The icon selector carries 4 classes so it
   beats the neutral 3-class muted-ink rule above; opacity 1 restores full tone saturation. */
.cdb-builder-v1 .cdb-kpi2--good .cdb-kpi2__num,
.cdb-builder-v1 .cdb-kpi2--good .cdb-kpi2__val .cdb-kpi2__icon {
    color: var(--v-primary-base);
    opacity: 1;
}
.cdb-builder-v1 .cdb-kpi2--caution .cdb-kpi2__num,
.cdb-builder-v1 .cdb-kpi2--caution .cdb-kpi2__val .cdb-kpi2__icon {
    /* Text-safe gold: #a07a00 is ~3.9:1 on white and passes WCAG large-text (3:1). Do NOT use
       #FBBD1C / #d2a000 for TEXT (~2.4:1, fails); those bright golds are fill-only. */
    color: #a07a00;
    opacity: 1;
}
.cdb-builder-v1 .cdb-kpi2--critical .cdb-kpi2__num,
.cdb-builder-v1 .cdb-kpi2--critical .cdb-kpi2__val .cdb-kpi2__icon {
    color: #b71c1c;
    opacity: 1;
}
.cdb-builder-v1 .cdb-kpi2--good {
    background-color: #f8fbfa;
}
.cdb-builder-v1 .cdb-kpi2--caution {
    background-color: #fdfbf5;
}
.cdb-builder-v1 .cdb-kpi2--critical {
    background-color: #fdf8f8;
}
/* The grid item is the actual KPI card. Match the inner tone wash here too so its 12px shell
   never reads as a separate neutral frame around a tinted rectangle. `:has` is already part of
   the builder-v1 grid contract for dashboard datagrids above. */
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--good) {
    background-color: #f8fbfa;
}
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--caution) {
    background-color: #fdfbf5;
}
.cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--critical) {
    background-color: #fdf8f8;
}
/* Dark-theme tone overrides. On the dark card surface (--ds-surface-elevated #2d2d2d) the light
   tone values fail WCAG large-text contrast (3:1 minimum): castleton #03624c is near-invisible,
   critical #b71c1c ~1.6:1, caution #a07a00 ~3.1:1 (borderline). Prefixing .theme--dark raises
   each selector one class above its light counterpart (num 4>3, icon 5>4) so it reliably wins in
   dark mode; light theme never carries .theme--dark, so the shipped light values are unchanged.
   Contrast ratios below are the tone color vs #2d2d2d. Opacity carries over from the light tone
   rules above (both match in every theme), so these override color only. */
.theme--dark .cdb-builder-v1 .cdb-kpi2--good .cdb-kpi2__num,
.theme--dark .cdb-builder-v1 .cdb-kpi2--good .cdb-kpi2__val .cdb-kpi2__icon {
    /* Mint (secondary): PRODUCT.md rule, primary castleton is too dark to see on dark chrome.
       ~6.1:1. */
    color: var(--v-secondary-base);
}
.theme--dark .cdb-builder-v1 .cdb-kpi2--caution .cdb-kpi2__num,
.theme--dark .cdb-builder-v1 .cdb-kpi2--caution .cdb-kpi2__val .cdb-kpi2__icon {
    /* Gold inversion: the #a07a00 trap is gold TEXT on WHITE. On the dark card the darker #a07a00
       is the one that fails (~3.1:1); the brighter chart gold #d2a000 is the legible one here.
       ~5.7:1. */
    color: #d2a000;
}
.theme--dark .cdb-builder-v1 .cdb-kpi2--critical .cdb-kpi2__num,
.theme--dark .cdb-builder-v1 .cdb-kpi2--critical .cdb-kpi2__val .cdb-kpi2__icon {
    /* The dark theme error token is #b71c1c (vuetify.js:69), same as light and dark-illegible
       (~1.6:1), so a dark-legible red is hardcoded instead. Material red-400 #ef5350 ~3.9:1. */
    color: #ef5350;
}
.theme--dark .cdb-builder-v1 .cdb-kpi2--good {
    background-color: #21332e;
}
.theme--dark .cdb-builder-v1 .cdb-kpi2--caution {
    background-color: #332f22;
}
.theme--dark .cdb-builder-v1 .cdb-kpi2--critical {
    background-color: #342626;
}
.theme--dark .cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--good) {
    background-color: #21332e;
}
.theme--dark .cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--caution) {
    background-color: #332f22;
}
.theme--dark .cdb-builder-v1 .vue-grid-item:not(.vue-grid-placeholder):has(.cdb-kpi2--critical) {
    background-color: #342626;
}
.cdb-builder-v1 .cdb-kpi2__meta {
    margin-top: auto;
    padding-top: 9px;
    border-top: 1px solid var(--ds-border-subtle);
    font-family: var(--ds-font-family-body);
    font-size: 12px;
    line-height: 1.35;
    color: var(--v-text-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cdb-builder-v1 .cdb-kpi2:not(.cdb-kpi2--empty) .cdb-kpi2__meta {
    margin-top: 0;
}
/* Text runs stay muted; digit runs go full-ink + bold so authored numbers pop (showcase
   .kpi2__meta b). The mute lives on the text spans, NOT the container: container opacity would
   create a stacking context that clamps the bold runs to the same alpha, so numbers could never
   darken. Theme-safe ink at reduced opacity (the secondary token fails contrast on the white
   card, see the value note above). */
.cdb-builder-v1 .cdb-kpi2__meta > span:not(.cdb-kpi2__meta-b) {
    opacity: 0.68;
}
.cdb-builder-v1 .cdb-kpi2__meta-b {
    font-weight: 600;
    color: var(--v-text-base);
}
/* Schema status chip (builder-v1 'By schema' coloring, §4.4/§4.9). Sits just under the value; the
   meta row bottom-anchors below it via margin-top:auto. A flat, hairline-outlined chip — a small
   color dot (or a warn glyph for a failure state) plus an always-present text label, so color never
   stands alone. No tinted wash / accent bar (industrial-design rule). */
.cdb-builder-v1 .cdb-kpi2__status {
    display: flex;
    align-items: center;
    margin-top: 7px;
    min-width: 0;
}
.cdb-builder-v1 .cdb-kpi2__schema {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 2px 8px;
    border: 1px solid var(--ds-border-subtle);
    border-radius: var(--ds-radius-full);
    font-family: var(--ds-font-family-body);
    font-size: 10.5px;
    line-height: 1.3;
}
.cdb-builder-v1 .cdb-kpi2__schema-dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    /* 3:1 non-text outline so the grey no-data / out-of-range dot stays crisp on the white card
       surface (§4.9). Matched values color the number itself now, so this dot is failure-state only. */
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.25);
}
.cdb-builder-v1 .cdb-kpi2__schema-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--v-text-base);
    opacity: 0.72;
}
/* Failure states: no color dot; a warn glyph carries the meaning beside the label. Alarming
   (schema unavailable) uses the text-safe gold; a transient load error stays muted ink. The icon
   selector carries 3 classes so the tone beats Vuetify's .theme--light.v-icon default. */
.cdb-builder-v1 .cdb-kpi2__schema .cdb-kpi2__schema-warn {
    color: var(--v-text-base);
    opacity: 0.55;
}
.cdb-builder-v1 .cdb-kpi2__schema--alarm .cdb-kpi2__schema-warn {
    color: #a07a00;
    opacity: 1;
}
/* Dark card: #a07a00 fails contrast on #2d2d2d (~3.1:1); the brighter #d2a000 is legible here
   (mirrors the .cdb-kpi2--caution dark override). The extra .theme--dark class wins over the light
   rule; light theme is untouched. */
.theme--dark .cdb-builder-v1 .cdb-kpi2__schema--alarm .cdb-kpi2__schema-warn {
    color: #d2a000;
}

/* Widget size - KPI density variants (dashboard-widget binds .cdb-kpi2--small / --large via
   kpiSizeClass; normal carries no modifier, so the base .cdb-kpi2 rules above stay verbatim - the
   37px card is today's look). Small/Large step the value plus the labels, footnote/status spacing
   and card padding one notch each way. The card-padding rule uses the .cdb-kpi2.cdb-kpi2--x compound
   (3 classes) to beat the base .cdb-kpi2 padding; the child rules are 3-class so they beat the
   2-class base child rules. None touch color, so the tone/dark overrides (num/icon) are unaffected. */
.cdb-builder-v1 .cdb-kpi2.cdb-kpi2--small {
    padding: 2px 4px 2px;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__label {
    font-size: 14px;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__val {
    margin-top: 8px;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__num {
    font-size: 30px;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__unit {
    font-size: 14px;
    font-weight: 700;
    text-transform: capitalize;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__status {
    margin-top: 5px;
}
.cdb-builder-v1 .cdb-kpi2--small .cdb-kpi2__meta {
    padding-top: 7px;
    font-size: 11px;
}
.cdb-builder-v1 .cdb-kpi2.cdb-kpi2--large {
    padding: 4px 6px 3px;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__label {
    font-size: 17px;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__val {
    margin-top: 14px;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__num {
    font-size: 46px;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__unit {
    font-size: 18px;
    font-weight: 700;
    text-transform: capitalize;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__status {
    margin-top: 9px;
}
.cdb-builder-v1 .cdb-kpi2--large .cdb-kpi2__meta {
    padding-top: 11px;
    font-size: 13px;
}

/* KPI header clearance for DashboardItem's overlaid widget controls. DashboardItem lifts its
   toolbar out of flow (position:absolute over the card) and adds .cdb-hover-chrome (edit) or
   .cdb-view-hover-chrome (view) to its root — BUT ONLY in builder-v1. So these rules never
   match flag-off (neither class), and never match the wizard preview (which renders
   <dashboard-widget> directly, without a DashboardItem wrapper) — there the header keeps its
   full width and an empty right corner, byte-identical to today. Reserves measured from
   DashboardItem: small icon buttons are 28px (44px only under @media pointer:coarse), the edit
   toolrow insets 8px (padding:0 8px) and the view toolrow insets 8px (right:8px). Formula:
   padding-right = clusterWidth + 8 (inset) + 4 (gap) − 5 (.cdb-kpi2 right inset). */
.cdb-hover-chrome .cdb-kpi2__top {
    /* Edit mode. Left: clear the far-top-left drag grip to the same ~30px card-edge offset the
       chart head uses (.cdb-kpi2 already insets 5px, so +25 = 30). Right: reserve filter +
       refresh + hover kebab (3 × 28 = 84 → ~92px). */
    padding-left: 25px;
    padding-right: 92px;
}
.cdb-view-hover-chrome .cdb-kpi2__top {
    /* View mode: no grip (grip is edit-only), reserve the hover-revealed filter + refresh
       (2 × 28 = 56 → ~64px) so a long label never runs under them when they appear. */
    padding-right: 64px;
}
@media (pointer: coarse) {
    /* Coarse pointers enlarge these same buttons to 44px (spec §9), so widen the reserve to
       match: edit 3 × 44 = 132 → ~140px, view 2 × 44 = 88 → ~96px. */
    .cdb-hover-chrome .cdb-kpi2__top {
        padding-right: 140px;
    }
    .cdb-view-hover-chrome .cdb-kpi2__top {
        padding-right: 96px;
    }
}

/* Dashboard tabs — showcase treatment is an underline tab (transparent surface, muted
   label, castleton text + 2px castleton bottom-border when active), NOT the current
   pill. Rendered purely in CSS on the existing v-btn-toggle DOM: overflow measurement,
   mandatory selection, ripple and deep-linking are all untouched. */
.cdb-builder-v1 #custom-dashboard-tabs {
    /* Direct-child only — the nested per-tab kebab activator is also a .v-btn and must
       keep the base's transparent reset, not inherit tab spacing/underline chrome. */
    & > .v-btn {
        background-color: transparent !important;
        border: none !important;
        border-bottom: 2px solid transparent !important;
        border-radius: 0 !important;
        height: 40px;
        padding: 0 4px !important;
        margin: 0 8px;
        color: var(--v-text-base) !important;
        font-weight: 600;

        & i {
            color: var(--v-text-base) !important;
        }

        &:hover:not(.v-btn--active) {
            background-color: transparent !important;
            color: var(--v-text-base) !important;
            border-color: transparent !important;
        }

        &.v-btn--active {
            background-color: transparent !important;
            border: none !important;
            border-bottom: 2px solid var(--v-primary-base) !important;
            color: var(--v-primary-base) !important;

            & i {
                color: var(--v-primary-base) !important;
            }
        }
    }
}

/* Empty + error states — showcase is a calm, muted, centred block (neutral icon, 600
   title, muted description). Tone down the heavy .text-h6 the live copy uses, across
   both the widget-level ("No Widgets yet") and page-level ("No Dashboard found" /
   load-error) shells. */
.cdb-builder-v1 .custom-dashboard-empty .text-h6 {
    font-family: var(--ds-font-family-body);
    font-size: var(--ds-font-subtitle);
    font-weight: 600;
    color: var(--v-text-base);
}

/* Save-status indicator — align resting/saved to castleton, keep the flat 1px chrome. */
.cdb-builder-v1 .cdb-save-status--saved {
    color: var(--v-primary-base);
}
.cdb-builder-v1 .cdb-save-status--saving {
    color: var(--v-text-base);
}

/* Backing datagrid (ag-grid) typography — the showcase table uses IBM Plex body, an
   uppercase muted header (10.5px/600, wide tracking) and compact 12px cells with
   tabular numerals. Only outer typography is touched (reachable via global CSS scoped
   under the flag); ag-grid's own theme/layout classes are left alone. Live-verify the
   selectors land (facilitator check). */
.cdb-builder-v1 .ag-root-wrapper,
.cdb-builder-v1 .ag-cell {
    font-family: var(--ds-font-family-body) !important;
}
.cdb-builder-v1 .ag-header-cell-text {
    text-transform: uppercase !important;
    font-size: 10.5px !important;
    font-weight: 600 !important;
    letter-spacing: 0.04em !important;
    color: var(--v-text-base) !important;
}
.cdb-builder-v1 .ag-cell {
    font-size: 12px !important;
    font-variant-numeric: tabular-nums;
}
</style>
